import type { Dispatch, SetStateAction } from 'react';
import React, {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useMedia } from 'react-media';
import { useRouter } from 'next/router';
import moment from 'moment';
import type { Id as ToastNotificationId } from 'react-toastify';
import { toast } from 'react-toastify';
import { useHotkeys } from 'react-hotkeys-hook';
import { useApolloClient } from '@apollo/client';
import { Resizable } from 're-resizable';
import debounce from 'lodash/debounce';
import groupBy from 'lodash/groupBy';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import { BodyWrap } from '../components/Main';
import useContextMenu from '../components/hooks/useContextMenu';
import {
  Badge,
  Box,
  Button,
  DatePicker,
  DeleteButton,
  DropdownButton,
  FilterBar,
  FilterBarSeparator,
  FilterBarTab,
  FormField,
  Input,
  Menu,
  MenuItem,
  MenuSeparator,
  Modal,
  ModalBody,
  ModalFooter,
  Text,
  View,
} from '../components/ui';
import {
  ModalProvider,
  useModalProvider,
} from '../components/contexts/ModalContext';
import type { BadgeProps } from '../components/ui/Badge/Badge';
import type {
  OrderTableRef,
  OrderTableRowData,
} from '../components/order/OrderTable';
import OrderTable from '../components/order/OrderTable';
import { getOrderContractType } from '../components/order/OrderTableUtils';
import type {
  ScenarioTimelineProps,
  ScenarioTimelineRef,
  VoyagePlacement,
} from '../components/timeline/ScenarioTimeline';
import ScenarioTimeline from '../components/timeline/ScenarioTimeline';
import type {
  ChangedEntries,
  Entry,
  Order,
  SwapDirection,
} from '../components/core/Core';
import {
  applyVoyageStart,
  checkOrderCompatibleWithCoa,
  CreateCOAEntry,
  createOffHireEntry,
  createSingleEventEntry,
  createTCOrderAssignments,
  deleteEntry,
  deriveEventModifications,
  EventCategory,
  getAffectedVoyageEvents,
  getChangedScheduleEntries,
  getOrderDurationEstimates,
  getPortCallSwapModifications,
  hasOrdersChangedInPortCalls,
  initChangedEntries,
  initDefaultCoaSpotEntryTimes,
  isCoaEntry,
  isOffHireEntry,
  isPortCallEntry,
  isSingleEntry,
  isSpotEntry,
  isTCOrderEntry,
  mergeEntryModifications,
  minimumEventDuration,
  modifyEntry,
  moveAssignmentsAlongTimeline,
  rearrangeShipmentEvents,
  resetArrivalDurations,
  swapScheduleEntry,
  updateBerthDurationsFromAPI,
  orderFullfillsSpecialRequirements,
} from '../components/core/Core';
import type {
  GetSuggestionsInputs,
  SuggestionResult,
  SuggestionsByVessel,
} from '../components/core/suggestions/getSuggestions';
import type { VesselToSortedEvents } from '../components/core/VesselSchedule';
import { midPoint } from '../components/core/Capacity';
import { calculateMassAndVolumeRanges } from '../components/core/Capacity';
import type { Vessel } from '../components/core/Vessel';
import type {
  SingleEventType,
  DischargeEvent,
  LoadEvent,
  TCTimelineEvent,
  CargoEvent,
  Event,
  SingleEvent,
  TCOrderEvent,
  Events,
  TimeData,
} from '../components/core/TimeData';
import {
  CoreEventType,
  isOffHireEvent,
  isSingleEvent,
  isTCOrderEvent,
  isCargoEvent,
  isTCTimelineEvent,
  getEventEndLocationId,
  getEventStartLocationId,
  isPortCallArrivalEventTimeData,
  isPortCallBerthingEventTimeData,
  isPortCallDepartureEventTimeData,
  isTimeData,
  isPortCallArrivalEvent,
  isCargoEventTimeData,
  isNotPortCallEventTimeData,
  isPortCallEventTimeData,
  isSingleEventTimeData,
  isTCOrderEventTimeData,
  isNotStaticEventTimeData,
} from '../components/core/TimeData';
import type {
  CoaEntry,
  OffHireEntry,
  SpotEntry,
} from '@seaber/common/core/entries';
import { dToMs, hToMs } from '../lib/time';
import {
  optimizeVoyageLegSpeeds,
  getAllAvailableSpeeds,
  type OptimizationContext as LegOptimizationContext,
  type OptimizationModalInputs as LegOptimizationModalInputs,
} from '../components/core/speed-optimization';
import type { GetIncomeForLoadedAmount } from '../components/shipment/income/income';
import {
  incomeForLoadedAmountGetter,
  isEuEtsCostEnabledForUser,
} from '../components/shipment/income/income';
import { getCostSharingDetailsForPortCalls } from '../components/shipment/TCE';
import { getFreightsByOrderId } from '../components/shipment/income/orderFreight';
import { useCurrencyRates, useDefaultCurrency } from '../state/zustand';
import type { VoyageSuggestionParams } from '../components/core/suggestions/VoyageSuggestionParams';
import { IconWrapper, ShareIcon } from '../components/Icons';
import ScenarioComparison from '../components/ScenarioComparison';
import {
  dateStringToNum,
  endSpinner,
  getVoyageEvents,
  startSpinner,
  withSpinner,
} from '../components/core/util';
import type { ExactProps } from '../lib/util';
import {
  assertDefined,
  assertedDefined,
  exhaustiveCheck,
  filteredMap,
  get,
  groupBySequence,
  guardNot,
  isDefined,
  mapKeyBy,
  mappedFind,
  mapTransform,
  NONE,
  normalizeForSearch,
  rangesOverlap,
  setFilter,
  setIntersection,
  transformRecord,
  useAsRef,
  useDeepMemo,
} from '../lib/util';
import { formatNumber } from '../lib/format';
import CalculationContext from '../components/contexts/CalculationContext';
import type {
  EventTimeline,
  EventTimelineTimeDataOnly,
  TimelineFocus,
} from '../components/core/Timelines';
import { findVoyageGroupForEvent } from '../components/core/Timelines';
import type {
  ComputedScenarioTimings,
  ScenarioOrder,
  ScenarioShipment,
} from '../lib/planningData';
import { useDefined } from '../lib/planningData';
import { useLocalSetting } from '../components/hooks/useLocalConfig';
import type { TableColumnsSelectorProps } from './orders';
import { TableColumnsSelector } from './orders';
import { getOrderDisplayName } from '../components/order/order';
import type { ClientId } from '../components/core/ClientId';
import { generateClientId } from '../components/core/ClientId';
import type {
  CommitStatus,
  InternalScenarioSchedule,
  TransactionParams,
  UseScenariosResult,
} from '../components/Scenario';
import {
  buildShipment,
  isBackupScenario,
  isInitialScenario,
  isMasterScenario,
  isSuggestionScenario,
  isUserScenario,
  parseScenario,
  ScenarioCommitPolicy,
  suggestionScenarioToUserScenario,
} from '../components/Scenario';
import type {
  BackupSchedule,
  ScenarioSchedule,
  SuggestionSchedule,
  UserSchedule,
} from '@seaber/common/core/scenario';
import AppStateContext from '../components/contexts/AppStateContext';
import { useOverridableState } from '../components/hooks/useOverridableState';
import { archiveByIds, handleSelectBefore } from '../components/core/archive';
import {
  AcceptAllUpdatesOptions,
  addTimeSourcesToResult,
  getRelatedEventForPortCallUpdate,
  PortCallUpdateHandlerProvider,
  PortCallUpdateProvider,
  timelinePortCallUpdateOpts,
  usePortCallUpdates,
  usePortCallUpdatesState,
} from '../components/PortCallUpdates';
import useInterval from '../components/hooks/useInterval';
import type { PortCallUpdateBase } from '../components/PostCallUpdates.interface';
import type {
  CommitLogScenarioByClientIdQueryResult,
  CommitLogScenarioByClientIdQueryVariables,
  Location,
  OpenPositionsFragment,
  PortCostEstimatesFromVcQueryResult,
  PortCostEstimatesFromVcQueryVariables,
  ScenarioTimeline_ShipFragment,
  VesselReservationFragment,
} from '../graphql';
import {
  CommitLogScenarioByClientIdDocument,
  PortCostEstimatesFromVcDocument,
  useAllRegionsQuery,
  useAllVesselAttributesQuery,
  useCommitLogScenarioUpdatedSubscription,
  useLastCommitsQuery,
  useRelatedOrgsQuery,
  useUpdateConfirmedVoyagesMutation,
} from '../graphql';
import type { RegionBalanceFilter } from '../components/timeline/Regions';
import { useRegions } from '../components/timeline/Regions';
import {
  getScenarioTimelinesDiff,
  hasTimelineChanged,
  isAutoSyncEnabledForScenario,
} from '../components/ScenarioUtil';
import { getOrderAssignments } from '@seaber/common/core/scenario';
import ScenarioMergeTimeline from '../components/timeline/ScenarioMergeTimeline';
import TimelinePreferencesContext, {
  DEFAULT_ZOOM_WEEKS,
  TimelinePreferencesProvider,
} from '../components/contexts/TimelinePreferencesContext';
import { compact, findLast, first, last, partition, uniqBy } from 'lodash';
import {
  useVoyageCharterUpdates,
  VoyageCharterUpdateProvider,
} from '../components/shipment/VoyageCharterUpdate';
import { TimelineControlButtons } from '../components/timeline/TimelineView';
import { useDistances } from '../components/distances/hooks';
import { getMissingDistances } from '../components/distances/distance-utils';
import { useMarcuraPortCosts } from '../components/marcura/hooks';
import { LoadingOverlay } from '../components/LoadingSpinner';
import {
  isWaitLaycanDefaultAtom,
  usePlanningLayoutState,
  useViolationBasis,
} from '../state/planning/options';
import { MenuToggle } from '../components/ui/Menu/Menu';
import type {
  SuggestVoyageModalParams,
  SuggestVoyageModalResult,
} from '../components/core/suggestions/SuggestVoyageModal';
import { SuggestVoyageModal } from '../components/core/suggestions/SuggestVoyageModal';
import { useModalWithResult } from '../components/hooks/useModalWithResult';
import { useCharteringOpenPositions } from '../components/hooks/useCharteringOpenPositions';
import { useNotify } from '../components/notifications/ToastNotifications';
import { SpeedOptimizationToast } from '../components/notifications/SpeedOptimizationToast';
import {
  bundleIncomingVoyageCharterRelatedOrders,
  getStatusFilterFromContractDetailsType,
} from '../components/voyageCharter/voyageCharter';
import {
  convertToDefaultCurrency,
  hasFeature,
  hasUserRight,
  useCurrentUser,
  usePlanningMode,
} from '../state/zustand';
import {
  AssignmentBehaviorOptions,
  LayoutOptions,
  RegionToolOptions,
  VesselDataFieldOptions,
  ViolationDisplayOptions,
  VoyageBoxFieldOptions,
} from '../components/PlanningTimelineOptions';
import { DragDropDescription } from '../components/hooks/useDragDrop';
import { useBunkerPrices } from '../components/hooks/useBunkerPrices';
import useEvent from '../components/hooks/useEvent';
import { useVesselReservations } from '../components/hooks/useVesselReservations';
import { useFocusedOrders } from '../state/planning/timeline';
import type { Distances } from '../components/distances/context';
import type {
  LostContractModalParams,
  LostContractModalResult,
} from '../components/lostContract/LostContractModal';
import LostContractModal from '../components/lostContract/LostContractModal';
import {
  IntegrationRaptorDraft,
  integrationRaptorDraftFilterSuggestions,
  IntegrationRaptorDraftStatus,
} from '../components/integrations/RaptorDraft';
import { isInquiry } from '../components/voyageCharter/orders';
import { useAtom, useAtomValue } from 'jotai';
import { showTooltips } from '../components/ui/Tooltip/Tooltip';
import ScenarioImprovements from '../components/ScenarioImprovements';
import type { ScenarioImprovement } from '../components/core/suggestions/getScenarioImprovements';
import { usePnlData } from '../components/hooks/usePnlData';
import useMeasure from '../components/hooks/useMeasure';
import type { GlobalPageProps } from './_app';
import {
  doesFacetedFilterMatch,
  getCompletionsFromAllMatchers,
  parseFacetedFilterQuery,
} from '../components/ui/FilterBar/FilterBar';
import type {
  DuplicateOrdersModalParams,
  DuplicateOrdersModalResult,
} from '../components/voyageCharter/DuplicateOrdersModal';
import DuplicateOrdersModal from '../components/voyageCharter/DuplicateOrdersModal';
import type { MergeDirectives } from '../components/MergeScenarios';
import { isLocationCoordinatesInvalid } from '../components/LocationSelect';
import { useDialog } from '../components/ui/Modal/useDialog';
import { SpeedOptimizationModal } from '../components/shipment/SpeedOptimizationModal';
import { FleetCleanupModal } from '../components/FleetCleanup';
import { IntegrationScenarioUpdateModal } from '../components/integrations/IntegrationScenarioUpdateModal';
import { PlanningHotkeysListener } from '../components/planning/Hotkeys';
import { DefaultMap } from '@seaber/common/lib/collections';
import type { CargoAllocationOptions } from '../components/core/suggestions/Suggestions';
import { OPTION_NO_CARGO_ALLOCATIONS } from '../components/core/suggestions/Suggestions';
import { getSuggestions } from '../components/core/suggestions/getSuggestions';
import type { ContractDetailsSchema } from '../components/voyageCharter/schema';
import { useIsMobileView } from '../components/hooks/useMediaQuery';
import { useOpenBatchEditCargoesModal } from '../components/batchEditCargoes/useOpenBatchEditCargoesModal';
import type {
  VoyageSuggestionReportParams,
  VoyageSuggestionReportResult,
} from '../components/core/suggestions/VoyageSuggestionReport';
import VoyageSuggestionReport from '../components/core/suggestions/VoyageSuggestionReport';
import type { ScheduleDataContext } from '../components/core/ScheduleDataContext';
import { useReApplyCargoAllocations } from '../components/hooks/useReApplyCargoAllocations';
import { ItemFilters, type ItemFilterValues } from '../components/ItemFilters';
import { Q88Updates } from '../components/integrations/Q88Updates';
import { VesonUpdates } from '../components/integrations/VesonUpdates';
import { ShipnetUpdates } from '../components/integrations/ShipnetUpdates';
import { PreemUpdates } from '../components/integrations/PreemUpdates';
import { PnlProvider } from '../components/pnl/ui/PnlProvider';
import { useSpecialRequirements } from '../components/hooks/useSpecialRequirements';

const COLOR_BG_MASTER_SCENARIO = '#953000';
const useHashParams = () => {
  const [hash, setHash] = useState(() =>
    typeof window !== 'undefined' ? window.location.hash : '',
  );

  useEffect(() => {
    const hashChangeHandler = () => {
      setHash(window.location.hash);
    };
    window.addEventListener('hashchange', hashChangeHandler);
    return () => {
      window.removeEventListener('hashchange', hashChangeHandler);
    };
  }, []);

  return useMemo(() => new URLSearchParams(hash.replace('#', '?')), [hash]);
};

const useDisplayedNumberOfBackups = () => {
  const params = useHashParams();
  return useMemo(() => {
    const backups = params.get('backups');
    return (backups && parseInt(backups)) || 20;
  }, [params]);
};

export type ScenarioMenuItemType = {
  label: string;
  hidden?: boolean;
  checked?: boolean;
  accessibilityLabel: string;
  disabled?: boolean;
  handler: () => Promise<InternalScenarioSchedule | undefined> | void;
};

export type ResetAssignmentsToMasterOpts =
  | {
      orderIds: number[];
      shipmentIds?: ClientId[];
    }
  | {
      orderIds?: number[];
      shipmentIds: ClientId[];
    };

const GLOBAL_MEDIA_QUERIES = {
  extraLarge: '(min-width: 1900px)',
  large: '(min-width: 1350px)',
  small: '(max-width: 1349px)',
  extraSmall: '(max-width: 768px)',
};

export enum StatusFilter {
  'MAPPING_REQUIRED',
  'INQUIRY' = 'INQUIRY',
  'OPEN' = 'OPEN',
  'COA_PRELIMS' = 'COA_PRELIMS',
  'PUBLISHED' = 'PUBLISHED',
  'COMPLETED' = 'COMPLETED',
  'SCENARIO' = 'SCENARIO',
  'TIMELINE_SELECTION' = 'TIMELINE_SELECTION',
  'UNPUBLISHED' = 'UNPUBLISHED',
  'REGION' = 'REGION',
  'ORPHAN_PARCELS' = 'ORPHAN PARCELS',
}

const OrderFilterTab: (props: {
  label: string;
  orderIds: Record<StatusFilter, Set<number>>;
  currentValue: StatusFilter;
  tabValue: StatusFilter;
  setActive: (x: StatusFilter) => void;
}) => JSX.Element = ({
  label,
  orderIds,
  currentValue,
  tabValue,
  setActive,
}) => {
  return (
    <FilterBarTab
      onPress={() => setActive(tabValue)}
      isActive={currentValue === tabValue}
      data-testid={`orders-filter-tab-${label}`}
      aria-selected={currentValue === tabValue}
    >
      <Text mr={3}>{label}</Text>
      <Badge isRounded>{orderIds[tabValue].size}</Badge>
    </FilterBarTab>
  );
};

const OrderFilterTabList: (props: {
  orderCountByStatus: Record<StatusFilter, Set<number>>;
  currentValue: StatusFilter;
  setActive: (x: StatusFilter) => void;
}) => JSX.Element = ({ orderCountByStatus, currentValue, setActive }) => {
  const [planningLayout] = usePlanningLayoutState();
  const planningMode = usePlanningMode();
  const isVoyageCharterMode = planningMode === 'voyage_charter';
  const resizableDimensions = React.useContext(ResizableDimensionsContext);
  const resizableWidth = resizableDimensions?.width ?? 0;

  const tabs: Array<{ value: StatusFilter; label: string }> = useMemo(
    () => [
      ...(orderCountByStatus[StatusFilter.MAPPING_REQUIRED].size > 0
        ? [
            {
              value: StatusFilter.MAPPING_REQUIRED,
              label: 'Mapping required',
            },
          ]
        : []),
      ...(orderCountByStatus[StatusFilter.ORPHAN_PARCELS].size > 0
        ? [
            {
              value: StatusFilter.ORPHAN_PARCELS,
              label: 'Orphan parcels',
            },
          ]
        : []),
      ...(isVoyageCharterMode
        ? [
            {
              value: StatusFilter.INQUIRY,
              label: 'Inquiries',
            },
            {
              value: StatusFilter.COA_PRELIMS,
              label: 'COA prelims',
            },
          ]
        : []),
      {
        value: StatusFilter.OPEN,
        label: 'Confirmed',
      },

      {
        value: StatusFilter.SCENARIO,
        label: 'Scenario',
      },
      {
        value: StatusFilter.UNPUBLISHED,
        label: 'Committed',
      },
      {
        value: StatusFilter.PUBLISHED,
        label: 'Published',
      },
      {
        value: StatusFilter.COMPLETED,
        label: 'Completed',
      },
      {
        value: StatusFilter.TIMELINE_SELECTION,
        label: 'Selected',
      },
      {
        value: StatusFilter.REGION,
        label: 'All',
      },
    ],
    [isVoyageCharterMode, orderCountByStatus],
  );

  const mainTabs = tabs.filter(
    (tab) =>
      tab.value === StatusFilter.INQUIRY || tab.value === StatusFilter.OPEN,
  );

  const dropdownTabs = tabs.filter(
    (tab) =>
      tab.value !== StatusFilter.INQUIRY && tab.value !== StatusFilter.OPEN,
  );

  const selectedTab =
    dropdownTabs.find((tab) => tab.value === currentValue) ?? dropdownTabs[0];
  const matches = useMedia({ queries: GLOBAL_MEDIA_QUERIES });
  const isTabMode =
    (matches.extraLarge &&
      +resizableWidth > 1900 / 2 &&
      planningLayout === 'vertical') ||
    (matches.large && planningLayout === 'horizontal');
  return (
    <>
      {isTabMode ? (
        <Box display="flex" flexDirection="row">
          {tabs.map(({ label, value }) => (
            <OrderFilterTab
              key={label}
              label={label}
              tabValue={value}
              currentValue={currentValue}
              setActive={setActive}
              orderIds={orderCountByStatus}
            />
          ))}
        </Box>
      ) : (
        <>
          {mainTabs.map(({ label, value }) => (
            <OrderFilterTab
              key={label}
              label={label}
              tabValue={value}
              currentValue={currentValue}
              setActive={setActive}
              orderIds={orderCountByStatus}
            />
          ))}
          <DropdownButton
            label={`${selectedTab.label} - ${
              orderCountByStatus[selectedTab.value].size
            }`}
            variant="secondary"
          >
            <Menu hasShadow>
              {dropdownTabs.map(({ label, value }) => {
                return (
                  <MenuItem
                    key={value}
                    label={`${label} - ${orderCountByStatus[value].size}`}
                    onPress={() => {
                      setActive(value);
                    }}
                  />
                );
              })}
            </Menu>
          </DropdownButton>
        </>
      )}
    </>
  );
};

type CategoryFilter =
  | 'all'
  | 'updated'
  | 'open'
  | 'reserved'
  | 'modified'
  | EventTimeline['type'];

const CategoryFilterTab = ({
  currentValue,
  label,
  tabValue,
  timelineCount,
  badgeVariant,
  selectCategory,
}: {
  currentValue: CategoryFilter;
  label: string;
  tabValue: CategoryFilter;
  timelineCount: number;
  badgeVariant?: BadgeProps['variant'];
  selectCategory: (filter: CategoryFilter) => void;
}): JSX.Element => {
  return (
    <FilterBarTab
      isActive={currentValue === tabValue}
      onPress={() => selectCategory(tabValue)}
      aria-label={label}
    >
      <Text mr={3}>{label}</Text>
      <Badge
        data-testid={`categories-filter-tab-${label}`}
        variant={badgeVariant}
        isRounded
      >
        {timelineCount}
      </Badge>
    </FilterBarTab>
  );
};

const timelineCategoryTypeFilters: Record<CategoryFilter, string> = {
  all: 'ALL',
  tc: 'FLEET',
  coa: 'COA',
  spot: 'SPOT',
  modified: 'Modified',
  updated: 'Updated',
  open: 'Open',
  reserved: 'Reserved',
};

const CategoryFilterTabList = ({
  setSelectedCategory,
  timelinesMatchingSearchFilter,
  timelinesByFilter,
  selectedCategory,
  updatedTimelines,
  isShipOwner,
}: {
  setSelectedCategory: Dispatch<SetStateAction<CategoryFilter>>;
  timelinesMatchingSearchFilter: Set<string>;
  timelinesByFilter: Record<CategoryFilter, Set<string>>;
  selectedFilterIds: number[];
  selectedCategory: CategoryFilter;
  updatedTimelines: Set<string>;
  isShipOwner?: boolean;
}): JSX.Element => {
  const [planningLayout] = usePlanningLayoutState();
  const matches = useMedia({ queries: GLOBAL_MEDIA_QUERIES });

  // Updates might be cleared e.g. when scenario is switched
  useEffect(() => {
    if (selectedCategory === 'updated' && updatedTimelines.size === 0) {
      setSelectedCategory('all');
    }
  }, [setSelectedCategory, selectedCategory, updatedTimelines]);

  const categoryCountsByFilter = useMemo(
    () =>
      transformRecord(
        timelinesByFilter,
        (timelines) =>
          setIntersection([timelines, timelinesMatchingSearchFilter]).size,
      ),
    [timelinesByFilter, timelinesMatchingSearchFilter],
  );

  const isTabMode =
    (matches.extraLarge && planningLayout === 'vertical') ||
    (matches.large && planningLayout === 'horizontal');
  const getCategoryLabel = (key: CategoryFilter) =>
    key === 'spot' && isShipOwner ? 'RELET' : timelineCategoryTypeFilters[key];

  return (
    <>
      {isTabMode ? (
        <Box display="flex" flexDirection="row">
          {Object.keys(timelineCategoryTypeFilters)
            .filter((x: CategoryFilter) => timelinesByFilter[x].size)
            .map((v: CategoryFilter) => {
              return (
                <CategoryFilterTab
                  key={v}
                  label={getCategoryLabel(v)}
                  tabValue={v}
                  currentValue={selectedCategory}
                  selectCategory={setSelectedCategory}
                  timelineCount={categoryCountsByFilter[v]}
                  badgeVariant={
                    v === 'updated' || v === 'reserved'
                      ? 'status/unassigned'
                      : undefined
                  }
                />
              );
            })}
        </Box>
      ) : (
        <DropdownButton
          label={timelineCategoryTypeFilters[selectedCategory]}
          variant="secondary"
        >
          <Menu hasShadow>
            {Object.keys(timelineCategoryTypeFilters)
              .filter((x: CategoryFilter) => categoryCountsByFilter[x] > 0)
              .map((v: CategoryFilter) => {
                const menuItemLabel = `${getCategoryLabel(v)} - ${
                  categoryCountsByFilter[v]
                }`;
                return (
                  <MenuItem
                    key={v}
                    aria-label={menuItemLabel}
                    label={menuItemLabel}
                    onPress={() => setSelectedCategory(v)}
                  />
                );
              })}
          </Menu>
        </DropdownButton>
      )}
    </>
  );
};

interface SelectionControlsProps {
  currentTimelineMode: 'plan' | 'commit';
  handleArchiveSelected: () => Promise<boolean>;
  handleAssignToSpot: (orderIds: Order['id'][], startDate?: Date) => void;
  handleGetSuggestions: (ids?: number[]) => void;
  handleSelectCompleted: () => void;
  handleSuggestScenarioImprovements: () => void;
  handleSuggestVoyageForVessels: () => void;
  handleUnassignSelectedOrders: () => void;
  isArchiveSelectedAvailable: boolean;
  isAutoAcceptEnabled: boolean;
  isCurrentScenarioMaster: boolean;
  isSelectedScenarioReadOnly: boolean;
  isSuggestAvailable: boolean;
  selectedOrderIds: number[];
  selectedOrdersTotalMass: number;
  selectedOrdersTotalVolume: number;
  showFleetCleanup: () => void;
  currentView: 'timeline' | 'cargoes';
  setCurrentView: (view: 'timeline' | 'cargoes') => void;
  isMobileView: boolean;
}

interface ZoomControlsProps {
  timelineSelection: Set<number>;
  collapseAllTimelines: () => void;
  handleZoomToVoyages: (orderIds?: number[]) => void;
  timelineRef: React.RefObject<ScenarioTimelineRef>;
  isCompressedView: boolean | undefined;
  isEmptyTimelinesHidden: boolean;
  isIncompatibleTimelinesHidden: boolean;
  setIsIncompatibleTimelinesHidden(newValue: boolean | undefined): void;
  setIsCompressedView(newValue: boolean | undefined): void;
  setIsEmptyTimelinesHidden(newValue: boolean | undefined): void;
  setCompletedLimit: (value: Date) => void;
  configUseCalculatedLimit: boolean;
  completedLimit: Date;
  undo: UseScenariosResult['undo'];
  isMobileView: boolean;
}

import SettingsSvg from '../images/settings.svg';
import { useLazyMemo } from '../components/hooks/misc';
import { useCalculateTimeDataPnl } from '../components/pnl/ui/context';

function ZoomControls({
  collapseAllTimelines,
  handleZoomToVoyages,
  timelineSelection,
  timelineRef,
  isCompressedView,
  isEmptyTimelinesHidden,
  isIncompatibleTimelinesHidden,
  setIsIncompatibleTimelinesHidden,
  setIsEmptyTimelinesHidden,
  setIsCompressedView,
  setCompletedLimit,
  configUseCalculatedLimit,
  completedLimit,
  undo,
  isMobileView,
}: ZoomControlsProps) {
  const { setZoomLevel } = useContext(TimelinePreferencesContext);
  const [showTooltip, setShowTooltip] = useAtom(showTooltips);
  return (
    <Box display="flex" alignItems="center" height={48}>
      {!isMobileView && (
        <>
          <Button
            mx={2}
            disabled={!undo.canUndo}
            title={undo.canUndo ? `Undo ${undo.undoHint}` : undefined}
            onPress={() => undo.undo()}
          >
            Undo ⟲
          </Button>
          <Button
            mx={2}
            disabled={!undo.canRedo}
            title={undo.canRedo ? `Redo ${undo.redoHint}` : undefined}
            onPress={() => undo.redo()}
          >
            Redo ⟳
          </Button>
        </>
      )}
      <Button mx={2} onPress={() => timelineRef.current?.scrollToToday()}>
        Today
      </Button>

      {!isMobileView && (
        <>
          <Button mx={2} onPress={collapseAllTimelines}>
            Collapse all
          </Button>
          <DropdownButton mx={2} label="Zoom">
            <Menu hasShadow>
              <MenuItem
                label="Show all voyages [CTRL + 1]"
                onPress={() => handleZoomToVoyages()}
              />
              <MenuItem
                label="Show selected orders [CTRL + 5]"
                onPress={() =>
                  handleZoomToVoyages(Array.from(timelineSelection))
                }
              />
              <MenuItem
                label="Zoom to default level [CTRL + 3]"
                onPress={() => setZoomLevel(DEFAULT_ZOOM_WEEKS)}
              />
              <MenuItem
                label="Zoom for 2 weeks [CTRL + 2]"
                onPress={() => setZoomLevel(2)}
              />
              <MenuItem
                label="Zoom for 4 weeks [CTRL + 4]"
                onPress={() => setZoomLevel(4)}
              />
            </Menu>
          </DropdownButton>
        </>
      )}
      <DropdownButton
        data-testid="planning-settings"
        closeOnContentClick={false}
        mx={2}
        label={
          <IconWrapper>
            <img src={SettingsSvg} width={14} height={18} />
          </IconWrapper>
        }
      >
        <Menu
          width="max-content"
          hasShadow
          px={4}
          display="flex"
          flexDirection={isMobileView ? 'column' : 'row'}
          {...(isMobileView && {
            overflowY: 'scroll',
            height: '400px',
          })}
        >
          <Box display="flex" mr={4} flexDirection="column">
            {!isMobileView && <LayoutOptions />}

            <Text fontWeight="bold" mt={isMobileView ? 1 : 2}>
              General timeline settings
            </Text>
            <MenuToggle
              type="checkbox"
              label="Compressed view"
              checked={isCompressedView}
              onChange={(ev) => setIsCompressedView(ev.target.checked)}
              {...(isMobileView && {
                width: '16px',
                height: '16px',
                mt: '2px',
              })}
            />
            <MenuToggle
              type="checkbox"
              label="Hide empty lines"
              checked={isEmptyTimelinesHidden}
              onChange={(ev) => setIsEmptyTimelinesHidden(ev.target.checked)}
              {...(isMobileView && {
                width: '16px',
                height: '16px',
                mt: '2px',
              })}
            />
            <MenuToggle
              type="checkbox"
              label="Show tooltips"
              checked={showTooltip}
              onChange={(ev) => setShowTooltip(ev.target.checked)}
              {...(isMobileView && {
                width: '16px',
                height: '16px',
                mt: '2px',
              })}
            />
            <MenuToggle
              type="checkbox"
              label="Hide incompatible contracts/vessels"
              checked={isIncompatibleTimelinesHidden}
              onChange={(ev) =>
                setIsIncompatibleTimelinesHidden(ev.target.checked)
              }
              {...(isMobileView && {
                width: '16px',
                height: '16px',
                mt: '2px',
              })}
            />
            <RegionToolOptions isMobileView={isMobileView} />
            <VoyageBoxFieldOptions isMobileView={isMobileView} />
          </Box>
          <Box display="flex" flexDirection="column">
            <VesselDataFieldOptions isMobileView={isMobileView} />
            <ViolationDisplayOptions isMobileView={isMobileView} />
            <AssignmentBehaviorOptions isMobileView={isMobileView} />
            {configUseCalculatedLimit && (
              <FormField label="Calculated limit:">
                <DatePicker
                  timeZone={undefined}
                  selected={completedLimit}
                  onChange={setCompletedLimit}
                />
              </FormField>
            )}
          </Box>
        </Menu>
      </DropdownButton>

      {!isMobileView && <TimelineControlButtons />}
    </Box>
  );
}

const SelectionControls = ({
  currentTimelineMode,
  handleArchiveSelected,
  showFleetCleanup,
  handleSelectCompleted,
  handleSuggestScenarioImprovements,
  handleSuggestVoyageForVessels,
  handleGetSuggestions,
  isArchiveSelectedAvailable,
  isAutoAcceptEnabled,
  isCurrentScenarioMaster,
  isSelectedScenarioReadOnly,
  isSuggestAvailable,
  selectedOrderIds,
  selectedOrdersTotalMass,
  selectedOrdersTotalVolume,
  currentView,
  setCurrentView,
  isMobileView,
}: SelectionControlsProps) => {
  return (
    <Box display="flex" alignItems="center">
      {isMobileView && (
        <Button
          marginRight={3}
          onPress={() => {
            setCurrentView(currentView === 'cargoes' ? 'timeline' : 'cargoes');
          }}
          variant="primary"
        >
          ⇕ Show {currentView === 'cargoes' ? 'timeline ' : 'cargoes '}⇕
        </Button>
      )}
      {currentTimelineMode === 'plan' && (
        <>
          {!isMobileView && (
            <Text fontWeight="bold" color="neutral.white_50" isUppercase mr={4}>
              Selection
            </Text>
          )}
          <View flexDirection="column">
            {(!isMobileView || (isMobileView && currentView === 'cargoes')) && (
              <View flexDirection="row">
                <Badge
                  data-testid={'selected-orders-count'}
                  variant={selectedOrderIds.length > 0 ? 'primary' : 'default'}
                  isRounded
                  mr={2}
                >
                  {selectedOrderIds.length}
                </Badge>
                <Text color="neutral.white_50" mr={3} fontWeight="bold">
                  selected
                </Text>
              </View>
            )}
            <Text color="neutral.white_50" mr={3} fontSize={1}>
              {selectedOrdersTotalVolume > 0
                ? `${formatNumber(selectedOrdersTotalVolume)} cbm`
                : ''}
              {selectedOrdersTotalVolume > 0 && selectedOrdersTotalMass > 0
                ? ', '
                : ''}
              {selectedOrdersTotalMass > 0
                ? `${formatNumber(selectedOrdersTotalMass)} mts`
                : ''}
            </Text>
          </View>
        </>
      )}
      {isSuggestAvailable &&
        currentTimelineMode === 'plan' &&
        !isSelectedScenarioReadOnly &&
        (!isMobileView || (isMobileView && currentView === 'cargoes')) && (
          <>
            <DropdownButton
              ml={3}
              mr={!isAutoAcceptEnabled ? 3 : 0}
              label="Suggestions"
            >
              <Menu hasShadow>
                <MenuItem
                  label="Voyages for vessels"
                  onPress={() => handleSuggestVoyageForVessels()}
                />
                <MenuItem
                  label="Show improvement suggestions"
                  onPress={() => handleSuggestScenarioImprovements()}
                />
                <MenuItem
                  label="Suggestions for selected cargoes"
                  onPress={() => handleGetSuggestions(selectedOrderIds)}
                />
              </Menu>
            </DropdownButton>
          </>
        )}
      {!isMobileView && (
        <DropdownButton
          ml={3}
          mr={!isAutoAcceptEnabled ? 3 : 0}
          label="Timeline"
        >
          <Menu hasShadow>
            <MenuItem
              label="Select voyages for archival"
              onPress={() => handleSelectCompleted()}
            />
            {isArchiveSelectedAvailable && (
              <MenuItem
                label="Archive selected"
                onPress={() => handleArchiveSelected()}
              />
            )}
            <MenuItem
              label={`Clean up fleet ${!isCurrentScenarioMaster ? '(Select Committed scenario)' : ''}`}
              disabled={!isCurrentScenarioMaster}
              onPress={showFleetCleanup}
            />
          </Menu>
        </DropdownButton>
      )}
    </Box>
  );
};

function VerticalLayoutSelectionControls(
  props: SelectionControlsProps &
    ZoomControlsProps & { containerRef?: React.Ref<HTMLDivElement> },
): JSX.Element | null {
  const [planningLayout] = usePlanningLayoutState();

  return (
    <Box
      ref={props.containerRef}
      display="flex"
      alignItems="center"
      marginLeft="auto"
    >
      {planningLayout === 'vertical' ? (
        <Box display="flex" alignItems="center">
          <FilterBarSeparator mx={3} />
          <SelectionControls {...props} />
        </Box>
      ) : null}
      <FilterBarSeparator mx={2} />
      <ZoomControls {...props} />
    </Box>
  );
}

function HorizontalLayoutSelectionControls(
  props: SelectionControlsProps,
): JSX.Element | null {
  const [planningLayout] = usePlanningLayoutState();
  if (planningLayout === 'vertical') return null;

  return <SelectionControls {...props} />;
}

const VesselAttributeFilterTab = ({
  label,
  selectedCategory,
  setSelectedCategory,
  selectedFilterIds,
  filtersAndTimeline,
  setSelectedFilterIds,
  ctrlKeyPressed,
}: {
  label: string;
  selectedCategory: CategoryFilter;
  selectedFilterIds: number[];
  filtersAndTimeline: Map<string, { id: number; vessels: Set<string> }>;
  setSelectedFilterIds: Dispatch<SetStateAction<number[]>>;
  setSelectedCategory: Dispatch<SetStateAction<CategoryFilter>>;
  ctrlKeyPressed: boolean;
}): JSX.Element | null => {
  const filter = filtersAndTimeline.get(label);
  const isActive = !!filter && selectedFilterIds.includes(filter.id);

  const toggle = useCallback(() => {
    if (!filter) return;
    if (label === 'All') {
      setSelectedFilterIds([0]);
      return;
    }

    // reset selected category to All.
    if (selectedCategory !== 'all') {
      setSelectedCategory('all');
    }

    if (!ctrlKeyPressed) {
      if (selectedFilterIds.includes(filter.id)) {
        setSelectedFilterIds([0]);
      } else {
        setSelectedFilterIds([filter.id]);
      }
    } else {
      if (selectedFilterIds.includes(filter.id)) {
        setSelectedFilterIds([
          ...selectedFilterIds.filter((x) => x !== filter.id && x !== 0),
        ]);
      } else {
        setSelectedFilterIds(
          [...selectedFilterIds, filter.id].filter((x) => x !== 0),
        );
      }
    }

    if (!selectedFilterIds.length) setSelectedFilterIds([0]);
  }, [
    filter,
    label,
    selectedCategory,
    selectedFilterIds,
    setSelectedFilterIds,
    setSelectedCategory,
    ctrlKeyPressed,
  ]);

  if (!filter || !filter.vessels.size) return null;

  return (
    <FilterBarTab aria-label={label} isActive={isActive} onPress={toggle}>
      <Text mr={3}>{label}</Text>
      <Badge data-testid={`vessels-filter-tab-${label}`} isRounded>
        {filter.vessels.size}
      </Badge>
    </FilterBarTab>
  );
};

const VesselAttributeFilterBar = ({
  timelines,
  selectedFilterIds,
  selectedCategory,
  setSelectedCategory,
  setSelectedFilterIds,
  parentWidth,
  controlsWidth,
}: {
  timelines: EventTimeline[];
  selectedFilterIds: number[];
  selectedCategory: CategoryFilter;
  setSelectedFilterIds: Dispatch<SetStateAction<number[]>>;
  setSelectedCategory: Dispatch<SetStateAction<CategoryFilter>>;
  controlsWidth: number;
  parentWidth: number;
}): JSX.Element | null => {
  const matches = useMedia({ queries: GLOBAL_MEDIA_QUERIES });

  const { data } = useAllVesselAttributesQuery();
  const vesselAttributes = data?.allVesselAttributes?.nodes;
  const [ctrlKeyPressed, setCtrlKeyPressed] = useState(false);

  const [filterLabels, setFilterLabels] = useState(['All']);
  useHotkeys('esc', () => {
    setSelectedFilterIds([0]);
    setFilterLabels(['All']);
  });

  const keyupListener = (event: KeyboardEvent) => {
    if (event.metaKey || event.ctrlKey) setCtrlKeyPressed(true);
  };
  const keydownListener = () => setCtrlKeyPressed(false);

  useEffect(() => {
    document.addEventListener('keydown', keyupListener);
    document.addEventListener('keyup', keydownListener);
    return () => {
      document.removeEventListener('keyup', keyupListener);
      document.removeEventListener('keydown', keydownListener);
    };
  }, []);

  const filtersAndTimeline = useMemo(() => {
    return vesselAttributes?.reduce<
      Map<string, { id: number; vessels: Set<string> }>
    >((acc, cur) => {
      acc.set('All', {
        id: 0,
        vessels: new Set(
          timelines.filter((tl) => !tl.vessel?.inactive).map((tl) => tl.id),
        ),
      });

      if (!cur) return acc;

      acc.set(cur.name, {
        id: cur.id,
        vessels: new Set(
          timelines
            .filter((tl) => {
              if (tl.vessel?.inactive) {
                return false;
              }
              const additionalAttributeFound =
                tl.vessel?.vesselAttributesAssociationsByShipId.nodes.some(
                  (node) => node?.vesselAttributeId === cur.id,
                );
              const primaryAttributeFound =
                tl.vessel?.primaryAttributeId === cur.id;
              return additionalAttributeFound || primaryAttributeFound;
            })
            .map((tl) => tl.id),
        ),
      });

      return acc;
    }, new Map());
  }, [timelines, vesselAttributes]);

  const [filtersRef, { width: filtersWidth }] = useMeasure();
  const usedWidth = filtersWidth + controlsWidth;
  const remainingWidth = parentWidth - usedWidth;

  if (!filtersAndTimeline || filtersAndTimeline?.size === 0) return null;

  return (
    <Box display="flex" alignItems="center" minHeight={48}>
      {matches.small || remainingWidth < 45 ? (
        <DropdownButton
          label={`Vessels: ${filterLabels.join(' & ')}`}
          variant="secondary"
          multiSelect={true}
        >
          <Menu hasShadow maxHeight="90vh" overflowY="auto">
            {Array.from(filtersAndTimeline)
              .filter((ft) => ft[1].vessels.size)
              .map((attribute) => {
                const label = attribute[0];
                const filter = filtersAndTimeline.get(label);
                if (!filter?.vessels) return;

                const toggle = () => {
                  if (!ctrlKeyPressed) {
                    setSelectedFilterIds([filter.id]);
                    setFilterLabels([label]);
                  } else {
                    if (label === 'All') {
                      setSelectedFilterIds([0]);
                      setFilterLabels(['All']);
                      return;
                    }
                    if (selectedFilterIds.includes(filter.id)) {
                      setSelectedFilterIds([
                        ...selectedFilterIds.filter(
                          (x) => x !== filter.id && x !== 0,
                        ),
                      ]);
                    } else {
                      setSelectedFilterIds(
                        [...selectedFilterIds, filter.id].filter(
                          (x) => x !== 0,
                        ),
                      );
                    }
                    setFilterLabels((prev) =>
                      selectedFilterIds.includes(filter.id)
                        ? prev.filter((x) => x !== label)
                        : prev.includes('All')
                          ? prev.filter((x) => x !== 'All').concat(label)
                          : prev.concat(label),
                    );
                  }
                  if (!selectedFilterIds.length) {
                    setSelectedFilterIds([0]);
                    setFilterLabels(['All']);
                  }
                };

                return (
                  <MenuItem
                    key={label}
                    accessibilityLabel={label}
                    label={`${label} - ${filter.vessels.size}`}
                    selected={selectedFilterIds.includes(filter.id)}
                    onPress={toggle}
                  />
                );
              })}
          </Menu>
        </DropdownButton>
      ) : (
        <Box ref={filtersRef} display="flex" alignItems="center">
          <Text fontWeight="bold" color="neutral.white_50" isUppercase mr={3}>
            Vessels
          </Text>
          {Array.from(filtersAndTimeline).map((filter) => {
            const label = filter[0];
            return (
              <VesselAttributeFilterTab
                key={label}
                label={label}
                selectedCategory={selectedCategory}
                selectedFilterIds={selectedFilterIds}
                setSelectedCategory={setSelectedCategory}
                filtersAndTimeline={filtersAndTimeline}
                setSelectedFilterIds={setSelectedFilterIds}
                ctrlKeyPressed={ctrlKeyPressed}
              />
            );
          })}
        </Box>
      )}
    </Box>
  );
};

const GetPortCallUpdates = ({
  isAutoAcceptEnabled,
  acceptAllOnlyConfirmed,
  scenario,
  timelines,
  showAcceptAll,
  saveScenario,
  selectedScenario,
  isCharterer,
  isMobileView,
}: {
  isAutoAcceptEnabled: boolean;
  acceptAllOnlyConfirmed: boolean;
  showAcceptAll: boolean;
  scenario: ScenarioSchedule;
  timelines: EventTimeline[];
  saveScenario: (
    scenarioCID: ScenarioSchedule['clientId'],
    opts: {
      /** When given, updates the scenario.shared value. `undefined` causes no update on the prop. */
      share?: boolean;
      /** When given, use this data instead of whatever exists in scenario store by the scenarioCID*/
      data?: UserSchedule;
      /** When true, save only happens if the scenario already exists in DB */
      updateOnly?: boolean;
      toastId?: ToastNotificationId;
    },
  ) => Promise<InternalScenarioSchedule | undefined>;
  selectedScenario: ScenarioSchedule;
  isCharterer?: boolean;
  isMobileView: boolean;
}) => {
  const getUpdatesToastId: ToastNotificationId = 'getUpdatesToastId';
  const { getPortCallUpdates, applyAllPortCallUpdates, updates } =
    usePortCallUpdates();

  const isShipnetToolsFeatureAvailable = hasFeature('shipnet_tools');
  const { getVoyageCharterUpdates } = useVoyageCharterUpdates();
  const [updateConfirmedVoyages] = useUpdateConfirmedVoyagesMutation();
  const portCallsById = mapKeyBy(scenario.portCalls, (pc) => pc.clientId);
  const handleGetUpdatesPress = useCallback(async () => {
    await withSpinner(async () => {
      if (isShipnetToolsFeatureAvailable) {
        toast(`Updating confirmed voyages`, {
          toastId: getUpdatesToastId,
          type: toast.TYPE.INFO,
          isLoading: true,
        });
        await saveScenario(selectedScenario.clientId, {
          toastId: getUpdatesToastId,
        });
        await updateConfirmedVoyages({
          fetchPolicy: 'no-cache',
          variables: {
            scenarioCid: scenario.clientId,
          },
        }).then((updateConfirmedVoyagesResult) => {
          const result =
            updateConfirmedVoyagesResult.data?.updateConfirmedVoyages;
          if (!result) {
            throw new Error('No data');
          }
          if (result.warnings.length === 0) {
            toast.update(getUpdatesToastId, {
              render: `Update confirmed voyages successfully`,
              type: toast.TYPE.SUCCESS,
              isLoading: false,
              autoClose: 2000,
            });
          } else {
            toast.update(getUpdatesToastId, {
              render: `Update confirmed voyages with warnings:`,
              type: toast.TYPE.WARNING,
              isLoading: false,
            });
            result.warnings.forEach((warning) =>
              toast(warning, {
                type: toast.TYPE.WARNING,
              }),
            );
          }
        });
      }
      if (!isAutoAcceptEnabled) {
        await getPortCallUpdates(portCallsById);
      }

      getVoyageCharterUpdates();
    });
  }, [
    isShipnetToolsFeatureAvailable,
    isAutoAcceptEnabled,
    getVoyageCharterUpdates,
    saveScenario,
    selectedScenario.clientId,
    updateConfirmedVoyages,
    scenario.clientId,
    getPortCallUpdates,
    portCallsById,
  ]);

  const initialPortCallUpdatesLoaded = useRef<boolean>(false);

  useEffect(() => {
    if (portCallsById.size > 0 && !initialPortCallUpdatesLoaded.current) {
      void getPortCallUpdates(portCallsById);
      initialPortCallUpdatesLoaded.current = true;
    }
  }, [portCallsById, getPortCallUpdates]);

  const hasNewUpdates = useMemo(
    () => updates.filter((u) => u.isNew).length > 0,
    [updates],
  );

  const [applyAllUpdatesOptionsVisible, setApplyAllUpdatesOptionsVisible] =
    useState<boolean>(false);

  const timelineForPortcall = useMemo(() => {
    const m = new Map<ClientId | null, EventTimeline>();
    timelines.forEach((timeline) =>
      timeline.voyageGroups.forEach((vg) =>
        vg.events.forEach((td) => {
          if (isPortCallEventTimeData(td)) {
            m.set(td.event.portCallId, timeline);
          }
        }),
      ),
    );
    return m;
  }, [timelines]);

  const filterPortCall = useCallback(
    (selectedVessels: ScenarioTimeline_ShipFragment[]) =>
      (portCallCID: ClientId): boolean => {
        const timeline = timelineForPortcall.get(portCallCID);
        // At least when a port call is archived we don't have it placed it on any current timeline so we'll land here
        // TODO: How can we ignore & clear port call updates for which the port call is already archived?
        if (!timeline) {
          console.warn(
            `No timeline for portcall ${portCallCID} on applying port call update. The port call might be archived.`,
          );
        }
        // Ticket SEABER-1766: We want to filter fleet timelines by vessel selection, other timelines we never filter
        return !(
          timeline &&
          timeline.type === 'tc' &&
          !selectedVessels.find((vessel) => vessel.id === timeline.vesselId)
        );
      },
    [timelineForPortcall],
  );

  const filterableVessels = useMemo(
    () =>
      timelines.flatMap((timeline) =>
        timeline.type === 'tc' ? [timeline.vessel] : [],
      ),
    [timelines],
  );

  const handleApplyAllUpdates = () => {
    if (filterableVessels.length > 0) {
      setApplyAllUpdatesOptionsVisible(true);
    } else {
      applyAllPortCallUpdates({
        onlyConfirmed: acceptAllOnlyConfirmed,
      });
    }
  };

  const eventsByPortCallId = useMemo(() => {
    const m = new DefaultMap<ClientId, TimeData[]>(() => []);
    timelines.forEach((timeline) => {
      timeline.voyageGroups.forEach((vg) =>
        vg.events.forEach((td) => {
          const { portCallId } = td.event;
          if (portCallId !== null) {
            m.get(portCallId).push(td);
          }
        }),
      );
    });
    return m;
  }, [timelines]);

  const handleMinorOrMajorUpdates = (updateType: 'minor' | 'major') => () => {
    const MINOR_UPDATE_THRESHOLD_MS = hToMs(4);

    applyAllPortCallUpdates({
      onlyConfirmed: acceptAllOnlyConfirmed,
      filterUpdate: (update) => {
        const relatedEvent = getRelatedEventForPortCallUpdate(
          update,
          eventsByPortCallId,
        );

        if (!relatedEvent) return false;

        const minStartDate = isPortCallDepartureEventTimeData(relatedEvent)
          ? eventsByPortCallId
              .get(update.portCallId)
              ?.find(isPortCallBerthingEventTimeData)?.start
          : relatedEvent.previousEventStart;

        if (!isDefined(minStartDate) || update.value <= minStartDate) {
          return false;
        }
        const diff = update.value - relatedEvent.start;

        return updateType === 'major'
          ? Math.abs(diff) >= MINOR_UPDATE_THRESHOLD_MS
          : Math.abs(diff) < MINOR_UPDATE_THRESHOLD_MS;
      },
    });
  };

  return (
    <>
      {(isShipnetToolsFeatureAvailable || isCharterer) && !isMobileView && (
        <Button onPress={handleGetUpdatesPress}>Get updates</Button>
      )}

      {showAcceptAll && !isMobileView && (
        <DropdownButton ml={3} label="Accept updates">
          <Menu hasShadow>
            <MenuItem
              label="Accept all minor updates"
              onPress={handleMinorOrMajorUpdates('minor')}
              disabled={selectedScenario.isReadOnly}
            />
            <MenuItem
              label="Accept all major updates"
              onPress={handleMinorOrMajorUpdates('major')}
              disabled={selectedScenario.isReadOnly}
            />
            <MenuItem
              label="Accept all updates"
              disabled={!hasNewUpdates}
              onPress={handleApplyAllUpdates}
            />
          </Menu>
        </DropdownButton>
      )}
      {applyAllUpdatesOptionsVisible && (
        <AcceptAllUpdatesOptions
          isOpen={true}
          onClose={() => setApplyAllUpdatesOptionsVisible(false)}
          vessels={filterableVessels}
          onApply={(selectedVessels) => {
            setApplyAllUpdatesOptionsVisible(false);
            applyAllPortCallUpdates({
              onlyConfirmed: acceptAllOnlyConfirmed,
              filterPortCall: filterPortCall(selectedVessels),
            });
          }}
        />
      )}
    </>
  );
};

function roundHourIfEnabled(
  targetDate: Date | string | number,
  isEnabled: boolean,
) {
  const date = new Date(targetDate);
  if (!isEnabled) {
    return date;
  }
  const minutes = date.getMinutes();
  date.setMinutes(0);
  if (minutes >= 30) {
    date.setHours(date.getHours() + 1);
  }
  return date;
}

export const ResizableDimensionsContext = React.createContext<
  { width: number | string; height: number | string } | undefined
>(undefined);

function getDefaultResizableSize(
  layout: 'horizontal' | 'vertical',
  isMobileView: boolean,
  currentView: 'timeline' | 'cargoes',
) {
  return {
    width: layout === 'horizontal' ? '100%' : '50%',
    height:
      layout === 'horizontal'
        ? isMobileView && currentView === 'cargoes'
          ? '100%'
          : 350
        : '100%',
  };
}

function ResizableContainer({
  children,
  isMobileView,
  currentView,
}: {
  children: React.ReactNode;
  isMobileView: boolean;
  currentView: 'timeline' | 'cargoes';
}): JSX.Element {
  const [planningLayout] = usePlanningLayoutState();

  const defaultSize = useMemo(
    () => getDefaultResizableSize(planningLayout, isMobileView, currentView),
    [currentView, isMobileView, planningLayout],
  );

  const [resizableHeight, setResizableHeight] = useState<number | string>(
    defaultSize.height,
  );
  const [resizableWidth, setResizableWidth] = useState<number | string>(
    defaultSize.width,
  );

  const contextValue = useMemo(
    () => ({
      height: resizableHeight,
      width: resizableWidth,
    }),
    [resizableHeight, resizableWidth],
  );

  useEffect(() => {
    const { width, height } = getDefaultResizableSize(
      planningLayout,
      isMobileView,
      currentView,
    );

    setResizableWidth(width);
    setResizableHeight(height);
  }, [currentView, isMobileView, planningLayout]);

  return (
    <Resizable
      enable={{
        bottom: planningLayout === 'horizontal',
        right: planningLayout === 'vertical',
      }}
      size={{
        width: planningLayout === 'vertical' ? resizableWidth : '100%',
        height: planningLayout === 'horizontal' ? resizableHeight : '100%',
      }}
      defaultSize={defaultSize}
      onResizeStop={(_e, _direction, _ref, d) => {
        if (planningLayout === 'horizontal') {
          setResizableHeight(Number(resizableHeight) + d.height);
        } else {
          const currentWidth =
            resizableWidth === '50%'
              ? window.innerWidth / 2
              : Number(resizableWidth);
          setResizableWidth(currentWidth + d.width);
        }
      }}
      minHeight="54px"
      maxHeight="100%"
      minWidth="3%"
      maxWidth={planningLayout === 'vertical' ? '97%' : '100%'}
      lockAspectRatio={false}
    >
      <ResizableDimensionsContext.Provider value={contextValue}>
        {children}
      </ResizableDimensionsContext.Provider>
    </Resizable>
  );
}

function PlanningLayout({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element {
  const [planningLayout] = usePlanningLayoutState();

  return (
    <Box
      display="flex"
      flex="1"
      flexDirection={planningLayout === 'horizontal' ? 'column' : 'row'}
      minHeight="0"
    >
      {children}
    </Box>
  );
}

const noCompletedOrdersIds = new Set<number>();

function getSuggestionVessels(
  timelinesWithoutExternalFleet: EventTimeline[],
  filterShownTimelines: (timeline: EventTimeline) => boolean,
  filterShownTimelinesByRegionBalance: (timeline: EventTimeline) => boolean,
) {
  const shownTimelines = timelinesWithoutExternalFleet
    .filter(filterShownTimelines)
    .filter(filterShownTimelinesByRegionBalance);

  const shownVessels = uniqBy(
    compact(shownTimelines.map((t) => t.vessel)),
    (v) => v.id,
  );

  const fleetVessels = uniqBy(
    compact(timelinesWithoutExternalFleet.map((t) => t.vessel)),
    (v) => v.id,
  );

  return { shownVessels, fleetVessels };
}

const PlanningImpl = (props: {
  scenarioTimings: ComputedScenarioTimings;
  isCommitRunning: boolean;
}): JSX.Element => {
  const client = useApolloClient();
  const notify = useNotify();

  const matches = useMedia({ queries: GLOBAL_MEDIA_QUERIES });

  const isMobileView = useIsMobileView();

  const currentUser = useCurrentUser();
  const configAutoSyncUserScenarios =
    currentUser?.rootOrganization?.configAutoSyncUserScenarios ?? false;
  const { queueAllDistancesBetweenLocations, fetchMissingDistances } =
    useDistances();
  const { portCosts: marcuraPortCosts, fetchMissingMarcuraPortCosts } =
    useMarcuraPortCosts();
  const { bunkerPriceByGradeName } = useBunkerPrices();
  const currencyRates = useCurrencyRates();
  const defaultCurrency = useDefaultCurrency();

  const isRoundedEstimationsFeatureEnabled = hasFeature(
    'use_rounded_estimations',
  );
  const isRaptorDraftIntegrationEnabled = hasFeature('raptor_draft');

  const planningMode = usePlanningMode();

  const [currentView, setCurrentView] = useState<'timeline' | 'cargoes'>(
    'cargoes',
  );

  const {
    isShipOwner,
    isCharterer,
    isSuggestAvailable,
    isArchiveSelectedAvailable,
    isAutoAcceptEnabled,
    acceptAllOnlyConfirmed,
  } = useMemo(() => {
    const isSuggestAvailable = hasFeature('suggest_voyage');

    const isArchiveSelectedAvailable = hasFeature('archive_selected');

    const isShipOwner = currentUser?.organization?.shipOwner;

    const isCharterer = currentUser?.organization?.charterer;

    const isAutoAcceptEnabled =
      currentUser?.rootOrganization?.configAutoAcceptUpdates ?? false;

    const acceptAllOnlyConfirmed =
      currentUser?.rootOrganization?.configAutoAcceptOnlyConfirmed ?? false;

    return {
      isShipOwner,
      isCharterer,
      isSuggestAvailable,
      isArchiveSelectedAvailable,
      isAutoAcceptEnabled,
      acceptAllOnlyConfirmed,
    };
  }, [currentUser]);

  const {
    masterData,
    selectedScenarioData,
    vessels,
    handledByCoas,
    singleEvents,
    offHires,
    useScenariosResult,
    calculationConfig,
    coas,
    committedData,
    data,
    distances,
    noGoZones,
    organizationsById,
    portIds,
    committedOrderById,
    refetchPlanningPageQuery,
    scheduleContext,
    completedLimit,
    setCompletedLimit,
    configUseCalculatedLimit,
    collapsedTimelines: [collapsedTimelineIds, setCollapsedTimelineIds],
  } = props.scenarioTimings;
  const {
    timelines,
    tcTimeDataByVessel,
    spotEntries,
    coaEntries,
    eventsByVessel,
  } = selectedScenarioData;

  const { currentUserOrganization } = committedData;

  const timelinesWithoutExternalFleet = useMemo(
    () => timelines.filter((t) => !t.vessel?.externalFleet),
    [timelines],
  );

  const [timelineSelectionActual, setTimelineSelection] = useState({
    selectedOrderIds: new Set<Order['id']>(),
    selectedEventIds: new Set<string | number>(),
    selectedSuggestionIds: new Set<string>(),
  });

  const timelineSelection = useDeepMemo(timelineSelectionActual);
  const timelineOrdersSelectionArr = useMemo(
    () => Array.from(timelineSelection.selectedOrderIds),
    [timelineSelection],
  );

  const [selectedOrderIds, setSelectedOrderIds] = useOverridableState<
    Order['id'][]
  >([], timelineOrdersSelectionArr);

  const [orderSearch, saveOrderSearch] = useLocalSetting(
    'planningOrdersFilterSearch',
    '',
  );

  const [comparisonVisible, setComparisonVisible] = useState(false);
  const [isFleetCleanupVisible, setIsFleetCleanupVisible] = useState(false);

  const [selectedStatusFilter, setSelectedStatusFilter] =
    useState<StatusFilter>(StatusFilter.OPEN);

  const timelineRef = useRef<ScenarioTimelineRef>(null);

  const { activeModals, openModal, closeModal, closeAllModals, visibleModal } =
    useModalProvider();

  const [currenttimelineFocus, setCurrenttimelineFocus] =
    useState<TimelineFocus | null>(null);

  const {
    loading,
    selectedScenario,
    scenarioOrderById,
    scenarioOrders,
    scenarios,
    selectScenario,
    createScenario,
    createEmptyScenario,
    mergeScenario,
    deleteScenarioToTrash,
    restoreIntegrationScenario,
    createAssignment,
    deleteAssignment,
    resetAssignmentsToMaster,
    deleteEvent,
    saveScenario,
    setScenarios,
    renameScenario,
    commitScenario,
    createAndUpdateAssignments,
    updateAssignmentsSharing,
    setOrderVoyageCharter,
    setVoyageCharterForOrders,
    updateOrder,
    updateScenariosAfterArchive,
    saveAllocations,
    patchShipment,
    setAutoSync,
    undo,
  } = useScenariosResult;
  const scenario = selectedScenario;
  const { schedule } = scenario;
  const integrationMasterScenario = useMemo(
    () => scenarios?.find((s) => s.isIntegrationMaster),
    [scenarios],
  );
  const isIntegrationScenario = selectedScenario === integrationMasterScenario;
  const completedData = isIntegrationScenario
    ? undefined
    : props.scenarioTimings.completedData;
  const completedOrderIds = isIntegrationScenario
    ? noCompletedOrdersIds
    : committedData.completedOrderIds;

  type UseEventWithUndo = <P extends unknown[], R>(
    params: TransactionParams,
    fn: (...args: P) => R,
  ) => (...args: P) => R;

  const useEventWithUndo: UseEventWithUndo = <P extends unknown[], R>(
    undoParams: TransactionParams,
    fn: (...args: P) => R,
  ) =>
    useEvent((...args: P) =>
      undo.withTransaction(undoParams, () => fn(...args)),
    );
  const isFirstRender = useRef(true);

  const orderByIdRef = useAsRef(scenarioOrderById);

  const timelineDiff = useMemo(
    () =>
      getScenarioTimelinesDiff(
        masterData?.timelines ?? selectedScenarioData.timelines,
        selectedScenarioData.timelines,
        selectedScenario,
      ),
    [masterData, selectedScenario, selectedScenarioData],
  );

  const changedTimelines = useMemo(() => {
    return timelines.filter((timeline) => {
      const masterTimeline = masterData?.timelines.find(
        (m) => m.id === timeline.id,
      );
      return (
        hasTimelineChanged(timeline, timelineDiff) ||
        (masterTimeline && hasTimelineChanged(masterTimeline, timelineDiff))
      );
    });
  }, [masterData?.timelines, timelineDiff, timelines]);

  const [mergeDirectivesByScenario, setMergeDirectivesByScenario] = useState(
    new Map<ClientId, MergeDirectives>(),
  );
  const modifiedTimelineIds = useMemo(() => {
    const changedTimelinesCommitted =
      timelineDiff &&
      masterData?.timelines.filter((timeline) =>
        hasTimelineChanged(timeline, timelineDiff),
      );

    return new Set([
      ...(changedTimelinesCommitted?.map((t) => t.id) ?? []),
      ...(changedTimelines?.map((t) => t.id) ?? []),
    ]);
  }, [changedTimelines, masterData?.timelines, timelineDiff]);

  useEffect(() => {
    if (!loading && !isInitialScenario(scenario)) {
      return;
    }
    startSpinner();
    return endSpinner;
  }, [loading, scenario]);

  const orderIdToVesselName = useMemo(() => {
    const orderIdToVesselName = new Map<number, string>();
    timelines.forEach((item) => {
      const vesselName = item.vessel?.name;
      if (!vesselName) return;
      item.voyageGroups.forEach((group) => {
        group.orders.forEach((order) => {
          orderIdToVesselName.set(order.id, vesselName);
        });
      });
    });
    return orderIdToVesselName;
  }, [timelines]);

  const { regionPolygons, regions } = useRegions();
  const orders = useMemo(() => {
    //filter timeline has vessel assigned
    return scenarioOrders.map((order) => {
      const assignedVessel = orderIdToVesselName.get(order.id);
      return {
        ...order,
        fromRegion: regions.get(order.locationByLoadPortId),
        toRegion: regions.get(order.locationByDischargePortId),
        assignedVessel: assignedVessel ?? null,
      };
    });
  }, [scenarioOrders, orderIdToVesselName, regions]);

  const singleEventTds = useMemo(() => {
    const events = timelines
      .flatMap((tl) => tl.voyageGroups)
      .flatMap((vg) => vg.events);
    return events.filter(isSingleEventTimeData);
  }, [timelines]);

  useEffect(() => {
    const portNames = new Set<string>();

    const checkAndAddPortMissingInfo = (
      port?: Pick<Location, 'longitude' | 'latitude' | 'country' | 'name'>,
    ) => {
      if (
        (!port?.longitude || !port.longitude || !port?.country) &&
        port?.name
      ) {
        portNames.add(port.name);
      }
    };

    orders.forEach((order) => {
      const loadPort = order?.locationByLoadPortId;
      const dischargingPort = order?.locationByDischargePortId;
      checkAndAddPortMissingInfo(loadPort || undefined);
      checkAndAddPortMissingInfo(dischargingPort || undefined);
    });

    if (portNames.size > 0) {
      const portNamesArr = Array.from(portNames).sort();
      notify(
        `Coordinates or country information is missing for the following ports. Please review Admin - Ports to add this essential data. \n${portNamesArr.join(', ')}`,
        {
          toastId: portNamesArr.join(', '),
          secondaryId: `${portNamesArr.join(', ')}-missing`,
        },
      );
    }
  }, [notify, orders]);

  const commitSelectedScenario = useEvent(
    async (
      statusUpdate: (status: CommitStatus) => void,
      {
        syncWithMaster,
        suppressEmail,
      }: {
        syncWithMaster: boolean;
        suppressEmail: boolean;
      },
    ) => {
      if (!committedData) {
        toast.error('Missing committedData');
        return;
      }
      // Validate that events are assigned to port calls, for debug purposes
      for (const timeline of timelines) {
        for (const voyageGroup of timeline.voyageGroups) {
          for (const td of voyageGroup.eventsAndLegs
            .filter(isTimeData)
            .filter(isNotPortCallEventTimeData)) {
            const event = td.event;
            if (!isOffHireEvent(event) && event.portCallId === null) {
              console.warn(
                `Before commit there is an event without portCallId assigned`,
                event,
              );
            }
          }
        }
      }

      return commitScenario(
        selectedScenario.clientId,
        timelines,
        props.scenarioTimings.getScenarioData,
        {
          policy: syncWithMaster
            ? ScenarioCommitPolicy.MERGE_COMPATIBLE
            : ScenarioCommitPolicy.OVERRIDE,
          statusUpdate,
          suppressEmail,
          committedData,
          mergeDirectives:
            mergeDirectivesByScenario.get(selectedScenario.clientId) ?? {},
        },
      );
    },
  );

  const openCommitModal = useCallback(
    (scenarioCID: ScenarioSchedule['clientId']) => {
      if (scenarioCID !== selectedScenario.clientId) {
        toast.error('Activate the target scenario first and then commit.');
        return;
      }

      if (isMasterScenario(selectedScenario)) {
        toast.error(
          'Cannot commit the committed scenario: it is already committed',
        );
        return;
      }

      if (isInitialScenario(selectedScenario)) {
        toast.error(
          'Cannot commit initial scenario: create new scenario first.',
        );
        return;
      }

      if (isBackupScenario(selectedScenario)) {
        toast.error(
          'Cannot commit backup scenario: create new scenario from it first.',
        );
        return;
      }

      openModal({
        type: 'COMMIT_SCENARIO',
        props: {
          onCommit: commitSelectedScenario,
          timelines,
        },
      });
    },
    [selectedScenario, openModal, commitSelectedScenario, timelines],
  );

  const [suggestionsByScenarioCId, setSuggestionsByScenarioCId] = useState<
    ReadonlyMap<ClientId, SuggestionResult | undefined>
  >(new Map());

  const selectedScenarioCId = selectedScenario.clientId;
  const suggestions: SuggestionResult | undefined = useMemo(
    () => suggestionsByScenarioCId.get(selectedScenarioCId),
    [suggestionsByScenarioCId, selectedScenarioCId],
  );
  const setSuggestions = useCallback(
    (suggestions: SuggestionResult | undefined) =>
      setSuggestionsByScenarioCId(
        (prev) =>
          new Map([...prev.entries(), [selectedScenarioCId, suggestions]]),
      ),
    [selectedScenarioCId],
  );
  const clearSuggestions = useCallback(() => {
    setSuggestions(undefined);
  }, [setSuggestions]);

  const setImprovementScenarios = useEvent(
    (suggestionScenarios: SuggestionSchedule[]) => {
      setScenarios([
        ...scenarios.filter((s) => !isSuggestionScenario(s)),
        ...suggestionScenarios,
      ]);
    },
  );

  const handleAcceptImprovementScenario = useEvent(
    (improvement: ScenarioImprovement, toNewScenario: boolean) => {
      const { baseScenarioId } = improvement.scenario;
      const userScenario = suggestionScenarioToUserScenario(
        improvement.scenario,
      );

      if (toNewScenario) {
        setScenarios([
          ...scenarios.filter(guardNot(isSuggestionScenario)),
          userScenario,
        ]);
        selectScenario(userScenario.clientId);
      } else {
        setScenarios(
          filteredMap(scenarios, (s) =>
            isSuggestionScenario(s)
              ? NONE
              : isUserScenario(s) && s.clientId === baseScenarioId
                ? {
                    ...userScenario,
                    name: s.name,
                    clientId: s.clientId,
                    shared: s.shared,
                    id: s.id,
                  }
                : s,
          ),
        );
        selectScenario(improvement.scenario.baseScenarioId);
      }
    },
  );

  const selectedOrders = useMemo(
    () => orders.filter((o) => selectedOrderIds.includes(o.id)),
    [orders, selectedOrderIds],
  );

  const selectedSingleEvents = useMemo(
    () =>
      singleEventTds
        .flatMap((se) => se.event)
        .filter((se) =>
          timelineSelection.selectedEventIds.has(`single-${se.entry.clientId}`),
        ),
    [singleEventTds, timelineSelection.selectedEventIds],
  );

  const ignoreIntegrationCargoMargins =
    !!currentUser?.organization?.configIgnoreIntegrationCargoMargins;

  const [selectedOrdersTotalVolume, selectedOrdersTotalMass] = useMemo(() => {
    return selectedOrders.reduce<[number, number]>(
      ([totalVolume, totalMass], order) => {
        const cargoRange = calculateMassAndVolumeRanges(
          order,
          ignoreIntegrationCargoMargins,
        );
        const volume = midPoint(cargoRange.volume);
        const mass = midPoint(cargoRange.mass);
        return [totalVolume + volume, totalMass + mass];
      },
      [0, 0],
    );
  }, [ignoreIntegrationCargoMargins, selectedOrders]);

  const calculateRemainingSuggestions = (
    suggestions: SuggestionResult,
    orderIdsToRemove?: number[],
    vesselIdsToRemove?: number[],
  ) =>
    mapTransform(
      suggestions.suggestionsByVessel,
      (suggestions, remainingVesselId) => {
        if (vesselIdsToRemove?.includes(remainingVesselId)) return [];
        return suggestions.filter((s) => {
          return !s.entries.some((e) => orderIdsToRemove?.includes(e.orderId));
        });
      },
    );

  /** Update currently shown suggestions by removing given orders and vessels from results */
  const filterCurrentSuggestions = useEvent(
    (opts: { removeOrderIds?: number[]; removeVesselIds?: number[] }): void => {
      if (!suggestions) return;
      const remainingSuggestions = calculateRemainingSuggestions(
        suggestions,
        opts.removeOrderIds,
        opts.removeVesselIds,
      );
      setSuggestions({
        ...suggestions,
        suggestionsByVessel: remainingSuggestions,
      });
    },
  );

  const assignedOrders = useMemo(
    () => scenario.schedule.filter(isTCOrderEntry),
    [scenario.schedule],
  );
  const assignedOrdersById = useMemo(
    () => mapKeyBy(assignedOrders, (o) => o.orderId),
    [assignedOrders],
  );
  const [assignedOrderIds, spotOrderIds, coaOrderIds] = useMemo(
    () => [
      new Set(assignedOrders.map((o) => o.orderId)),
      new Set(
        spotEntries
          .map((item) => {
            return item.orderId;
          })
          .filter(isDefined),
      ),
      new Set(
        coaEntries.map((item) => {
          return item.orderId;
        }),
      ),
    ],
    [assignedOrders, spotEntries, coaEntries],
  );

  const masterScenario = useMemo(
    () => scenarios.find(isMasterScenario),
    [scenarios],
  );

  const orderIdsByVcId = useMemo(() => {
    const result = new Map<number, number[]>();
    for (const order of orders) {
      const voyageId = order.incomingVoyageCharterRelation?.voyageCharterId;
      if (!voyageId) continue;
      const orderId = order.id;

      if (result.has(voyageId)) {
        result.get(voyageId)?.push(orderId);
      } else {
        result.set(voyageId, [orderId]);
      }
    }
    return result;
  }, [orders]);

  const getVoyageCharterConsistency = useCallback(() => {
    // initialize empty result
    const result = {
      orphanParcelIds: new Set<number>(),
      missedOrderIdsByVoyageId: new Map<number, number[]>(),
      differentSchedulingOrderIdsByVoyageId: new Map<number, number[]>(),
    };

    if (!masterScenario) {
      return result;
    }

    // Exit early if no voyage charters found
    if (orderIdsByVcId.size === 0) {
      return result;
    }

    const assignedOrderIdsInMaster = getOrderAssignments(masterScenario);

    for (const [voyageId, orderIds] of orderIdsByVcId) {
      const numberOfOrders = orderIds.length;

      // Skip if only one order in voyage charter
      if (numberOfOrders < 2) {
        continue;
      }

      // Find orders that are not assigned in master scenario
      const orderIdsMissed = orderIds.filter(
        (orderId) => !assignedOrderIdsInMaster.has(orderId),
      );

      // Case 1: Some orders are assigned, some are not = orphan parcels
      if (orderIdsMissed.length > 0 && orderIdsMissed.length < numberOfOrders) {
        result.missedOrderIdsByVoyageId.set(voyageId, orderIdsMissed);
      }
      // Case 2: All orders are assigned, but need to check if assigned consistently
      else if (orderIdsMissed.length === 0) {
        const categories = new Set();
        const vesselIds = new Set();
        const coaIds = new Set();

        for (const orderId of orderIds) {
          const entry = assignedOrderIdsInMaster.get(orderId);
          if (!entry) continue;

          categories.add(entry.category);
          vesselIds.add(entry.vesselId);
          if (isCoaEntry(entry)) {
            coaIds.add(entry.coaId);
          }
        }

        // If orders are assigned to different vessels/contracts/categories
        if (categories.size !== 1 || vesselIds.size !== 1 || coaIds.size > 1) {
          result.differentSchedulingOrderIdsByVoyageId.set(voyageId, orderIds);
        }
      }
    }

    // Create the set of orphan parcels if any exist
    if (result.missedOrderIdsByVoyageId.size > 0) {
      result.orphanParcelIds = new Set(
        Array.from(result.missedOrderIdsByVoyageId.values()).flat(),
      );
    }

    return result;
  }, [masterScenario, orderIdsByVcId]);

  const voyageCharterConsistency = useLazyMemo(getVoyageCharterConsistency);

  useEffect(() => {
    // Skip if no master scenario exists
    if (!masterScenario) {
      return;
    }

    // Calculate orphan parcels and inconsistently assigned orders
    const {
      orphanParcelIds,
      missedOrderIdsByVoyageId,
      differentSchedulingOrderIdsByVoyageId,
    } = voyageCharterConsistency();

    // Show error notification only on the first render
    if (isFirstRender.current) {
      // Notification for orphan parcels
      if (orphanParcelIds.size > 0) {
        const missedText = Array.from(missedOrderIdsByVoyageId.entries())
          .map(([, orderIds]) => `[${orderIds.join(', ')}]`)
          .join('; \n');

        toast.error(
          `The following orders aren't included in the committed version, but other orders on the same voyage charter are. Please assign the orders and commit, or release the rest of the voyage charter from the committed schedule.\n${missedText}`,
          { toastId: `orders-${Array.from(orphanParcelIds).join('-')}` },
        );
      }

      // Notification for inconsistent assignments
      if (differentSchedulingOrderIdsByVoyageId.size > 0) {
        const allDifferentSchedulingOrderIds = Array.from(
          differentSchedulingOrderIdsByVoyageId.values(),
        ).flat();
        const differentSchedulingText = Array.from(
          differentSchedulingOrderIdsByVoyageId.entries(),
        )
          .map(([, orderIds]) => `[${orderIds.join(', ')}]`)
          .join('; \n');
        toast.error(
          `The following orders are scheduled to different vessels/contracts, but have the same cargo.\n${differentSchedulingText}`,
          { toastId: `voyages-${allDifferentSchedulingOrderIds.join('-')}` },
        );
      }
    }

    isFirstRender.current = false;
  }, [orders, masterScenario, voyageCharterConsistency]);

  const inquiryOrderIds = useMemo(() => {
    return new Set<number>(orders.filter(isInquiry).map((o) => o.id));
  }, [orders]);

  const isOrderAssigned = useCallback(
    (orderId: number) =>
      assignedOrderIds.has(orderId) ||
      spotOrderIds.has(orderId) ||
      coaOrderIds.has(orderId),
    [assignedOrderIds, spotOrderIds, coaOrderIds],
  );

  const isOrderRequiresMapping = useCallback(
    (orderId: number) => {
      const order = orderByIdRef.current.get(orderId);
      if (!order?.locationByLoadPortId || !order.locationByDischargePortId)
        return false;
      return (
        isLocationCoordinatesInvalid(order.locationByLoadPortId) ||
        isLocationCoordinatesInvalid(order.locationByDischargePortId)
      );
    },
    [orderByIdRef],
  );
  const coaPrelimsIds = useMemo(() => {
    return new Set<number>(
      orders
        .filter(
          (order) =>
            order.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId
              ?.isPreliminary === true,
        )
        .map((o) => o.id),
    );
  }, [orders]);

  const coaContractNameById = useMemo(() => {
    const result: Map<number, string> = new Map();
    coas.forEach((coa) => {
      result.set(coa.id, coa.name || '');
    });
    return result;
  }, [coas]);

  const getOrderContractName = useCallback(
    (order: ScenarioOrder) => {
      if (scenario) {
        const assignment = scenario.coaAssignments.find(
          ({ orderId }) => orderId === order.id,
        );
        if (assignment) {
          return coaContractNameById.get(assignment.coaId) ?? null;
        }
      }
      if (planningMode === 'voyage_charter' && order.coaId) {
        return coaContractNameById.get(order.coaId) ?? null;
      }
      return null;
    },
    [coaContractNameById, planningMode, scenario],
  );

  useEffect(() => {
    // Whenever assigned orders change, ensure that no current suggestion contains an already assigned order
    filterCurrentSuggestions({
      removeOrderIds: [...assignedOrderIds, ...spotOrderIds, ...coaOrderIds],
    });
  }, [assignedOrderIds, spotOrderIds, coaOrderIds, filterCurrentSuggestions]);

  const facetedFilterMatchersByOrder: Map<
    number,
    Record<string, string[]>
  > = useMemo(() => {
    return new Map(
      orders.map((order): [number, Record<string, string[]>] => {
        const orderId = order.id.toString();
        const fromRegion = normalizeForSearch(order.fromRegion);
        const toRegion = normalizeForSearch(order.toRegion);
        const loadLocation = order.locationByLoadPortId;
        const dischargeLocation = order.locationByDischargePortId;
        const loadPort = normalizeForSearch(loadLocation?.name);
        const dischargePort = normalizeForSearch(dischargeLocation?.name);
        const loadCountry = normalizeForSearch(loadLocation?.country);
        const dischargeCountry = normalizeForSearch(dischargeLocation?.country);
        const orderName = normalizeForSearch(
          order.orderDetailsByOrderId.nodes[0]?.orderName,
        );
        const amount = order.cargoAmount.toString();
        const unit = normalizeForSearch(order.cargoUnitByCargoUnitId?.name);
        const commodity = normalizeForSearch(
          order.assortmentByAssortmentId?.name,
        );
        const commodityGroup = normalizeForSearch(
          order.assortmentByAssortmentId?.assortmentGroupByAssortmentGroup
            ?.name,
        );
        const charterer = normalizeForSearch(
          organizationsById.get(order.chartererOrganizationId ?? NaN)?.name,
        );
        const voyageExternalId = normalizeForSearch(
          order.incomingVoyageCharterRelation?.voyageCharterByVoyageCharterId
            ?.externalId,
        );
        const fixtureNote = normalizeForSearch(
          order.orderDetailsByOrderId.nodes.at(0)?.fixtureNote,
        );
        const remarks = normalizeForSearch(
          order.orderDetailsByOrderId.nodes.at(0)?.extraInfo,
        );
        const contractName = getOrderContractName(order) ?? '';
        const nominatedVessel = normalizeForSearch(order.nominatedVessel);
        const assignedVessel = normalizeForSearch(order.assignedVessel);
        const externalOrderId = normalizeForSearch(order.externalOrderId);
        const cargoCreator = normalizeForSearch(order.userByUserId?.name);
        const type = getOrderContractType(order);

        return [
          order.id,
          {
            id: [orderId],
            port: [loadPort, dischargePort],
            loadPort: [loadPort],
            dischargePort: [dischargePort],
            region: [fromRegion, toRegion],
            fromRegion: [fromRegion],
            toRegion: [toRegion],
            country: [loadCountry, dischargeCountry],
            loadCountry: [loadCountry],
            dischargeCountry: [dischargeCountry],
            amount: [amount],
            unit: [unit],
            from: [loadPort, fromRegion, loadCountry],
            to: [dischargePort, toRegion, dischargeCountry],
            name: [orderName],
            commodity: [commodity],
            commodityGroup: [commodityGroup],
            charterer: [charterer],
            voyageExternalId: [voyageExternalId],
            fixtureNote: [fixtureNote],
            remarks: [remarks],
            contractName: [contractName],
            nominatedVessel: [nominatedVessel],
            assignedVessel: [assignedVessel],
            externalOrderId: [externalOrderId],
            cargoCreator: [cargoCreator],
            type: [type],
          },
        ];
      }),
    );
  }, [getOrderContractName, orders, organizationsById]);

  const orderFilterCompletions = useMemo(() => {
    // Merge all matchers by order into a single collection
    return getCompletionsFromAllMatchers(facetedFilterMatchersByOrder);
  }, [facetedFilterMatchersByOrder]);

  const ordersBySearchKey = useMemo(() => {
    const knownFacets = Object.keys(orderFilterCompletions);
    const facetedQuery = parseFacetedFilterQuery(orderSearch, knownFacets);

    return orders.filter((order) => {
      return facetedQuery
        ? doesFacetedFilterMatch(
            facetedQuery,
            facetedFilterMatchersByOrder.get(order.id) ?? {},
          )
        : true;
    });
  }, [
    orderFilterCompletions,
    orderSearch,
    orders,
    facetedFilterMatchersByOrder,
  ]);

  const [regionBalanceFilter, setRegionBalanceFilterState] =
    useState<RegionBalanceFilter>([]);

  const orderIdsByRegionBalanceFilter = useMemo(
    () =>
      new Set(
        (regionBalanceFilter.length === 0
          ? ordersBySearchKey
          : ordersBySearchKey.filter((order) =>
              regionBalanceFilter.some(
                (filter) =>
                  ((!order.fromRegion && filter.region === 'Others') ||
                    filter.region === order.fromRegion) &&
                  (!filter.toRegion ||
                    (!order.toRegion && filter.toRegion === 'Others') ||
                    filter.toRegion === order.toRegion) &&
                  (!order.layday || filter.endTime.isAfter(order.layday)) &&
                  (!order.cancelling ||
                    filter.startTime.isBefore(order.cancelling)),
              ),
            )
        ).map((order) => order.id),
      ),
    [ordersBySearchKey, regionBalanceFilter],
  );

  const { scenarioOrderIds, publishedOrderIds, unpublishedOrderIds } =
    useMemo(() => {
      const scenarioOrderIds = new Set<number>();
      const publishedOrderIds = new Set<number>();
      const unpublishedOrderIds = new Set<number>();

      const inUserScenario = isUserScenario(selectedScenario);

      [spotEntries, coaEntries, assignedOrders].forEach((entries) =>
        entries.forEach(({ clientId, orderId, shared }) => {
          const inScenario =
            inUserScenario && selectedScenario.entryStates.has(clientId);

          if (inScenario) {
            scenarioOrderIds.add(orderId);
          }

          if (shared) {
            publishedOrderIds.add(orderId);
          } else if (!inScenario) {
            unpublishedOrderIds.add(orderId);
          }
        }),
      );

      return {
        scenarioOrderIds,
        publishedOrderIds,
        unpublishedOrderIds,
      };
    }, [assignedOrders, coaEntries, spotEntries, selectedScenario]);

  const allOrderIdsByStatusFilter: Record<
    StatusFilter,
    Set<number>
  > = useMemo(() => {
    const ids = orders.map((x) => x.id);
    const isVoyageCharterMode = planningMode === 'voyage_charter';

    return {
      [StatusFilter.MAPPING_REQUIRED]: new Set(
        ids.filter((id) => isOrderRequiresMapping(id)),
      ),
      [StatusFilter.INQUIRY]: new Set(
        ids.filter((id) => !isOrderAssigned(id) && inquiryOrderIds.has(id)),
      ),
      [StatusFilter.OPEN]: new Set(
        ids.filter(
          (id) =>
            !isOrderAssigned(id) &&
            !completedOrderIds.has(id) &&
            (isVoyageCharterMode ? !inquiryOrderIds.has(id) : true) &&
            !coaPrelimsIds.has(id) &&
            !voyageCharterConsistency().orphanParcelIds.has(id),
        ),
      ),
      [StatusFilter.COA_PRELIMS]: new Set(
        ids.filter(
          (id) =>
            !isOrderAssigned(id) &&
            !completedOrderIds.has(id) &&
            (isVoyageCharterMode ? !inquiryOrderIds.has(id) : true) &&
            coaPrelimsIds.has(id),
        ),
      ),
      [StatusFilter.PUBLISHED]: new Set(
        ids.filter((id) => publishedOrderIds.has(id)),
      ),
      [StatusFilter.COMPLETED]: new Set(
        ids.filter((id) => completedOrderIds.has(id)),
      ),
      [StatusFilter.SCENARIO]: new Set(
        ids.filter((id) => scenarioOrderIds.has(id)),
      ),
      [StatusFilter.TIMELINE_SELECTION]: new Set(
        ids.filter((id) => timelineSelection.selectedOrderIds.has(id)),
      ),
      [StatusFilter.UNPUBLISHED]: new Set(
        ids.filter((id) => unpublishedOrderIds.has(id)),
      ),
      [StatusFilter.REGION]: orderIdsByRegionBalanceFilter,
      [StatusFilter.ORPHAN_PARCELS]: voyageCharterConsistency().orphanParcelIds,
    };
  }, [
    orders,
    planningMode,
    orderIdsByRegionBalanceFilter,
    isOrderRequiresMapping,
    isOrderAssigned,
    inquiryOrderIds,
    completedOrderIds,
    coaPrelimsIds,
    publishedOrderIds,
    scenarioOrderIds,
    unpublishedOrderIds,
    timelineSelection.selectedOrderIds,
    voyageCharterConsistency,
  ]);

  const filteredOrderIdsByStatusFilter: Record<
    StatusFilter,
    Set<number>
  > = useMemo(() => {
    const keywordFilteredOrderIds = new Set(ordersBySearchKey.map((x) => x.id));

    return transformRecord(allOrderIdsByStatusFilter, (ids) =>
      setFilter(ids, (id) => keywordFilteredOrderIds.has(id)),
    );
  }, [ordersBySearchKey, allOrderIdsByStatusFilter]);

  const isOrderPublished = useCallback(
    (orderId: number) => publishedOrderIds.has(orderId),
    [publishedOrderIds],
  );

  const openOrderIds = useMemo(
    () => new Set(filteredOrderIdsByStatusFilter[StatusFilter.OPEN]),
    [filteredOrderIdsByStatusFilter],
  );

  /** Gets all orders on the same incoming voyage charter as the given orders */
  const getAllIncomingVoyageCharterOrders = useCallback(
    (orderIds: number[]): Set<number> => {
      const set = new Set(orderIds);
      const selectedOrders = orderIds.map((id) => scenarioOrderById.get(id));
      const voyageCharters = new Set<number>();
      for (const order of selectedOrders) {
        const vcId = order?.incomingVoyageCharterRelation?.voyageCharterId;
        if (vcId) {
          voyageCharters.add(vcId);
        }
      }
      for (const order of scenarioOrders) {
        const vcId = order?.incomingVoyageCharterRelation?.voyageCharterId;
        if (vcId && voyageCharters.has(vcId)) {
          set.add(order.id);
        }
      }

      return set;
    },
    [scenarioOrderById, scenarioOrders],
  );

  const [timelinesSearchFilter, setTimelinesSearchFilter] = useLocalSetting(
    'planningTimelinesFilterSearch',
    '',
  );
  const [isCompressedView, setIsCompressedView] =
    useLocalSetting('compressedView');
  const [isEmptyTimelinesHidden, setIsEmptyTimelinesHidden] = useLocalSetting(
    'hideEmptyTimelines',
    false,
  );
  const [isIncompatibleTimelinesHidden, setIsIncompatibleTimelinesHidden] =
    useLocalSetting('hideIncompatibleContracts', false);

  // Keep track of filter and category selection on the root level
  const [selectedFilterIds, setSelectedFilterIds] = useState<number[]>([0]);
  const [selectedCategory, setSelectedCategory] =
    useState<CategoryFilter>('all');

  const { updates: portCallUpdates } = usePortCallUpdatesState(
    undefined,
    timelinePortCallUpdateOpts,
  );

  const { updatesByVoyageCharter } = useVoyageCharterUpdates();

  const updatedTimelines = useMemo(() => {
    const updatedPortCallIds = new Set(
      portCallUpdates.map((u) => u.portCallId),
    );
    const updatedVoyageCharterIds = new Set(updatesByVoyageCharter.keys());

    const updatedTimelineIds = timelines
      .filter((tl) => {
        const timelinePortCallIds = new Set();
        const timelineVoyageCharterIds = new Set();

        tl.voyageGroups.forEach((vg) => {
          vg.portCalls.forEach((pc) => timelinePortCallIds.add(pc.id));
          vg.orders.forEach((o) => {
            const vcRel = o.outgoingVoyageCharterRelation;
            if (vcRel) {
              timelineVoyageCharterIds.add(vcRel.voyageCharterId);
            }
          });
        });

        const hasPortCallUpdates =
          setIntersection([timelinePortCallIds, updatedPortCallIds]).size > 0;
        const hasVoyageCharterUpdates =
          setIntersection([timelineVoyageCharterIds, updatedVoyageCharterIds])
            .size > 0;

        return hasPortCallUpdates || hasVoyageCharterUpdates;
      })
      .map((tl) => tl.id);

    return new Set(updatedTimelineIds);
  }, [timelines, portCallUpdates, updatesByVoyageCharter]);

  const currentUserOrg = currentUser?.organizationId ?? null;

  const { charteringOpenPositions, vesselsWithOpenPosition } =
    useCharteringOpenPositions(currentUserOrg);

  const { vesselReservations, reservedVessels } =
    useVesselReservations(currentUserOrg);

  const timelinesByFilter = useMemo<Record<CategoryFilter, Set<string>>>(() => {
    const activeTimelines = timelines.filter((t) => !t.vessel?.inactive);
    const activeUpdatedTimelines = new Set(
      [...updatedTimelines].filter((timelineId) =>
        activeTimelines.find((timeline) => timeline.id === timelineId),
      ),
    );

    const groupedTimelines = groupBy(timelines, 'type');

    return {
      all: new Set(activeTimelines.map((tl) => tl.id)),
      coa: new Set((groupedTimelines.coa ?? []).map((tl) => tl.id)),
      spot: new Set((groupedTimelines.spot ?? []).map((tl) => tl.id)),
      tc: new Set(
        (
          activeTimelines.filter(
            (t) => t.type === 'tc' && !t.vessel.externalFleet,
          ) ?? []
        ).map((tl) => tl.id),
      ),
      updated: activeUpdatedTimelines,
      open: new Set(
        (groupedTimelines.tc ?? [])
          .filter(
            (tl) =>
              tl.type === 'tc' && vesselsWithOpenPosition.has(tl.vesselId),
          )
          .map((tl) => tl.id),
      ),
      reserved: new Set(
        (groupedTimelines.tc ?? [])
          .filter((tl) => tl.type === 'tc' && reservedVessels.has(tl.vesselId))
          .map((tl) => tl.id),
      ),
      modified: modifiedTimelineIds,
    };
  }, [
    timelines,
    updatedTimelines,
    vesselsWithOpenPosition,
    reservedVessels,
    modifiedTimelineIds,
  ]);

  const timelinesMatchingTypeFilter = useMemo<Set<string>>(() => {
    return new Set(
      timelines
        .filter(
          (tl) =>
            (selectedFilterIds.includes(0) ||
              tl.vessel?.vesselAttributesAssociationsByShipId?.nodes?.find(
                (n) => selectedFilterIds.includes(n?.vesselAttributeId ?? 0),
              ) ||
              (tl.vessel?.primaryAttributeId &&
                selectedFilterIds.includes(tl.vessel?.primaryAttributeId))) &&
            timelinesByFilter[selectedCategory].has(tl.id),
        )
        .map((tl) => tl.id),
    );
  }, [timelines, selectedFilterIds, timelinesByFilter, selectedCategory]);

  const facetedFilterMatchersByTimeline: Map<
    string,
    Record<string, string[]>
  > = useMemo(() => {
    const now = Date.now();
    return new Map(
      timelines.map((timeline): [string, Record<string, string[]>] => {
        /** Ports active recently or in the future  */
        const activeLocations = new Set<string>();
        /** Ports in the past */
        const pastLocations = new Set<string>();

        for (const vg of timeline.voyageGroups) {
          vg.events.filter(isPortCallEventTimeData).forEach((td) => {
            if (isPortCallDepartureEventTimeData(td)) {
              const locationName =
                scheduleContext.locations
                  .get(getEventStartLocationId(td.event))
                  ?.name.replaceAll(' ', '') ?? '';

              // In addition to future ports, we also consider the historical ports from the past 7 days
              if (td.start > now - dToMs(7)) {
                activeLocations.add(locationName);
              }
              if (td.start < now) {
                pastLocations.add(locationName);
              }
            }
          });
        }

        const ownerOrganizationName =
          timeline.type === 'coa'
            ? timeline.coa.shipowner?.name?.replaceAll(' ', '') || ''
            : timeline.type === 'spot' && timeline.ownerOrganizationId !== null
              ? organizationsById
                  .get(timeline.ownerOrganizationId)
                  ?.name?.replaceAll(' ', '') || ''
              : '';

        return [
          timeline.id,
          {
            port: Array.from(activeLocations),
            historicalPort: Array.from(pastLocations),
            coa: [
              (timeline.type === 'coa' &&
                timeline.coa.name?.replaceAll(' ', '')) ||
                '',
            ],
            vessel: [timeline.vessel?.name?.replaceAll(' ', '') || ''],
            shipowner: [ownerOrganizationName],
            capacity: [timeline.subtitle?.replaceAll(' ', '') || ''],
          },
        ];
      }),
    );
  }, [scheduleContext.locations, timelines, organizationsById]);

  const timelineFilterCompletions = useMemo(() => {
    // Merge all matchers by timeline into a single collection
    return getCompletionsFromAllMatchers(facetedFilterMatchersByTimeline);
  }, [facetedFilterMatchersByTimeline]);

  const timelinesMatchingSearchFilter = useMemo(() => {
    const knownFacets = Object.keys(timelineFilterCompletions);
    const facetedQuery = parseFacetedFilterQuery(
      timelinesSearchFilter,
      knownFacets,
    );
    const filteredTls = facetedQuery
      ? timelines.filter((group) =>
          doesFacetedFilterMatch(
            facetedQuery,
            facetedFilterMatchersByTimeline.get(group.id) ?? {},
          ),
        )
      : timelines;

    return new Set(filteredTls.map((tl) => tl.id));
  }, [
    facetedFilterMatchersByTimeline,
    timelines,
    timelinesSearchFilter,
    timelineFilterCompletions,
  ]);

  const coasCompatibleWithSelectedOrders = useMemo(() => {
    if (isIncompatibleTimelinesHidden) {
      const selections = selectedOrderIds.filter((orderId) =>
        openOrderIds.has(orderId),
      );

      return new Set(
        coas
          .filter((coa) =>
            selections.every((orderId) => {
              const order = scenarioOrderById.get(orderId);
              assertDefined(order, 'selected order should exist. this is bug.');
              return checkOrderCompatibleWithCoa(order, coa, 'noop');
            }),
          )
          .map((coa) => coa.id),
      );
    } else {
      return null;
    }
  }, [
    coas,
    isIncompatibleTimelinesHidden,
    openOrderIds,
    scenarioOrderById,
    selectedOrderIds,
  ]);

  const timelinesById = useMemo(
    () => mapKeyBy(timelines, (timeline) => timeline.id),
    [timelines],
  );

  const { data: regionsData } = useAllRegionsQuery();
  const regionsById = useMemo(
    () => mapKeyBy(compact(regionsData?.allRegions?.nodes), (r) => r.id),
    [regionsData],
  );

  const positionRegionsByVesselId = useMemo(() => {
    const openPositions = new DefaultMap<
      number,
      {
        region: string;
        time: number;
      }[]
    >(() => []);

    const ldDaysByRegion = new Map<
      number,
      Array<{
        region: string;
        start: number;
        end: number;
      }>
    >();

    const now = Date.now();
    for (const timeline of timelinesWithoutExternalFleet) {
      if (timeline.type !== 'tc') {
        continue;
      }
      let maxEnd = 0;
      let locationId = -1;
      for (const vg of timeline.voyageGroups) {
        const { events } = vg;

        events.forEach((td) => {
          if (isTCOrderEvent(td.event)) {
            const ldDays = ldDaysByRegion.get(timeline.vesselId) ?? [];
            const location = scheduleContext.locations.get(td.event.locationId);
            const region = regions.get(location) ?? 'Others';

            ldDays.push({
              region,
              start: td.start,
              end: td.end,
            });
            ldDaysByRegion.set(timeline.vesselId, ldDays);
          }
        });

        const lastEvent = events[events.length - 1];
        if (!lastEvent) {
          continue;
        }
        const { end, event } = lastEvent;
        if (end > maxEnd) {
          maxEnd = end;
          locationId =
            'locationId' in event ? event.locationId : event.locationBId;
        }
      }
      const location =
        locationId !== -1
          ? scheduleContext.locations.get(locationId)
          : undefined;
      openPositions.update(timeline.vesselId, (ops = []) => [
        ...ops,
        {
          region: regions.get(location) ?? 'Others',
          time: maxEnd || now,
        },
      ]);
    }

    // Add extra open positions at the start and end of reservations
    for (const {
      vesselId,
      startRegionId,
      endRegionId,
      startTime: startTimeStr,
      duration,
    } of vesselReservations) {
      const startTime = dateStringToNum(startTimeStr);

      const startRegion = isDefined(startRegionId)
        ? regionsById.get(startRegionId)
        : null;
      if (startRegion) {
        openPositions.update(vesselId, (ops = []) => {
          if (ops.some((op) => op.time > startTime)) {
            return ops;
          }
          return [
            ...ops,
            {
              region: startRegion.name,
              time: startTime,
            },
          ];
        });
      }

      const endRegion = isDefined(endRegionId)
        ? regionsById.get(endRegionId)
        : null;
      if (endRegion) {
        const endTime = startTime + dToMs(duration);

        openPositions.update(vesselId, (ops = []) => {
          if (ops.some((op) => op.time > endTime)) return ops;
          return [
            ...ops,
            {
              region: endRegion.name,
              time: endTime,
            },
          ];
        });
      }
    }
    return { openPositions, ldDaysByRegion };
  }, [
    timelinesWithoutExternalFleet,
    scheduleContext.locations,
    regions,
    vesselReservations,
    regionsById,
  ]);

  const [regionBalanceTimelines, setRegionBalanceTimelines] =
    useState<Set<string> | null>(null);

  const setRegionBalanceFilter = useCallback(
    (newRegionBalanceFilter: RegionBalanceFilter) => {
      setRegionBalanceFilterState(newRegionBalanceFilter);

      if (newRegionBalanceFilter.length === 0) {
        setRegionBalanceTimelines(null);
      } else {
        const inFilter = new Set<string>();
        for (const group of timelinesWithoutExternalFleet) {
          if (group.type !== 'tc') {
            continue;
          }
          const vesselId = group.vesselId;

          const openPositions = vesselId
            ? positionRegionsByVesselId.openPositions.get(vesselId)
            : null;

          const ldTimes = vesselId
            ? (positionRegionsByVesselId.ldDaysByRegion.get(vesselId) ?? [])
            : [];

          const inRegionBalanceSelection = newRegionBalanceFilter.some(
            (filter) => {
              const { region, toRegion, startTime, endTime } = filter;
              const isOpen = openPositions?.some(
                (openPosition) =>
                  region === openPosition?.region &&
                  endTime.isAfter(openPosition?.time),
              );

              if (isOpen) {
                return true;
              }

              const filterRange = {
                start: startTime.valueOf(),
                end: endTime.valueOf(),
              };

              return ldTimes.some((ldTime, index) => {
                const sameRegion = ldTime.region === region;
                if (!sameRegion) {
                  return false;
                }
                const overlaps = rangesOverlap(ldTime, filterRange);
                if (!overlaps) {
                  return false;
                }
                if (!toRegion) {
                  return true;
                }
                for (let i = index + 1, l = ldTimes.length; i < l; i++) {
                  if (ldTimes[i].region === toRegion) {
                    return true;
                  }
                }
                return false;
              });
            },
          );

          if (inRegionBalanceSelection) {
            inFilter.add(group.id);
          }
        }
        setRegionBalanceTimelines(inFilter);
      }
    },
    [
      timelinesWithoutExternalFleet,
      positionRegionsByVesselId.openPositions,
      positionRegionsByVesselId.ldDaysByRegion,
    ],
  );

  const filterShownTimelines = useCallback(
    (timeline: EventTimeline): boolean => {
      const predicate = (group: EventTimeline) => {
        const contractIsInactive = group.type === 'coa' && group.coa.inactive;
        const vesselIsInactive = group.vessel?.inactive ?? false;

        const typeMatches = timelinesMatchingTypeFilter.has(group.id);
        const queryMatches = timelinesMatchingSearchFilter.has(group.id);

        const notHiddenAsIncompatible =
          !isIncompatibleTimelinesHidden ||
          group.type !== 'coa' ||
          !coasCompatibleWithSelectedOrders ||
          coasCompatibleWithSelectedOrders.has(group.coaId);

        const notHiddenAsEmpty =
          !isEmptyTimelinesHidden ||
          group.voyageGroups.some((vg) => vg.events.length > 0);

        return (
          !contractIsInactive &&
          !vesselIsInactive &&
          typeMatches &&
          queryMatches &&
          notHiddenAsIncompatible &&
          notHiddenAsEmpty
        );
      };

      if (predicate(timeline)) return true;

      // For COA lines we should show the contract line when any of its vessels matches and for vessel lines the contract line should be shown. For other types only the current line itself matters.
      const contractTimeline =
        'contractTimelineId' in timeline && timeline.type === 'coa'
          ? predicate(
              assertedDefined(
                timelinesById.get(timeline.contractTimelineId),
                `expected contract timeline ${timeline.contractTimelineId}`,
              ),
            )
          : false;

      if (contractTimeline) return true;

      return 'vesselTimelineIds' in timeline
        ? timeline.vesselTimelineIds.some((id) =>
            predicate(
              assertedDefined(
                timelinesById.get(id),
                `expected vessel timeline ${id}`,
              ),
            ),
          )
        : false;
    },
    [
      timelinesMatchingSearchFilter,
      timelinesMatchingTypeFilter,
      coasCompatibleWithSelectedOrders,
      isIncompatibleTimelinesHidden,
      isEmptyTimelinesHidden,
      timelinesById,
    ],
  );

  const filterShownTimelinesByRegionBalance = useCallback(
    (timeline: EventTimeline): boolean => {
      if (!regionBalanceTimelines) return true;

      const predicate = (group: EventTimeline) =>
        regionBalanceTimelines.has(group.id);

      if (predicate(timeline)) return true;

      // For COA lines we should show the contract line when any of its vessels matches and for vessel lines the contract line should be shown. For other types only the current line itself matters.
      const contractTimeline =
        'contractTimelineId' in timeline && timeline.type === 'coa'
          ? predicate(
              assertedDefined(
                timelinesById.get(timeline.contractTimelineId),
                `expected contract timeline ${timeline.contractTimelineId}`,
              ),
            )
          : false;

      if (contractTimeline) return true;

      return 'vesselTimelineIds' in timeline
        ? timeline.vesselTimelineIds.some((id) =>
            predicate(
              assertedDefined(
                timelinesById.get(id),
                `expected vessel timeline ${id}`,
              ),
            ),
          )
        : false;
    },
    [timelinesById, regionBalanceTimelines],
  );

  const setTimelineGroupCollapsed = useCallback(
    (groupId: EventTimeline['id'], isCollapsed: boolean) => {
      const updatedSet = new Set(collapsedTimelineIds);
      if (isCollapsed) {
        updatedSet.add(groupId);
      } else {
        updatedSet.delete(groupId);
      }
      setCollapsedTimelineIds(updatedSet);
    },
    [collapsedTimelineIds, setCollapsedTimelineIds],
  );

  const collapseAllTimelines = useCallback(() => {
    setCollapsedTimelineIds(new Set(timelines.map((timeline) => timeline.id)));

    if (timelineRef.current) {
      timelineRef.current.collapseExpandedVoyages();
    }
  }, [setCollapsedTimelineIds, timelines]);

  const filterOrderByStatus = useCallback(
    (order: ScenarioOrder): boolean => {
      return filteredOrderIdsByStatusFilter[selectedStatusFilter].has(order.id);
    },
    [filteredOrderIdsByStatusFilter, selectedStatusFilter],
  );

  const filteredOrderIds = useDeepMemo(
    useMemo(() => {
      return new Set(
        ordersBySearchKey
          .filter(
            (order) =>
              filterOrderByStatus(order) &&
              orderIdsByRegionBalanceFilter.has(order.id),
          )
          .map((order) => order.id),
      );
    }, [filterOrderByStatus, ordersBySearchKey, orderIdsByRegionBalanceFilter]),
  );
  const onOrderFiltersChange = (filterValues: ItemFilterValues): void => {
    saveOrderSearch(() => filterValues.freeFilter);
  };

  const sharedOrderIds = useMemo(() => {
    return [...scenario.schedule, ...coaEntries, ...spotEntries].reduce<
      Set<number>
    >((set, entry) => {
      if (
        (isTCOrderEntry(entry) || isSpotEntry(entry) || isCoaEntry(entry)) &&
        entry.shared
      ) {
        set.add(entry.orderId);
      }

      return set;
    }, new Set());
  }, [scenario.schedule, coaEntries, spotEntries]);

  const handlePublishVoyage = useCallback(
    async (orderIds: Set<number>, shared: boolean) => {
      if (!currentUser) return;

      try {
        await updateAssignmentsSharing(orderIds, shared);
      } catch (err) {
        console.error('Error publishing assignment', err);
        toast(`${shared ? 'Publishing' : 'Unpublishing'} assignments failed`, {
          type: toast.TYPE.ERROR,
        });
        return;
      }
    },
    [currentUser, updateAssignmentsSharing],
  );

  const onOrderSelectionChange = useCallback((newOrderIds: number[]) => {
    setTimelineSelection((prevState) => ({
      ...prevState,
      selectedOrderIds: new Set(newOrderIds),
    }));
  }, []);

  const onEventSelectionChange = useCallback(
    (newEventIds: (number | string)[]) => {
      setTimelineSelection((prevState) => ({
        ...prevState,
        selectedEventIds: new Set(newEventIds),
      }));
    },
    [],
  );

  const onSuggestionSelectionChange = useCallback(
    (newSuggestionIds: string[]) => {
      setTimelineSelection((prevState) => ({
        ...prevState,
        selectedSuggestionIds: new Set(newSuggestionIds),
      }));
    },
    [],
  );

  const handlePublishOrder = useEvent(
    async (orderIds: number | number[], shared: boolean) => {
      const ids = Array.isArray(orderIds) ? orderIds : [orderIds];
      const orders = ids.map((orderId) => scenarioOrderById.get(orderId));

      const missing = orders.findIndex((o) => !o);
      if (missing !== -1) {
        toast(`Could not find order by id ${ids[missing]}`, {
          type: toast.TYPE.ERROR,
        });
        return;
      }

      const orderIdsToPublish = new Set<number>();

      outer: for (const order of orders as ScenarioOrder[]) {
        const orderId = order.id;
        const shipmentId = order.shipmentId;
        if (shipmentId === null) {
          // Should it even be possible to publish an order without a shipmentId?
          orderIdsToPublish.add(orderId);
        } else {
          for (const tl of timelines) {
            for (const vg of tl.voyageGroups) {
              if (vg.orders.find((o) => o.id === orderId)) {
                for (const o of vg.orders) {
                  if (o.shipmentId === shipmentId) {
                    orderIdsToPublish.add(o.id);
                  }
                }
                continue outer;
              }
            }
          }
          toast(`Could not find voyage for order ${orderId}`, {
            type: toast.TYPE.ERROR,
          });
          return;
        }
      }

      await handlePublishVoyage(orderIdsToPublish, shared);
    },
  );

  const viewingOrderId = useRef<number | null>(null);

  const handleOrderView = useEvent((orderId: number): void => {
    viewingOrderId.current = orderId;
    openModal({
      type: 'VIEW_ORDER',
      props: { orderId },
    });
  });

  const handleOrderDuplicate = useEvent((orderId: number): void => {
    viewingOrderId.current = orderId;
    openModal({
      type: 'CREATE_ORDER',
      props: {
        duplicateFromOrderId: orderId,
        currentUserOrg: assertedDefined(currentUserOrg, 'currentUserOrg'),
      },
    });
  });

  const onDuplicateAndOffsetOrders = useEvent(
    (currentOrderId: number): void => {
      const orderIds =
        selectedOrderIds.length > 0 ? selectedOrderIds : [currentOrderId];
      const orders = orderIds.map((orderId) =>
        assertedDefined(
          scenarioOrderById.get(orderId),
          `expected order ${orderId} to exist for duplication`,
        ),
      );

      openDuplicateOrdersModal({ orders });
    },
  );

  const setOrderShipment = useEventWithUndo(
    { title: `Set shipment` },
    (targetOrderId: Order['id'], shipmentId: ClientId | null) => {
      const existing = orderByIdRef.current.get(targetOrderId);
      assertDefined(existing, 'order to reassign or remove shipment for');

      if (existing.shipmentId === shipmentId) return;

      updateOrder(targetOrderId, { shipmentId });
    },
  );

  /**
   * Bundles given order id's into the same shipment.
   * If shipmentId is directly given, we'll use that.
   * If useFirstShipmentId then using the shipment from the first order in the array, otherwise preferring committed shipments over temporary ones.
   * It's useful to use the first one when bundling new assignments with existing ones as this allows not having to reassign shipment id's on already assigned orders.
   */
  const createShipment = useEventWithUndo(
    { title: `Create shipment` },
    (
      bundledOrderIds: Array<Order['id']>,
      opts: {
        useFirstShipmentId?: boolean;
        doNotReuseExistingShipments?: boolean;
        shipmentId?: ClientId;
      },
    ) => {
      let shipmentId: ClientId;
      if (opts.shipmentId) {
        shipmentId = opts.shipmentId;
      } else {
        const existingShipmentIds = opts.doNotReuseExistingShipments
          ? []
          : bundledOrderIds
              .map((oid) => scenarioOrderById.get(oid))
              .flatMap((order) =>
                order?.shipmentId != null ? [order.shipmentId] : [],
              );

        if (!opts.useFirstShipmentId) {
          existingShipmentIds.sort((a, b) =>
            typeof a !== 'number' ? 1 : typeof b !== 'number' ? -1 : a - b,
          ); // sort tmp ids to end to prefer committed ids
        }
        shipmentId = existingShipmentIds[0] || generateClientId();
      }

      assertDefined(
        currentUser?.id,
        'Cannot create shipment without known user',
      );

      bundledOrderIds.forEach((orderId) =>
        setOrderShipment(orderId, shipmentId),
      );
      return shipmentId;
    },
  );

  const handleSetVoyageCharter = useEventWithUndo(
    { title: `Set voyage charter` },
    (orderIds: number[]) => {
      const orders = orderIds.map((orderId) => {
        const order = scenarioOrderById.get(orderId);
        if (!order) {
          throw new Error('Order missing for order id: ' + orderId);
        }
        return order;
      });
      setVoyageCharterForOrders(orders);
    },
  );

  const isBerthPredictionAvailable =
    !!currentUser?.organization?.organizationFeaturesByOrganizationId?.nodes?.some(
      (node) => node?.featureName === 'berth_prediction',
    );

  const removeOrdersFromTimelineFocus = useEvent((orderIds: Set<number>) => {
    setCurrenttimelineFocus((currentFocus) => {
      if (!currentFocus) return currentFocus;

      const updatedFocus: TimelineFocus = {
        ...currentFocus,
        orderIds:
          currentFocus.orderIds &&
          currentFocus.orderIds.filter((o) => !orderIds.has(o)),
      };

      return updatedFocus;
    });
  });

  /** Cleans up all leftover single events and port calls when orders are unassigned */
  const removeDanglingEventsAndPortCalls = useEvent(
    (removedOrderIds: Set<number>, removedSingleEventIds?: string[]) => {
      const allOrderShipmentIds = compact(
        Array.from(removedOrderIds).map(
          (orderId) => scenarioOrderById.get(orderId)?.shipmentId,
        ),
      );

      for (const timeline of timelines) {
        for (const vg of timeline.voyageGroups) {
          const orderIdsByShipment = new DefaultMap<
            number | ClientId,
            Set<number>
          >(() => new Set());
          for (const td of vg.events) {
            if (isCargoEventTimeData(td)) {
              orderIdsByShipment
                .get(td.event.order.shipmentId ?? NaN)
                .add(td.event.order.id);
            }
          }

          /** Extra port calls and single events which should also be cleaned as by product of removing orders */
          const additionalAffectedEventIds = new Set<ClientId>();
          for (const shipmentId of allOrderShipmentIds) {
            const shipmentOrderIds = [...orderIdsByShipment.get(shipmentId)];
            const allOrdersInShipmentDeleted = shipmentOrderIds.every(
              (orderId) => removedOrderIds.has(orderId),
            );

            if (shipmentOrderIds.length > 0 && allOrdersInShipmentDeleted) {
              const [affectedTds] = getAffectedVoyageEvents(
                vg.events,
                new Set(shipmentOrderIds),
                true,
              );
              for (const td of affectedTds) {
                if (
                  isSingleEventTimeData(td) ||
                  isPortCallArrivalEventTimeData(td)
                ) {
                  additionalAffectedEventIds.add(td.event.entry.clientId);
                }
              }
            }
          }

          const newEvents = vg.events
            .map((td) => td.event)
            .filter(
              (e) =>
                !(isCargoEvent(e) && removedOrderIds.has(e.order.id)) &&
                !(
                  isSingleEvent(e) &&
                  removedSingleEventIds?.includes(e.entry.clientId)
                ) &&
                !additionalAffectedEventIds.has(e.entry.clientId),
            );

          const firstChangedIndex = newEvents.findIndex((e, i) => {
            const shipmentId =
              isTCOrderEvent(e) && (e as TCOrderEvent).order.shipmentId;
            return (
              e !== vg.events[i].event ||
              (shipmentId && allOrderShipmentIds.includes(shipmentId))
            );
          });
          const firstEvent = newEvents[firstChangedIndex];
          if (firstEvent) {
            const portCallId = firstEvent.portCallId;
            let arrivalIndex = firstChangedIndex;
            while (
              newEvents[arrivalIndex]?.portCallId === portCallId &&
              !isPortCallArrivalEvent(newEvents[arrivalIndex]) &&
              arrivalIndex > 0
            ) {
              arrivalIndex--;
            }

            resetArrivalDurations(arrivalIndex, newEvents, calculationConfig);
          }

          if (newEvents.length !== vg.events.length) {
            const changeMaps = deriveEventModifications(
              newEvents,
              vg.events.map((td) => td.event),
              timeline.vessel,
              scheduleContext,
              calculationConfig,
              selectedScenarioData.scenarioPortCallByClientId,
            );
            createAndUpdateAssignments(changeMaps);
          }
        }
      }
    },
  );

  const handleDeleteAssignment = useEventWithUndo(
    { title: 'Delete assignment' },
    (orderIds: number[], keepShipment = false, singleEventIds?: string[]) => {
      const allOrderIds = getAllIncomingVoyageCharterOrders(orderIds);

      // If deleted order is already focused (i.e. shown in shipping plan), remove it from focus before removing it from schedule & timelines
      removeOrdersFromTimelineFocus(allOrderIds);

      for (const orderId of allOrderIds) {
        if (!keepShipment) {
          setOrderShipment(orderId, null);
          setOrderVoyageCharter(orderId, null);
        }

        deleteAssignment(orderId);
      }

      removeDanglingEventsAndPortCalls(allOrderIds, singleEventIds);
    },
  );

  const handleRearrangeShipment = useEventWithUndo(
    { title: `Rearrange shipment` },
    (orderIds: number[], eventsToArrange: TimeData[]) => {
      const timeline = timelines.find((t) =>
        t.voyageGroups.some((v) =>
          v.orders.find((o) => orderIds.includes(o.id)),
        ),
      );

      if (!timeline) {
        console.error('Could not find timeline for orders', ...orderIds);
        return;
      }

      if (!timeline.vessel) {
        return;
      }

      const timelineEvents = timeline.voyageGroups[0].events
        .map((td) => td.event)
        .filter(isTCTimelineEvent);

      const firstEvent = eventsToArrange[0];
      const firstFutureEvent = eventsToArrange.find(
        (td) => td.start > Date.now(),
      );
      if (!firstFutureEvent) {
        toast.warn(
          `Only future events can be rearranged and all the events in this shipments are already in the past. Skipping rearrangement.`,
        );
        return;
      }

      const firstRearrangeIndex = Math.max(
        firstEvent.index,
        firstFutureEvent.index,
      );

      const lastEvent = eventsToArrange[eventsToArrange.length - 1];
      const lastEventIndex = lastEvent.index;

      const changes = rearrangeShipmentEvents(
        timelineEvents,
        firstRearrangeIndex,
        lastEventIndex,
        timeline.vessel,
        scheduleContext,
        calculationConfig,
        selectedScenarioData.scenarioPortCallByClientId,
      );
      createAndUpdateAssignments(changes);
    },
  );

  const handleResetAssignmentsToMaster = useEventWithUndo(
    { title: `Reset to master` },
    (items: ResetAssignmentsToMasterOpts) => {
      const orderIdsFromShipments = (items.shipmentIds ?? []).flatMap(
        (shipmentId) => {
          return uniq(
            [...masterData.scenarioOrderById.values(), ...scenarioOrders]
              .filter((o) => o.shipmentId === shipmentId)
              .map((o) => o.id),
          );
        },
      );
      const orderIds = [...(items.orderIds ?? []), ...orderIdsFromShipments];
      if (currenttimelineFocus) {
        // Remove focus on the touched order in case it might not exist in committed scenario and gets removed
        const updatedFocus: TimelineFocus = {
          ...currenttimelineFocus,
          orderIds:
            currenttimelineFocus.orderIds &&
            currenttimelineFocus.orderIds.filter((o) => !orderIds.includes(o)),
        };
        setCurrenttimelineFocus(updatedFocus);
      }

      /** TODO: Only updating assignments in scenario does not update the scenario with recomputed timelines.
       * This means that the state in scenario store may differ from what's shown on timelines after this operation.
       * The ideal approach is to make the changes to Events, recompute timelines and derive change maps with deriveEventModifications
       * and then update the scenario with the result using createAndUpdateAssignments.
       * This is not a critical problem because scenario will be updated from timelines before commit anyway.
       */
      resetAssignmentsToMaster(orderIds);
    },
  );

  const handleConfirmEventOverwrite = useEventWithUndo(
    { title: `Confirm overwrite` },
    (tds: TimeData[]) => {
      const entries = uniqBy(
        tds.filter(isNotStaticEventTimeData).map((td) => td.event.entry),
        (e) => e.clientId,
      );
      const refreshAllUpdatedAt = <T extends Entry>(
        xs: Entry[],
        filter: (x: Entry) => x is T,
      ): Map<ClientId, T> => {
        return new Map(
          xs
            .filter(filter)
            .map((e) => [e.clientId, { ...e, updatedAt: Date.now() }]),
        );
      };
      createAndUpdateAssignments({
        changedCoas: refreshAllUpdatedAt(entries, isCoaEntry),
        changedEvents: refreshAllUpdatedAt(entries, isSingleEntry),
        changedOffHires: refreshAllUpdatedAt(entries, isOffHireEntry),
        changedPortCalls: refreshAllUpdatedAt(entries, isPortCallEntry),
        changedSpots: refreshAllUpdatedAt(entries, isSpotEntry),
        changedTCs: refreshAllUpdatedAt(entries, isTCOrderEntry),
        deletedEvents: new Map(),
        deletedPortCalls: new Set(),
        updatedTimeData: [],
        changedCargoAllocations: new Map(),
        changedOrderVoyageCharterIds: new Map(),
      });
    },
  );

  const handleCreateSpotAssignment = useEventWithUndo(
    { title: `Create spot assignment` },
    (
      assignments: Array<{
        orderId: number;
        startDate: Date;
        ownerOrganizationId: number | null;
        vesselId: number | null;
        shared?: boolean;
      }>,
      opts: {
        shipWithOrderId: Order['id'] | null;
        deleteIfExisting?: boolean;
        startIsOverride?: boolean;
      },
    ): void => {
      const { deleteIfExisting = true } = opts;

      for (let i = 0; i < assignments.length; ++i) {
        const assignment = assignments[i];

        const order = scenarioOrderById.get(assignment.orderId);
        assertDefined(order, `No order found for assignment`);

        const vessel = vessels.find((ship) => ship.id === assignment.vesselId);
        const specialRequirementFulfilled = vessel
          ? orderFullfillsSpecialRequirements(
              vessel,
              order,
              specialRequirements,
            ).isFulfilled
          : true;

        if (!specialRequirementFulfilled) {
          toast.error(
            `The vessel does not satisfy all order special requirements`,
          );
          return;
        }

        if (deleteIfExisting) {
          const oldHandledBySpot = spotEntries.find(
            (spot) => spot.orderId === order.id,
          );
          if (oldHandledBySpot) {
            handleDeleteAssignment([order.id]);
          }
        }

        // TODO: Creating handledBySpot should maybe get timeline validation processing like TC does (DeriveCapacitiesAndTimes & co)
        const handledBySpot: ExactProps<SpotEntry, typeof assignment> = {
          category: EventCategory.Spot,
          dbId: null,
          clientId: generateClientId(),
          orderId: order.id,
          metadata: '',
          ownerOrganisationId: assignment.ownerOrganizationId,
          vesselId: assignment.vesselId,
          ...initDefaultCoaSpotEntryTimes(
            assignment.startDate.valueOf(),
            undefined,
            !!opts.startIsOverride && i == 0,
          ),
          shared: assignment.shared ?? false,
          loadingConcurrent:
            currentUserOrganization?.configEventsConcurrentDefault ?? false,
          dischargingConcurrent:
            currentUserOrganization?.configEventsConcurrentDefault ?? false,
          loadingPortCallId: null,
          dischargingPortCallId: null,
          updatedAt: Date.now(),
        };

        createAssignment(handledBySpot);
      }

      const orderIds = (
        opts.shipWithOrderId ? [opts.shipWithOrderId] : []
      ).concat(assignments.map((a) => a.orderId));

      const reuseShipmentId = opts.shipWithOrderId !== null;
      createShipment(orderIds, {
        useFirstShipmentId: reuseShipmentId,
        doNotReuseExistingShipments: !reuseShipmentId,
      });
      handleSetVoyageCharter(orderIds);

      setTimelineSelection((prevState) => ({
        ...prevState,
        selectedOrderIds: new Set(),
      }));
      setFocusedOrders(orderIds);
    },
  );

  const handleCreateCoaAssignment = useEventWithUndo(
    { title: `Create coa assignment` },
    (
      assignments: Array<{
        orderId: number;
        startDate: Date;
        coaId: number;
        vesselId: number | null;
        shared?: boolean;
      }>,
      opts: {
        shipWithOrderId: Order['id'] | null;
        startIsOverride?: boolean;
      },
    ): void => {
      const createCoaAssignmentToastId: ToastNotificationId =
        'createCoaAssignmentToastId';
      for (let i = 0; i < assignments.length; ++i) {
        const assignment = assignments[i];
        const vessel = assignment.vesselId
          ? vessels.find((ship) => ship.id === assignment.vesselId)
          : undefined;
        const order = scenarioOrderById.get(assignment.orderId);
        const coa = coas.find((coa) => coa.id === assignment.coaId);

        if (order && coa) {
          const isCompatible = checkOrderCompatibleWithCoa(order, coa, 'toast');
          if (!isCompatible) {
            toast.dismiss(createCoaAssignmentToastId);
            return;
          }

          const durationEstimates = getOrderDurationEstimates({
            distances,
            config: calculationConfig,
            order,
            contract: coa,
            coasById: scheduleContext.coasById,
            vessel,
          });

          if (durationEstimates) {
            const endTime =
              new Date(assignment.startDate).getTime() +
              durationEstimates.totalDuration;

            const handledByCoa: CoaEntry = CreateCOAEntry(
              order,
              {
                startTime: assignment.startDate.valueOf(),
                endTime,
                startIsOverride: !!opts.startIsOverride && i === 0,
              },
              {
                coaId: assignment.coaId,
                vesselId: assignment.vesselId,
                shared: assignment.shared,
                loadingConcurrent:
                  currentUserOrganization?.configEventsConcurrentDefault,
                dischargingConcurrent:
                  currentUserOrganization?.configEventsConcurrentDefault,
              },
            );

            createAssignment(handledByCoa);
          }
        }
      }

      const orderIds = (
        opts.shipWithOrderId ? [opts.shipWithOrderId] : []
      ).concat(assignments.map((a) => a.orderId));

      createShipment(orderIds, {
        useFirstShipmentId: opts.shipWithOrderId !== null,
      });
      handleSetVoyageCharter(orderIds);

      setTimelineSelection((prevState) => ({
        ...prevState,
        selectedOrderIds: new Set(),
      }));
      setFocusedOrders(orderIds);
    },
  );

  const handleMergeToShipment = useEventWithUndo(
    { title: `Merge shipment` },
    (orderIds: Order['id'][]) => {
      return createShipment(orderIds, { useFirstShipmentId: false });
    },
  );

  const handleRemoveOrderFromShipment = useEventWithUndo(
    { title: `Remove order from shipment` },
    (targetOrderId: Order['id']) => {
      setOrderShipment(targetOrderId, generateClientId());
    },
  );

  const handleAssignToSpot = (
    orderIds: Order['id'][],
    startDate?: Date,
  ): void => {
    const ordersToAssign = orders.filter((order) =>
      orderIds.includes(order.id),
    );
    if (!ordersToAssign) {
      return;
    }
    ordersToAssign.forEach((orderToAssign) => {
      handleDeleteAssignment([orderToAssign.id]);
      handleCreateSpotAssignment(
        [
          {
            orderId: orderToAssign.id,
            startDate: startDate ? new Date(startDate) : new Date(),
            ownerOrganizationId: null,
            vesselId: null,
          },
        ],
        { shipWithOrderId: null },
      );
    });
  };

  /** Schedule context does not memoize effectively. We will turn it into a ref so that we're able to memoize *handleSwap*,
   * which we know always wants to use the latest scheduleContext anyway. */
  const scheduleContextForHandleSwap = useAsRef(scheduleContext);
  const scenarioPortCallByClientId = useAsRef(
    selectedScenarioData.scenarioPortCallByClientId,
  );
  /** We also know we want to use the latest timeline data so turning this to a ref is safe too. */
  const timelinesForHandleSwap = useAsRef(timelines);

  const autoAllocateCargo = hasFeature('automatic_cargo_allocations');
  const allocationOptions: CargoAllocationOptions = useMemo(
    () =>
      autoAllocateCargo
        ? {
            allocateCargo: true,
            combineSameAssortment:
              currentUserOrganization?.configCargoAllocationCombineAssortment ??
              false,
          }
        : OPTION_NO_CARGO_ALLOCATIONS,
    [
      autoAllocateCargo,
      currentUserOrganization?.configCargoAllocationCombineAssortment,
    ],
  );

  const violationBasis = useViolationBasis();
  const specialRequirements = useSpecialRequirements();

  const handleCreateAssignment = useEventWithUndo(
    { title: `Assign orders` },
    async (
      assignments: Array<{
        orderId: Order['id'];
        startDate: Date;
        vesselId: Vessel['id'] | null;
        shared?: boolean;
      }>,
      opts: Parameters<ScenarioTimelineProps['onCreateAssignment']>[1] & {
        shipWithOrderId: Order['id'] | null;
        /** If given, used instead of the timeline data available in scope. This is necessary when the data in scope may be out of date. (e.g. when handling moving as delete + create) */
        timelines?: EventTimelineTimeDataOnly[];
        /** Predefined ordering for events to be created (provided when moving events) */
        suggestedEventsOrdering?: Events[];
        singleEventsInfo?: Array<{
          singleEvents: SingleEvent[];
          preTCEvent: LoadEvent | DischargeEvent;
          nextTCEvent: LoadEvent | DischargeEvent;
        }>;
      },
    ): Promise<void> => {
      if (uniq(assignments.map((a) => a.vesselId)).length !== 1) {
        throw new Error(`All created assignments must be on one vessel`);
      }

      const firstAssignment = get(assignments, 0);
      assertDefined(firstAssignment, `Expected at least one assignment`);

      const vessel = vessels.find(
        (ship) => ship.id === firstAssignment.vesselId,
      );
      assertDefined(vessel, `No vessel found for assignment`);

      const shipmentOrderIds = (
        opts.shipWithOrderId ? [opts.shipWithOrderId] : []
      ).concat(assignments.map((a) => a.orderId));

      /** Reuse from: 1) Target order, or 2) Any existing one from these new orders, or finally 3) Create new one */
      const shipmentId: ClientId =
        mappedFind(
          shipmentOrderIds,
          (id) => orderByIdRef.current.get(id)?.shipment?.clientId,
        ) ?? generateClientId();

      const earliestAssignmentStart = Math.min(
        ...assignments.map((a) => +a.startDate),
      );
      // Never create new assignments into the past unless the start time is explicitly overriden
      const earliestStart =
        opts.voyagePlacement === 'exact'
          ? earliestAssignmentStart
          : Math.max(earliestAssignmentStart, Date.now());

      const timeline = (opts.timelines ?? timelinesForHandleSwap.current).find(
        (t) => t.type === 'tc' && t.vessel?.id === vessel.id,
      );
      const originalTds = timeline?.voyageGroups[0].events ?? [];
      const originalEvents: TCTimelineEvent[] = originalTds
        .map((td) => td.event)
        .filter(isTCTimelineEvent);

      const eventBefore =
        findLast(originalTds, (td) => td.start < earliestStart)?.event ??
        originalTds.at(-1)?.event ??
        null;
      const eventAfter =
        originalTds.find((td) => td.start > earliestStart)?.event ?? null;

      // When there's only one assignment there's nothing to optimize, so we don't need to ensure loaded distances
      let requiredDistances = distances;

      const portIds = new Set<number>();
      const assortmentIds = new Set<number>();
      assignments.forEach((assignment) => {
        const order = orderByIdRef.current.get(assignment.orderId);
        assertDefined(order, `No order found for assignment`);
        const loadPortId = order.locationByLoadPortId?.id;
        const dischargePortId = order.locationByDischargePortId?.id;
        const assortmentId = order.assortmentByAssortmentId?.id;
        if (loadPortId) {
          portIds.add(loadPortId);
        }
        if (dischargePortId) {
          portIds.add(dischargePortId);
        }
        if (assortmentId) {
          assortmentIds.add(assortmentId);
        }
      });

      if (
        portIds.size > 0 &&
        assortmentIds.size > 0 &&
        hasFeature('marcura_integration')
      ) {
        fetchMissingMarcuraPortCosts(marcuraPortCosts, portIds, assortmentIds);
      }

      if (assignments.length > 1) {
        const relatedEventLocations = new Set(
          assignments
            .flatMap((assignment) => {
              const order = orderByIdRef.current.get(assignment.orderId);
              assertDefined(order, `No order found for assignment`);
              return compact([order.loadPortId, order.dischargePortId]);
            })
            .concat(
              compact([
                eventBefore && getEventEndLocationId(eventBefore),
                eventAfter && getEventStartLocationId(eventAfter),
              ]),
            ),
        );

        // Ensure that distance matrix is loaded for all related port pairs
        requiredDistances = await fetchMissingDistances(
          distances,
          relatedEventLocations,
        );
      }

      const originalModifications = createTCOrderAssignments(
        originalEvents,
        assignments.map((assignment) => {
          const order = orderByIdRef.current.get(assignment.orderId);
          assertDefined(order, `No order found for assignment`);
          return {
            ...assignment,
            order: {
              ...order,
              shipment: buildShipment(shipmentId),
              shipmentId,
            },
            params: {
              loadingConcurrent:
                currentUserOrganization?.configEventsConcurrentDefault ?? false,
              dischargingConcurrent:
                currentUserOrganization?.configEventsConcurrentDefault ?? false,
            },
          };
        }),
        vessel,
        earliestStart,
        {
          ...scheduleContext,
          distances: requiredDistances,
        },
        calculationConfig,
        selectedScenarioData.scenarioPortCallByClientId,
        {
          shipWithOrderId: opts.shipWithOrderId,
          voyagePlacement: opts.voyagePlacement,
          suggestedEventsOrdering: opts.suggestedEventsOrdering,
          allocationOptions,
          singleEventsInfo: opts.singleEventsInfo,
        },
        violationBasis,
        specialRequirements,
      );

      if (!originalModifications) {
        return;
      }

      let modifications = originalModifications;

      if (currentUserOrganization?.configPortcallDurationBerthapiDefault) {
        await withSpinner(async () => {
          modifications = await updateBerthDurationsFromAPI(
            client,
            modifications,
            isBerthPredictionAvailable,
            scheduleContext,
            calculationConfig,
            selectedScenarioData.scenarioPortCallByClientId,
          );
        }).catch((err) => {
          console.error('Could not fetch berth predictions', err);
          toast.error(
            `Could not get automatic berth duration predictions. Please check the durations manually.`,
          );
        });
      }

      await withSpinner(async () => {
        const changedPortcallsToVoyageCharters = new DefaultMap<
          ClientId,
          Set<number>
        >(() => new Set());
        const allVCs = new Set<number>();

        assignments.forEach((assignment) => {
          const tc = [...modifications.changedTCs.values()].find(
            (x) => x.orderId === assignment.orderId,
          );

          if (tc) {
            [tc.loadingPortCallId, tc.dischargingPortCallId].forEach((pcId) => {
              if (pcId) {
                const vcSet = changedPortcallsToVoyageCharters.get(pcId);
                const vcId = orderByIdRef.current.get(tc.orderId)
                  ?.incomingVoyageCharterRelation
                  ?.voyageCharterByVoyageCharterId?.id;
                if (vcId) {
                  vcSet.add(vcId);
                  allVCs.add(vcId);
                }
              }
            });
          }
        });

        if (allVCs.size === 0) {
          return;
        }

        const { data: portdataEstimates } = await client.query<
          PortCostEstimatesFromVcQueryResult['data'],
          PortCostEstimatesFromVcQueryVariables
        >({
          query: PortCostEstimatesFromVcDocument,
          variables: {
            vcIds: [...allVCs.values()],
          },
          fetchPolicy: 'no-cache',
        });

        if (!portdataEstimates?.allVoyageCharterPortData) {
          return;
        }

        for (const [
          pcId,
          vcIds,
        ] of changedPortcallsToVoyageCharters.entries()) {
          const pcModification = modifications.changedPortCalls.get(pcId);
          if (!pcModification) {
            continue;
          }
          const scenarioPc = scenario.portCalls.find(
            (pc) => pc.clientId === pcId,
          );
          if (scenarioPc && scenarioPc.oneTimeOverrideCost) {
            continue;
          }

          const allCostsByEachVc = [...vcIds.values()]
            .map(
              (vcId) =>
                portdataEstimates?.allVoyageCharterPortData?.nodes?.find(
                  (x) =>
                    x &&
                    x.voyageCharterId === vcId &&
                    x?.portDatumByPortDataId?.portId ===
                      pcModification.locationId,
                )?.portDatumByPortDataId,
            )
            .filter(isDefined);
          const costsSum = allCostsByEachVc.reduce(
            (acc, cur) =>
              acc +
              (cur.portcallCostEstimation
                ? convertToDefaultCurrency(
                    +cur.portcallCostEstimation,
                    cur.portcallCostEstimationCurrency || undefined,
                  ) || 0
                : 0),
            0,
          );

          const oneTimeOverrideCost =
            (pcModification.oneTimeOverrideCost || 0) + costsSum || null;

          pcModification.oneTimeOverrideCost = oneTimeOverrideCost;
          pcModification.useOneTimeOverrideCost = oneTimeOverrideCost !== null;
        }
      });

      createAndUpdateAssignments(modifications);

      for (const assignment of assignments) {
        if (selectedScenario.voyageCharterByOrderId.has(assignment.orderId)) {
          setOrderVoyageCharter(assignment.orderId, null);
        }
      }

      createShipment(shipmentOrderIds, { shipmentId });

      setTimelineSelection((prevState) => ({
        ...prevState,
        selectedOrderIds: new Set(),
      }));
      setFocusedOrders(shipmentOrderIds);
    },
  );

  const handleAddToAssignment = useEventWithUndo(
    { title: `Add to assignment` },
    (
      existing: Order['id'] | Array<Order['id']>,
      newAssignmentOrderIds: Array<Order['id']>,
      requestedStartLoad: Date,
    ): void | Promise<void> => {
      const existingOrderIds = (
        Array.isArray(existing) ? existing : [existing]
      ).filter((id) => isOrderAssigned(id));

      if (existingOrderIds.length === 0) {
        return;
      }

      const assigned = newAssignmentOrderIds.filter((id) =>
        isOrderAssigned(id),
      );
      const toastId: ToastNotificationId = 'AssigningAlreadyAssignedOrder';
      if (
        assigned.length > 0 &&
        newAssignmentOrderIds.length !== assigned.length &&
        !existingOrderIds.every((id) => assigned.includes(id))
      ) {
        const all = newAssignmentOrderIds.join(', ');
        const existing = existingOrderIds.join(', ');

        const msg = `Trying to create new assignments for orders #${all} but ${existing} are already assigned, either add the new orders to that voyage, or un-assign the old ones first`;
        toast.error(msg, {
          toastId: toastId,
          isLoading: false,
        });
        return;
      }

      newAssignmentOrderIds = newAssignmentOrderIds.filter(
        (id) => !isOrderAssigned(id),
      );

      const existingScheduled = schedule
        .filter(isTCOrderEntry)
        .find((e) => existingOrderIds.includes(e.orderId));
      if (existingScheduled) {
        const targetOrderId = existingScheduled.orderId;
        const startLoad = Math.max(
          +requestedStartLoad,
          existingScheduled.loadingStart + 1,
        );

        assertDefined(
          existingScheduled.vesselId,
          `Existing schedule didn't have an assigned vessel.`,
        );

        return handleCreateAssignment(
          newAssignmentOrderIds.map((newAssignmentOrderId, i) => ({
            orderId: newAssignmentOrderId,
            startDate: new Date(+startLoad + i),
            vesselId: existingScheduled.vesselId ?? null,
          })),
          {
            shipWithOrderId: targetOrderId,
            voyagePlacement: 'asap',
          },
        );
      }

      const existingSpot = spotEntries.find((s) =>
        existingOrderIds.includes(s.orderId),
      );
      if (existingSpot) {
        const targetOrderId = existingSpot.orderId;
        const startLoad = Math.max(
          +requestedStartLoad,
          existingSpot.loadingStart + 1,
        );
        return handleCreateSpotAssignment(
          newAssignmentOrderIds.map((newAssignmentOrderId, i) => ({
            orderId: newAssignmentOrderId,
            startDate: new Date(+startLoad + i),
            ownerOrganizationId: existingSpot.ownerOrganisationId ?? null,
            vesselId: existingSpot.vesselId ?? null,
          })),
          { shipWithOrderId: targetOrderId, startIsOverride: false },
        );
      }

      const existingCoaAssignment = coaEntries.find((c) =>
        existingOrderIds.includes(c.orderId),
      );

      if (existingCoaAssignment) {
        const targetOrderId = existingCoaAssignment.orderId;
        const startLoad = Math.max(
          +requestedStartLoad,
          existingCoaAssignment.loadingStart + 1,
        );
        return handleCreateCoaAssignment(
          newAssignmentOrderIds.map((newAssignmentOrderId, i) => ({
            orderId: newAssignmentOrderId,
            startDate: new Date(+startLoad + i),
            coaId: existingCoaAssignment.coaId,
            vesselId: existingCoaAssignment.vesselId ?? null,
          })),
          { shipWithOrderId: targetOrderId, startIsOverride: false },
        );
      }

      if (!existingScheduled && !existingSpot && !existingCoaAssignment) {
        throw new Error(
          `Tried to add orders ${newAssignmentOrderIds.join(
            ', ',
          )} into shipment with order(s) ${existingOrderIds.join(
            ',',
          )} but couldn't find existing assignment.`,
        );
      }
    },
  );

  const openEditVoyageCharterModal = (
    voyageCharterId: number | null = null,
    duplicate = false,
  ) => {
    openModal({
      type: 'EDIT_VOYAGE_CHARTER',
      props: {
        duplicate,
        voyageCharterId,
        handleGetSuggestionsForOrders: handleGetSuggestionsForOrders,
        reApplyCargoAllocations: allocationOptions.allocateCargo
          ? reApplyCargoAllocations
          : undefined,
      },
    });
  };

  const handleVoyageCharterView = useEvent((voyageCharterId: number) =>
    openEditVoyageCharterModal(voyageCharterId),
  );

  const handleVoyageCharterDuplicate = useEvent(
    (voyageCharterId: number): void =>
      openEditVoyageCharterModal(voyageCharterId, true),
  );

  const reApplyCargoAllocations = useReApplyCargoAllocations({
    assignedOrdersById,
    eventsByVessel,
    cargoAllocations: scenario.cargoAllocations,
    allocationOptions,
    saveAllocations,
  });

  const handleSwap = useEventWithUndo(
    { title: `Swap events` },
    async (
      timelineId: EventTimeline['id'],
      _indexToMove: number,
      direction: SwapDirection,
      swappedTd: TimeData,
    ): Promise<void> => {
      let timeline = assertedDefined(
        timelinesForHandleSwap.current.find(
          (timeline) => timeline.id === timelineId,
        ),
        `expected to find timeline ${timelineId}`,
      );
      while (timeline.parent) {
        timeline = timeline.parent;
      }
      const voyageGroup = findVoyageGroupForEvent(timeline, swappedTd.event);
      assertDefined(
        voyageGroup,
        `voyageGroup for ${timeline.type} event at ${swappedTd.event.start} in handleSwap`,
      );

      let changed = swapScheduleEntry(
        voyageGroup.events,
        voyageGroup.vessel,
        swappedTd,
        direction,
        scheduleContextForHandleSwap.current,
        calculationConfig,
        selectedScenarioData.scenarioPortCallByClientId,
      );
      if (
        isBerthPredictionAvailable &&
        currentUserOrganization?.configPortcallDurationBerthapiDefault &&
        hasOrdersChangedInPortCalls(
          [voyageGroup.events],
          changed.updatedTimeData,
        )
      ) {
        await withSpinner(async () => {
          changed = await updateBerthDurationsFromAPI(
            client,
            changed,
            isBerthPredictionAvailable,
            scheduleContextForHandleSwap.current,
            calculationConfig,
            selectedScenarioData.scenarioPortCallByClientId,
          );
        }, `Fetching berth predictions...`).catch((err) => {
          console.error('Could not fetch berth predictions', err);
          toast.error(
            `Could not get automatic berth duration predictions. Please check the durations manually.`,
          );
        });
      }
      createAndUpdateAssignments(changed);
    },
  );

  const handlePortCallSwap: NonNullable<
    ScenarioTimelineProps['onSwapPortCall']
  > = useEventWithUndo(
    { title: 'Swap port calls' },
    async (
      timelineId,
      portCallId,
      shipmentIds,
      direction,
      step = 1,
    ): Promise<void> => {
      let timeline = assertedDefined(
        timelinesForHandleSwap.current.find(
          (timeline) => timeline.id === timelineId,
        ),
        `expected timeline ${timelineId} to swap port call ${portCallId} on to be found`,
      );
      while (timeline.parent) {
        timeline = timeline.parent;
      }
      const voyageGroup = timeline.voyageGroups.find((vg) =>
        vg.events.some((td) => td.event.portCallId === portCallId),
      );
      assertDefined(
        voyageGroup,
        `expected to find the voyage group for the port call ${portCallId} to swap in handlePortCallSwap`,
      );
      let changed = getPortCallSwapModifications(
        voyageGroup.events,
        voyageGroup.vessel,
        portCallId,
        shipmentIds,
        direction,
        scheduleContextForHandleSwap.current,
        calculationConfig,
        step,
        selectedScenarioData.scenarioPortCallByClientId,
      );
      if (
        isBerthPredictionAvailable &&
        currentUserOrganization?.configPortcallDurationBerthapiDefault &&
        hasOrdersChangedInPortCalls(
          [voyageGroup.events],
          changed.updatedTimeData,
        )
      ) {
        await withSpinner(async () => {
          changed = await updateBerthDurationsFromAPI(
            client,
            changed,
            isBerthPredictionAvailable,
            scheduleContextForHandleSwap.current,
            calculationConfig,
            selectedScenarioData.scenarioPortCallByClientId,
          );
        }, `Fetching berth predictions...`).catch((err) => {
          console.error('Could not fetch berth predictions', err);
          toast.error(
            `Could not get automatic berth duration predictions. Please check the durations manually.`,
          );
        });
      }
      createAndUpdateAssignments(changed);
    },
  );

  const pnlData = usePnlData(
    orderByIdRef.current,
    data,
    scheduleContext.portCostEstimateMaps,
  );

  const missingDistances = useMemo(
    () =>
      getMissingDistances(
        distances,
        portIds,
        noGoZones,
        scheduleContext.locations,
      ),
    [distances, portIds, noGoZones, scheduleContext.locations],
  );

  const waitingDistanceUpdate = useRef<((value: Distances) => void)[]>([]);
  const setWaitingDistanceUpdate = useEvent(async (): Promise<Distances> => {
    return new Promise((resolve) => {
      waitingDistanceUpdate.current.push(resolve);
    });
  });

  useEffect(() => {
    if (missingDistances.length === 0) {
      waitingDistanceUpdate.current.forEach((resolve) => resolve(distances));
      waitingDistanceUpdate.current = [];
    }
  }, [distances, missingDistances]);

  const getSuggestVoyageParams = useDialog(
    (
      params: SuggestVoyageModalParams,
      onFinish: (result: SuggestVoyageModalResult | null) => void,
    ) => {
      return (
        <SuggestVoyageModal
          {...params}
          onCancel={() => onFinish(null)}
          onComplete={onFinish}
        />
      );
    },
  );

  const showVoyageSuggestionReport = useDialog(
    (
      params: VoyageSuggestionReportParams,
      onFinish: (_result: VoyageSuggestionReportResult) => void,
    ) => {
      return (
        <VoyageSuggestionReport
          {...params}
          onCancel={() => onFinish(null)}
          onComplete={onFinish}
        />
      );
    },
  );

  const currentScheduleContext = useRef(props.scenarioTimings.scheduleContext);
  currentScheduleContext.current = props.scenarioTimings.scheduleContext;

  const waitForSuggestionRequirements = useEvent(() => {
    return new Promise<{
      updatedContext: ScheduleDataContext;
      wasCancelled: boolean;
    }>(async (resolve) => {
      // Ensure that distance matrix is loaded
      let updatedDistances = distances;
      if (missingDistances.length > 0) {
        queueAllDistancesBetweenLocations(portIds);
        updatedDistances = await setWaitingDistanceUpdate();
      }

      const updatedContext = {
        ...currentScheduleContext.current,
        distances: updatedDistances,
      };
      resolve({ updatedContext, wasCancelled: false });
    });
  });

  const waitLaycanDefault = useAtomValue(isWaitLaycanDefaultAtom);
  const calculateTimeDataPnl = useCalculateTimeDataPnl();

  /** Prefer calling this instead of `getSuggestions` directly. This will handle ensuring refreshed requirements in scope and updating UI automatically. */
  const getSuggestionsWithScope = useEvent(
    async (
      vessels: Vessel[],
      orderIds: number[],
      params: Omit<
        GetSuggestionsInputs,
        | 'delayUntilLD'
        | 'allocationOptions'
        | 'violationBasis'
        | 'specialRequirements'
      >,
    ) => {
      const { updatedContext, wasCancelled } =
        await waitForSuggestionRequirements();
      if (wasCancelled) {
        return;
      }

      const itemsToAssign = bundleIncomingVoyageCharterRelatedOrders(
        orderIds,
        scenarioOrderById,
      );

      if (!itemsToAssign.length) {
        toast.warn('No applicable orders to suggest voyages for');
        return;
      }

      const suggestionsByVessel: SuggestionsByVessel = new Map();

      const result = await getSuggestions(
        itemsToAssign,
        vessels,
        updatedContext,
        schedule,
        eventsByVessel,
        calculationConfig,
        selectedOrderIds,
        {
          ...params,
          delayUntilLD: waitLaycanDefault,
          allocationOptions,
          violationBasis,
          specialRequirements,
        },
        pnlData,
        calculateTimeDataPnl,
        bunkerPriceByGradeName,
        {
          onNewSuggestions: (vesselId, suggestions) => {
            suggestionsByVessel.set(vesselId, suggestions);
            const suggestionResult: SuggestionResult = {
              selectedOrderIds,
              suggestionsByVessel,
              isCompleted: false,
              logs: null,
            };
            setSuggestions(suggestionResult);
          },
        },
      );

      if (isRaptorDraftIntegrationEnabled) {
        await integrationRaptorDraftFilterSuggestions(
          result,
          vessels,
          calculationConfig.locationsById,
        );
      }

      setSuggestions(result);
      if (isMobileView) {
        setCurrentView('timeline');
      }
    },
  );

  /** Get suggestions for a selection of orders from the OrderTable.  */
  const handleGetSuggestionsForOrders = useEvent(async (ids?: number[]) => {
    const { shownVessels, fleetVessels } = getSuggestionVessels(
      timelinesWithoutExternalFleet,
      filterShownTimelines,
      filterShownTimelinesByRegionBalance,
    );
    const params = await getSuggestVoyageParams({
      fleetVessels: fleetVessels,
      shownVessels: shownVessels,
      defaultValues: {
        orderSelection: 'only_selected',
        vesselType: 'shown',
        voyageType: 'new_or_existing_voyage',
      },
    });
    if (params === null) {
      return;
    }

    if (params.vessels.length === 0) {
      toast.warn('No vessels to suggest voyages for');
      return;
    }

    const orderIds = filterOrdersBySuggestionParams(orders, params, {
      selectedOrderIds: ids,
    }).map((o) => o.id);

    await getSuggestionsWithScope(params.vessels, orderIds, params);
  });

  const { open: editLostContract, modalProps: editLostContractModalProps } =
    useModalWithResult<LostContractModalResult, LostContractModalParams>();

  const {
    open: openDuplicateOrdersModal,
    modalProps: duplicateOrdersModalProps,
  } = useModalWithResult<
    DuplicateOrdersModalResult,
    DuplicateOrdersModalParams
  >();

  const {
    open: openImprovementSuggestions,
    modalProps: improvementSuggestionsModalProps,
  } = useModalWithResult<void, { mode: 'improvements' }>();

  /** Suggest one or more orders for the selected vessel. */
  const handleSuggestVoyage = useEvent(
    async (vesselId: number, addToShipment?: ClientId) => {
      const vessel = vessels.find((v) => v.id === vesselId);
      if (!vessel) {
        return;
      }

      const existingEvents = eventsByVessel.get(vesselId);
      if (!existingEvents) {
        return;
      }

      const targetVoyageEvents =
        addToShipment !== undefined
          ? getVoyageEvents(existingEvents, addToShipment)
          : undefined;
      const firstTargetVoyageEvent = first(targetVoyageEvents);
      const lastTargetVoyageEvent = last(targetVoyageEvents);
      const { locations } = scheduleContext;

      const addToVoyage =
        addToShipment !== undefined &&
        firstTargetVoyageEvent &&
        lastTargetVoyageEvent
          ? {
              shipmentId: addToShipment,
              startLocationName:
                locations.get(getEventStartLocationId(firstTargetVoyageEvent))
                  ?.name ?? 'Port',
              endLocationName:
                locations.get(getEventEndLocationId(lastTargetVoyageEvent))
                  ?.name ?? 'Port',
            }
          : undefined;

      const { shownVessels, fleetVessels } = getSuggestionVessels(
        timelinesWithoutExternalFleet,
        filterShownTimelines,
        filterShownTimelinesByRegionBalance,
      );

      const params = await getSuggestVoyageParams({
        fleetVessels: fleetVessels,
        shownVessels: shownVessels,
        vessel: vessel,
        firstAllowedTime: new Date(
          Math.max(firstTargetVoyageEvent?.start ?? Date.now() + 1, Date.now()),
        ),
        addToVoyage: addToVoyage,
        defaultValues: {
          orderSelection: 'all',
          vesselType: 'selected',
          voyageType: 'new_or_existing_voyage',
        },
      });

      if (params === null) {
        return;
      }

      const selectedVessel = params.vesselType === 'selected';

      const isAddingToShipment = selectedVessel && addToVoyage;

      const lastTargetDelivery =
        isAddingToShipment &&
        targetVoyageEvents &&
        findLast(targetVoyageEvents, isCargoEvent)?.order.deliveredBefore;

      if (
        lastTargetDelivery &&
        dateStringToNum(lastTargetDelivery) < params.firstAllowedTime
      ) {
        toast.error(
          `Existing shipment needs to be delivered before first allowed suggestion time, cannot create valid suggestions.`,
        );
        return;
      }

      if (params.vessels.length === 0) {
        toast.warn('No vessels to suggest voyages for');
        return;
      }

      /** All the orders our suggestions may consider given the chosen parameters */
      const orderIds = filterOrdersBySuggestionParams(orders, params).map(
        (o) => o.id,
      );

      await getSuggestionsWithScope(params.vessels, orderIds, {
        ...params,
        addToShipmentId: (isAddingToShipment && addToShipment) || null,
        insertAfter: (isAddingToShipment && firstTargetVoyageEvent) || null,
        insertBefore: (isAddingToShipment && lastTargetVoyageEvent) || null,
      });
    },
  );

  const filterOrdersBySuggestionParams = useCallback(
    (
      orders: ScenarioOrder[],
      params: VoyageSuggestionParams,
      opts?: {
        /** Overrides selectedOrderIds state */
        selectedOrderIds?: number[];
      },
    ): ScenarioOrder[] => {
      const targetOrderIds = opts?.selectedOrderIds ?? selectedOrderIds;

      return orders
        .filter((o) => {
          const selectionFilterMatches =
            !isOrderAssigned(o.id) && targetOrderIds.includes(o.id);

          if (params.orderSelection === 'only_selected') {
            return selectionFilterMatches;
          }

          const orderStatusMaps =
            params.orderSelection === 'only_visible'
              ? filteredOrderIdsByStatusFilter
              : allOrderIdsByStatusFilter;

          const inquiryFilterMatches =
            params.orderTypes.inquiries && orderStatusMaps.INQUIRY.has(o.id);

          const contractType = getOrderContractType(o);

          const openOrderContractTypeFilterMatches =
            (params.orderTypes.coaPreliminaries &&
              contractType === 'PRELIMINARY') ||
            (orderStatusMaps.OPEN.has(o.id) &&
              ((params.orderTypes.fixedCoas && contractType === 'COA') ||
                (params.orderTypes.fixedSpots && contractType === 'SPOT')));

          return (
            selectionFilterMatches ||
            inquiryFilterMatches ||
            openOrderContractTypeFilterMatches
          );
        }) // Drop selected orders if the setting was chosen
        .filter(
          (o) =>
            params.orderSelection !== 'exclude_selected' ||
            !targetOrderIds.includes(o.id),
        );
    },
    [
      isOrderAssigned,
      selectedOrderIds,
      filteredOrderIdsByStatusFilter,
      allOrderIdsByStatusFilter,
    ],
  );

  const handleSuggestVoyageForVessels = useEvent(async () => {
    const { shownVessels, fleetVessels } = getSuggestionVessels(
      timelinesWithoutExternalFleet,
      filterShownTimelines,
      filterShownTimelinesByRegionBalance,
    );

    const params = await getSuggestVoyageParams({
      fleetVessels: fleetVessels,
      shownVessels: shownVessels,
      defaultValues: {
        orderSelection: 'all',
        vesselType: 'shown',
        voyageType: 'new_or_existing_voyage',
      },
    });

    if (params === null) {
      return;
    }

    if (params.vessels.length === 0) {
      toast.warn('No vessels to suggest voyages for');
      return;
    }

    /** All the orders our suggestions may consider given the chosen parameters */
    const orderIds = filterOrdersBySuggestionParams(orders, params).map(
      (o) => o.id,
    );

    await getSuggestionsWithScope(params.vessels, orderIds, params);
  });

  // Helper function to update suggestions after filtering
  const updateSuggestionsWithRemaining = useCallback(
    (orderIdsToRemove?: number[], vesselIdsToRemove?: number[]) => {
      if (!suggestions) return;

      const remainingSuggestions = calculateRemainingSuggestions(
        suggestions,
        orderIdsToRemove,
        vesselIdsToRemove,
      );
      const suggestionsExist = Array.from(remainingSuggestions.values()).some(
        (suggestion) => suggestion.length > 0,
      );

      setSuggestions(
        suggestionsExist
          ? {
              ...suggestions,
              suggestionsByVessel: remainingSuggestions,
            }
          : undefined,
      );
    },
    [suggestions, setSuggestions],
  );

  const handleSuggestionSelect = useEventWithUndo(
    { title: `Select suggestion` },
    (vesselId: number, suggestionIndex: number): void => {
      if (!suggestions || !suggestions.isCompleted) return;
      const suggestion =
        suggestions.suggestionsByVessel.get(vesselId)?.[suggestionIndex];
      if (suggestion?.schedule) {
        const eventsByVessel: VesselToSortedEvents = new Map();
        const tdsByVessel = new Map();
        const events = suggestion.schedule.combination.schedule;
        eventsByVessel.set(vesselId, events);
        timelines.forEach((t) => {
          if (t.type === 'tc') {
            tdsByVessel.set(t.vesselId, t.voyageGroups[0].events);
          }
        });
        for (const td of suggestion.schedule.timeData) {
          td.event.start = td.start;
        }

        const changes = getChangedScheduleEntries(
          vessels,
          {
            entries: suggestion.entries,
            eventsByVessel,
          },
          tdsByVessel,
          scheduleContext,
          calculationConfig,
          selectedScenarioData.scenarioPortCallByClientId,
        );

        const getOrderIncomingVoyageCharterId = (
          order: ScenarioOrder,
        ): number | null =>
          order.incomingVoyageCharterRelation?.voyageCharterId ?? null;

        // A suggestion may have multiple empty-to-empty voyages, split these to separate shipments
        const tds = suggestion.schedule.timeData.filter(isCargoEventTimeData);

        let shipmentCounter = 0;
        const shipments = groupBySequence(tds, (_td, i) => {
          const prevTd = get(tds, i - 1);
          if (prevTd && prevTd.ordersOnBoard === 0) {
            shipmentCounter++;
          }
          return shipmentCounter;
        });

        // Merge shipments that have the same voyage charter
        const mergedShipments: TimeData<CargoEvent>[][] = [];
        for (const shipmentTds of shipments) {
          const shipmentWithSameVC = mergedShipments.find((mergedTds) =>
            mergedTds.some((td) =>
              shipmentTds.some(
                (td2) =>
                  getOrderIncomingVoyageCharterId(td.event.order) ===
                  getOrderIncomingVoyageCharterId(td2.event.order),
              ),
            ),
          );
          if (shipmentWithSameVC) {
            shipmentWithSameVC.push(...shipmentTds);
          } else {
            mergedShipments.push(shipmentTds);
          }
        }

        let suggestionShipmentOrderIds: number[] = [];

        for (const tds of mergedShipments) {
          suggestionShipmentOrderIds = uniq(
            tds
              .filter(isCargoEventTimeData)
              .map((td) => td.event.order.id)
              // Only set shipment to new assignments
              .filter(
                (orderId) =>
                  !!suggestion.entries.find((e) => e.orderId === orderId),
              ),
          );
          if (suggestionShipmentOrderIds.length === 0) {
            continue;
          }

          let shipmentId = suggestion.shipmentId;

          if (shipmentId != null) {
            for (const orderId of suggestionShipmentOrderIds) {
              setOrderShipment(orderId, shipmentId);
            }
          } else {
            // Create the shipment itself and patch orders
            shipmentId = createShipment(suggestionShipmentOrderIds, {
              useFirstShipmentId: false,
              doNotReuseExistingShipments: false,
            });
          }
          // Patch shipmentId in entries to be written into scenario
          for (const entryChange of changes.changedTCs.values()) {
            if (suggestionShipmentOrderIds.includes(entryChange.orderId)) {
              entryChange.shipmentId = shipmentId;
            }
          }
        }

        createAndUpdateAssignments(changes);

        const orderIds = suggestion.entries.map((e) => e.orderId);
        handleZoomToVoyages(orderIds);

        const orderIdsToRemove = orderIds;
        const vesselIdsToRemove = [vesselId];

        /** Remove only those suggestions that had their used up by the selection, and those on the same vessel */
        updateSuggestionsWithRemaining(orderIdsToRemove, vesselIdsToRemove);
      }
    },
  );

  const [dismissedSuggestionIds, setDismissedSuggestionIds] = useState<
    Set<`${number}-${number}`>
  >(new Set());

  // Clear dismissed suggestion ids when suggestions complete
  useEffect(() => {
    if (!suggestions) {
      setDismissedSuggestionIds(new Set());
    }
  }, [suggestions, setDismissedSuggestionIds]);

  const handleDismissSuggestion = useCallback(
    (vesselId: number, suggestionIndex: number): void => {
      const newDismissedSuggestionIds = new Set(dismissedSuggestionIds);
      newDismissedSuggestionIds.add(`${vesselId}-${suggestionIndex}`);
      setDismissedSuggestionIds(newDismissedSuggestionIds);

      const vesselSuggestions = suggestions?.suggestionsByVessel.get(vesselId);
      if (!vesselSuggestions || !suggestions) return;

      // If all suggestions of vessel are dismissed, remove the vessel from the suggestions
      const suggestionKeys = vesselSuggestions.map(
        (_, i) => `${vesselId}-${i}` as const,
      );
      const allSuggestionOfVesselDismissed = suggestionKeys.every((key) =>
        newDismissedSuggestionIds.has(key),
      );
      if (allSuggestionOfVesselDismissed) {
        updateSuggestionsWithRemaining(undefined, [vesselId]);
      }
    },
    [suggestions, dismissedSuggestionIds, updateSuggestionsWithRemaining],
  );

  const handleUnassignSelectedOrders = (): void => {
    handleDeleteAssignment(
      selectedOrderIds,
      false,
      selectedSingleEvents.map((e) => e.entry.clientId),
    );
  };

  const [editingScenarioName, setEditingScenarioName] = useState<string | null>(
    null,
  );

  const [completedBeforeDate, setCompletedBeforeDate] = useState<
    Date | undefined
  >();

  const handleSelectCompleted = (): void => {
    openModal({
      type: 'SELECT_COMPLETED',
      props: {
        onSelectCompletedBeforeDate: (date: Date) => {
          handleSelectBefore(
            orders,
            singleEvents,
            date,
            setSelectedOrderIds,
            setTimelineSelection,
            setCompletedBeforeDate,
          );
        },
      },
    });
  };

  const handleArchiveSelected = useEvent(async (): Promise<boolean> => {
    const ordersToArchive = new Set(selectedOrderIds);
    const portCallsToArchive = new Set<number>();
    const scheduledOrdersToArchive = new Set<number>();
    const handledBySpotsToArchive = new Set<number>();
    const handledByCoasToArchive = new Set<number>();
    const singleEventsToArchive = new Set<number>();
    const offHiresToArchive = new Set<number>();

    for (const orderId of ordersToArchive) {
      const order = scenarioOrderById.get(orderId);
      if (!order) continue;

      for (const pc of order.orderPortCallRelationsByOrderId.nodes) {
        const pcId = pc?.portCallId;
        if (pcId !== undefined) {
          portCallsToArchive.add(pcId);
        }
      }

      for (const so of order.scheduledOrdersByOrderId.nodes) {
        const id = so?.id;
        if (id !== undefined) {
          scheduledOrdersToArchive.add(id);
        }
      }

      const spotId = order.handledBySpotByOrderId?.id;
      if (spotId !== undefined) {
        handledBySpotsToArchive.add(spotId);
      }

      for (const hbc of handledByCoas) {
        const id = hbc?.id;
        if (id !== undefined && hbc.orderId === orderId) {
          handledByCoasToArchive.add(id);
        }
      }
    }

    for (const se of selectedSingleEvents) {
      const singleEvent = singleEvents.find(
        (e) => e.clientId.replace(/-/g, '') === se.entry.clientId,
      );
      assertDefined(singleEvent, 'Could not find single event');
      const nodes =
        singleEvent.singleEventPortCallRelationsBySingleEventId.nodes;
      for (const pc of nodes) {
        const pcId = pc?.portCallId;
        if (pcId !== undefined) {
          portCallsToArchive.add(pcId);
        }
      }
      const id = singleEvent?.id;
      if (!id) {
        toast.error(
          'Could not find single event id. Please try to commit and archive again',
        );
        continue;
      }
      if (
        nodes.every((pc) => {
          const pcId = pc?.portCallId;
          return pcId !== undefined && portCallsToArchive.has(pcId);
        })
      ) {
        singleEventsToArchive.add(id);
      }
    }

    for (const oh of offHires) {
      const id = oh?.id;
      if (id !== undefined && moment(oh.end).isBefore(completedBeforeDate)) {
        offHiresToArchive.add(id);
      }
    }

    for (const order of scenarioOrders) {
      if (!ordersToArchive.has(order.id)) {
        const conflictingPCRelation = compact(
          order.orderPortCallRelationsByOrderId.nodes,
        ).find((rel) => portCallsToArchive.has(rel.portCallId));
        if (conflictingPCRelation) {
          const orderDisplayName = getOrderDisplayName(order);
          toast.error(
            `Cannot archive port call ${
              conflictingPCRelation.portCallId
            } because it is the ${
              conflictingPCRelation.isLoadPort ? 'loading' : 'discharging'
            } port call for order ${orderDisplayName} which is not being archived. Select fewer orders to archive or include order ${orderDisplayName}.`,
          );
          return false;
        }
      }
    }

    const orderIdsArray = Array.from(ordersToArchive);
    toast.info(`Archiving orders #${orderIdsArray.join(', ')}...`);

    await archiveByIds(
      client,
      orderIdsArray,
      Array.from(portCallsToArchive),
      Array.from(scheduledOrdersToArchive),
      Array.from(handledBySpotsToArchive),
      Array.from(handledByCoasToArchive),
      Array.from(singleEventsToArchive),
      Array.from(offHiresToArchive),
    );

    const toastId = toast.info(`Re-fetching data after archive...`);

    setSelectedOrderIds([]);
    clearTimelineSelection();
    setCompletedBeforeDate(undefined);

    await refetchPlanningPageQuery();

    await updateScenariosAfterArchive();

    toast.update(toastId, {
      render: `Orders #${orderIdsArray.join(', ')} were successfully archived`,
      type: toast.TYPE.SUCCESS,
      autoClose: 5000,
    });

    return true;
  });

  const handleMarkAsLostContract = useEvent(
    async (orderIds: number[]): Promise<boolean> => {
      assertDefined(masterScenario, `Could not find master scenario`);

      const masterAssignments = getOrderAssignments(masterScenario);
      const scenarioAssignments = getOrderAssignments(selectedScenario);

      // Ensure that every order in every affected voyage charter is included, even if it wasn't initially selected
      const orderGroupsToMark = bundleIncomingVoyageCharterRelatedOrders(
        orderIds,
        scenarioOrderById,
      );

      const validGroups = orderGroupsToMark.filter((orderGroup) => {
        // Make sure every order on this voyage charter is free from assignments
        for (const order of orderGroup) {
          const isOrderAssignedInMaster = masterAssignments.has(order.id);
          const isOrderAssignedInScenario = scenarioAssignments.has(order.id);
          if (isOrderAssignedInMaster || isOrderAssignedInScenario) {
            const orderDisplayName = getOrderDisplayName(order);
            toast.error(
              `Cannot mark order ${orderDisplayName} as lost contract because it belongs to a ${
                isOrderAssignedInMaster ? 'committed ' : ''
              }voyage.`,
            );
            return false;
          }
        }
        return true;
      });

      const successfulGroups = (
        await Promise.all(
          validGroups.map(async (orderGroup) => {
            const result = await editLostContract({ orders: orderGroup });
            return [orderGroup, result] as const;
          }),
        )
      )
        .filter(([, result]) => result)
        .map(([orderGroup]) => orderGroup);

      if (successfulGroups.length === 0) {
        notify('No orders were marked as lost contracts', {
          type: 'warning',
          toastId: 'handleMarkAsLost',
        });

        return false;
      }

      notify(`Archiving ${successfulGroups.flat().length} order(s)`, {
        type: 'info',
        toastId: 'handleMarkAsLost',
      });

      await archiveByIds(
        client,
        validGroups.flat().map((o) => o.id),
        [],
        [],
        [],
        [],
        [],
        [],
      );

      notify(`Marking as lost done, re-fetching data.`, {
        type: 'info',
        toastId: 'handleMarkAsLost',
      });

      setSelectedOrderIds([]);
      clearTimelineSelection();

      await refetchPlanningPageQuery();

      await updateScenariosAfterArchive();

      notify('Marking as lost done', {
        type: 'success',
        toastId: 'handleMarkAsLost',
      });

      return true;
    },
  );

  /*
   * When moving whole shipments (isCollapsed) we always try to keep their
   * shipment and voyage charter relations intact.
   *
   * When moving partial shipments, merging a shipment into another
   * one (shipwithOrderId) or when moving a shipment from a contract (coa/spot)
   * timeline to a tc/vessel timeline we want to destroy any relations to the
   * previous shipment / voyage charter.
   */
  const handleMoveAssignment = useEventWithUndo(
    { title: `Move assignment` },
    async (
      orderIds: Order['id'][],
      singleEventIds: string[],
      firstStart: Date,
      targetTimeline: EventTimeline,
      isCollapsed: boolean, // true when orderIds includes the whole shipment
      opts: {
        voyagePlacement: VoyagePlacement;
        shipWithOrderId: number | null; // defined when we want to add the orderIds to an existing shipment
        /** When moving orders to change their shipments we get a better result if we use the delete-recreate method */
        alwaysDeleteFirst?: boolean;
        toastId?: ToastNotificationId;
      },
    ): Promise<void> => {
      const moveAssignmentToastId: ToastNotificationId =
        opts.toastId ?? 'moveAssignmentToastId';
      const sourceTimeline = timelines.find((t) =>
        t.voyageGroups.some((v) =>
          v.orders.find((o) => orderIds.includes(o.id)),
        ),
      );

      assertDefined(
        sourceTimeline,
        `Expected to find the timeline to move from`,
      );

      const allOrderIds = getAllIncomingVoyageCharterOrders(orderIds);

      // If we move along the same timeline (i.e. to change time / sequence) we can make a much more efficient update on the entries instead of delete + create
      if (!opts.alwaysDeleteFirst && sourceTimeline.id === targetTimeline.id) {
        const modifications = moveAssignmentsAlongTimeline(
          targetTimeline,
          allOrderIds,
          +firstStart,
          isCollapsed,
          scheduleContext,
          calculationConfig,
          selectedScenarioData.scenarioPortCallByClientId,
          {
            voyagePlacement: opts.voyagePlacement,
          },
        );
        createAndUpdateAssignments(modifications);
        toast.dismiss(moveAssignmentToastId);
        return;
      }

      const keepShipment =
        isCollapsed &&
        opts.shipWithOrderId === null &&
        (targetTimeline.type !== 'tc' || sourceTimeline.type === 'tc');

      try {
        handleDeleteAssignment(
          Array.from(allOrderIds),
          keepShipment,
          singleEventIds,
        );
      } catch (err) {
        toast.error(`Error moving assignments: ${err.message}`);
        return;
      }

      const sourceVoyageGroup = sourceTimeline.voyageGroups.find((vg) =>
        vg.orders.find((o) => orderIds.includes(o.id)),
      );

      if (!sourceVoyageGroup) {
        toast.error(
          `Source voyage group not found when moving orders ${orderIds.join(
            ', ',
          )}`,
        );
        return;
      }

      const allOldEvents = sourceVoyageGroup.events;

      const [oldEventsSequence] = getAffectedVoyageEvents(
        allOldEvents,
        new Set(allOrderIds),
        isCollapsed,
      );

      if (targetTimeline.type === 'tc') {
        // Filter out deleted assignments from the timedata array because otherwise they will be detected at creation and re-created.
        // Coa & Spot don't need this at the moment because they don't study the timeline at entry creation.
        const updatedTimelines: EventTimelineTimeDataOnly[] = timelines.map(
          (t) => ({
            type: t.type,
            vessel: t.vessel,
            voyageGroups: t.voyageGroups.map((vg) => ({
              events: vg.events.filter(
                (e) =>
                  !(
                    isCargoEventTimeData(e) && allOrderIds.has(e.event.order.id)
                  ),
              ),
            })),
          }),
        );

        const oldSingleEvents: Array<{
          singleEvents: SingleEvent[];
          preTCEvent: LoadEvent | DischargeEvent;
          nextTCEvent: LoadEvent | DischargeEvent;
        }> = [];
        for (const [startIdx, startTd] of allOldEvents.entries()) {
          if (isTCOrderEventTimeData(startTd)) {
            // Find following discharge event and the single events between
            const singleEvents: SingleEvent[] = [];
            for (let i = startIdx + 1; i < allOldEvents.length; i++) {
              const nextTd = allOldEvents[i];
              // If there is load event
              if (isSingleEventTimeData(nextTd)) {
                singleEvents.push(nextTd.event);
              } else if (isTCOrderEventTimeData(nextTd)) {
                if (singleEvents.length > 0) {
                  oldSingleEvents.push({
                    preTCEvent: startTd.event,
                    nextTCEvent: nextTd.event,
                    singleEvents,
                  });
                }
                break;
              }
            }
          }
        }

        await handleCreateAssignment(
          Array.from(allOrderIds).map((orderId, i) => ({
            orderId,
            startDate: new Date(+firstStart + i),
            vesselId: targetTimeline.vesselId,
          })),
          {
            shipWithOrderId: opts.shipWithOrderId,
            voyagePlacement: opts.voyagePlacement,
            timelines: updatedTimelines,
            suggestedEventsOrdering: oldEventsSequence.map((td) => td.event),
            singleEventsInfo: oldSingleEvents,
          },
        );
      } else if (targetTimeline.type === 'spot') {
        handleCreateSpotAssignment(
          Array.from(allOrderIds).map((orderId, i) => ({
            orderId,
            startDate: new Date(+firstStart + i),
            ownerOrganizationId: targetTimeline.ownerOrganizationId,
            vesselId: targetTimeline.vesselId,
          })),
          {
            shipWithOrderId: opts.shipWithOrderId,
            deleteIfExisting: false,
            startIsOverride: opts.voyagePlacement === 'exact',
          },
        );
      } else if (targetTimeline.type === 'coa') {
        handleCreateCoaAssignment(
          Array.from(allOrderIds).map((orderId, i) => ({
            orderId,
            startDate: new Date(+firstStart + i),
            coaId: targetTimeline.coaId,
            vesselId: targetTimeline.vesselId ?? null,
          })),
          {
            shipWithOrderId: opts.shipWithOrderId,
            startIsOverride: opts.voyagePlacement === 'exact',
          },
        );
      } else {
        exhaustiveCheck(targetTimeline);
      }

      // If we move orders and delete their old assignments & shipments, we
      // create a new shipment for them here unless we merged them to another
      // shipment
      if (!keepShipment && opts.shipWithOrderId === null) {
        createShipment(Array.from(allOrderIds), {
          useFirstShipmentId: false,
          doNotReuseExistingShipments: true,
        });
      }
    },
  );

  const handleChangeOrdersVoyage = useEventWithUndo(
    { title: `Reassign order` },
    async (
      orderIds: Set<Order['id']>,
      targetTimelineId: EventTimeline['id'],
      opts: Parameters<ScenarioTimelineProps['onChangeOrdersVoyage']>[2],
    ) => {
      const timeline = assertedDefined(
        timelines.find((t) => t.id === targetTimelineId),
        `expected to find timeline ${targetTimelineId} for reassigning order voyage`,
      );
      const sourceTimelimes = timelines.filter((t) =>
        t.voyageGroups.some((v) => v.orders.some((o) => orderIds.has(o.id))),
      );
      const sameTimeline =
        sourceTimelimes.length === 1 && sourceTimelimes[0].id === timeline.id;
      await handleMoveAssignment(
        [...orderIds],
        opts.singleEventIds,
        opts.startDate,
        timeline,
        opts.isWholeShipment,
        {
          voyagePlacement: opts.voyagePlacement,
          shipWithOrderId: opts.shipWithOrderId,
          alwaysDeleteFirst: !sameTimeline || opts.shipWithOrderId !== null,
        },
      );
    },
  );

  const handleMoveSingleEvent = useEvent(
    async (
      targetTimelineId: EventTimeline['id'],
      singleEventIds: string[],
      opts: {
        startDate: Date;
      },
    ) => {
      const targetTimeline = assertedDefined(
        timelines.find((t) => t.id === targetTimelineId),
        `expected to find timeline ${targetTimelineId} for reassigning single event`,
      );
      const sourceTimeline = timelines.find((t) =>
        t.voyageGroups.some((v) =>
          v.events.find(
            (e) =>
              isSingleEventTimeData(e) &&
              singleEventIds.some((id) => id.includes(e.event.entry.clientId)),
          ),
        ),
      );

      assertDefined(
        sourceTimeline,
        `Expected to find the timeline to move from`,
      );

      const sameTimeline = targetTimeline.id === sourceTimeline.id;

      const singleEvent = sourceTimeline.voyageGroups
        .flatMap((e) => e.events)
        .filter(isSingleEventTimeData)
        .find((e) =>
          singleEventIds.some((id) => id.includes(e.event.entry.clientId)),
        );

      assertDefined(singleEvent, 'Single event is not found');

      // If we move along the same timeline (i.e. to change time / sequence) we can make a much more efficient update on the entries instead of delete + create
      if (sameTimeline) {
        const originalTimeData = targetTimeline.voyageGroups.find((v) =>
          v.events.find(
            (e) =>
              isSingleEventTimeData(e) &&
              singleEventIds.some((id) => id.includes(e.event.entry.clientId)),
          ),
        )?.events;
        assertDefined(
          originalTimeData,
          `Expected to find existing timeline events for single event to move`,
        );

        const singleEventPortCallId = originalTimeData.find((e) =>
          singleEventIds.some((id) => id.includes(e.event.entry.clientId)),
        )?.event.portCallId;

        const [existingTds, timeDataWithMovedRemoved] = partition(
          originalTimeData,
          (td) => td.event.portCallId === singleEventPortCallId,
        );

        let indexToInsertAt = timeDataWithMovedRemoved.findIndex(
          (event) => +event.start > +opts.startDate,
        );

        if (indexToInsertAt === -1) {
          indexToInsertAt = timeDataWithMovedRemoved.length;
        }

        let movedEvents: Events[] = existingTds.map((td, i) => ({
          ...td.event,
          // Clear the overrides for other events because they are no longer relevant.
          startOverride: null,
          seaDurationOverride: i === 0 ? null : td.event.seaDurationOverride,
        }));

        if (timeDataWithMovedRemoved.length === 0) {
          movedEvents = applyVoyageStart(movedEvents, +opts.startDate);
        }

        const newAllEvents = timeDataWithMovedRemoved.map((td) => td.event);
        newAllEvents.splice(indexToInsertAt, 0, ...movedEvents);

        const modifications = deriveEventModifications(
          newAllEvents,
          originalTimeData.map((td) => td.event),
          movedEvents[0].vessel ?? null,
          scheduleContext,
          calculationConfig,
          selectedScenarioData.scenarioPortCallByClientId,
          { firstChangedIndex: indexToInsertAt },
        );

        createAndUpdateAssignments(modifications);
      } else {
        // remove single event and related port calls
        for (const timeline of timelines) {
          for (const vg of timeline.voyageGroups) {
            const singleEventPortCallId = vg.events.find((e) =>
              singleEventIds.some((id) => id.includes(e.event.entry.clientId)),
            )?.event.portCallId;
            if (!singleEventPortCallId) continue;
            const newEvents = vg.events
              .map((td) => td.event)
              .filter((e) => e.portCallId !== singleEventPortCallId);

            const changeMaps = deriveEventModifications(
              newEvents,
              vg.events.map((td) => td.event),
              timeline.vessel,
              scheduleContext,
              calculationConfig,
              selectedScenarioData.scenarioPortCallByClientId,
            );
            createAndUpdateAssignments(changeMaps);
          }
        }

        if (targetTimeline.vesselId) {
          const tcTimeline = targetTimeline.voyageGroups[0];
          const after = tcTimeline.events.find(
            (e) => e.start > +opts.startDate,
          );
          handleCreateEvent(
            +singleEvent.event.event,
            targetTimeline.vesselId,
            singleEvent.event.locationId,
            singleEvent.event.locationName,
            singleEvent.event.locationTimeZone,
            +singleEvent.event.duration,
            after?.index || -1,
            +opts.startDate,
          );
        } else {
          toast.error(`Strange vessel id`);
        }
      }
    },
  );

  const eventsRef = useRef<VesselToSortedEvents>();
  useEffect(() => {
    eventsRef.current = eventsByVessel;
  }, [eventsByVessel]);

  const handleUpdateEntry = useEventWithUndo(
    { title: `Update values` },
    async (timeline: EventTimeline, event: Event, change: Partial<Events>) => {
      await withSpinner(async () => {
        while (timeline.parent) {
          timeline = timeline.parent;
        }
        const voyageGroup = findVoyageGroupForEvent(timeline, event);
        assertDefined(
          voyageGroup,
          'voyageGroup for event in handleUpdateEntry',
        );

        const td = voyageGroup.eventsAndLegs
          .filter(isTimeData)
          .find((td) => td.event === event);
        assertDefined(td, `Event to update not found in timeline`);
        const indexToModify = voyageGroup.eventsAndLegs.indexOf(td);

        const result = modifyEntry(
          scheduleContext,
          voyageGroup,
          indexToModify,
          change,
          calculationConfig,
          selectedScenarioData.scenarioPortCallByClientId,
        );

        createAndUpdateAssignments(result);
      });
    },
  );

  const handleApplyPortCallUpdate = useEventWithUndo(
    { title: `Apply port call updates` },
    (updates: PortCallUpdateBase[]) => {
      let result: ChangedEntries | null = null;
      // Some update logic may unset overrides on later events when an earlier one is updated.
      // We avoid this by processing updates from left to right
      updates = sortBy(updates, (u) => u.value);

      for (const tl of timelinesForHandleSwap.current) {
        for (const vg of tl.voyageGroups) {
          const voyageGroup = { ...vg };

          for (const update of updates) {
            const tdIndex = voyageGroup.eventsAndLegs.findIndex(
              (e) =>
                isTimeData(e) &&
                e.event.category === EventCategory.PortCall &&
                e.event.event === update.type &&
                e.event.portCallId === update.portCallId,
            );

            if (tdIndex === -1) {
              continue;
            }

            const startOverride = roundHourIfEnabled(
              update.value,
              isRoundedEstimationsFeatureEnabled,
            );

            const changes: Partial<Events> = {
              startOverride: startOverride.getTime(),
            };

            const td = voyageGroup.eventsAndLegs[tdIndex];
            const prevTd = get(voyageGroup.eventsAndLegs, tdIndex - 1);
            // Automatically shorten an existing sea duration override when applying the start override would yield a timing conflict
            if (prevTd && isTimeData(td) && isPortCallArrivalEvent(td.event)) {
              if (td.event.seaDurationOverride !== null) {
                changes.seaDurationOverride = Math.max(
                  minimumEventDuration,
                  Math.min(
                    td.event.seaDurationOverride,
                    +startOverride - prevTd.start,
                  ),
                );
              }
            }

            const newResult = modifyEntry(
              scheduleContextForHandleSwap.current,
              voyageGroup,
              tdIndex,
              changes,
              calculationConfig,
              selectedScenarioData.scenarioPortCallByClientId,
            );

            result =
              result === null
                ? newResult
                : mergeEntryModifications(result, newResult);

            // Update the voyage group with recalculated timedata, to be used for the next update in the same group
            voyageGroup.events = result.updatedTimeData[0]; // The result from modifyEntry only contains the single timedata array
            voyageGroup.eventsAndLegs = voyageGroup.events.map((td) => ({
              ...td,
              key: 'dummy', // modifyEntry doesn't use the key but the type requires it
            }));
            voyageGroup.legs = []; // Not really used in modifyEntry but cleaner to keep it in sync with events array
          }
        }
      }

      if (!result) {
        console.warn(`There were no port call updates to apply`);
        return;
      }

      const resultWithTimeSource = addTimeSourcesToResult(result, updates);

      createAndUpdateAssignments(resultWithTimeSource);
    },
  );

  const handleIgnorePortCallUpdates = useEventWithUndo(
    { title: `Ignore port call updates` },
    (portCallIds: ClientId[]) => {
      const now = new Date().valueOf();

      const changedPortCalls: ChangedEntries['changedPortCalls'] = new Map(
        portCallIds.flatMap((clientId) => {
          const existingEntry =
            scenarioPortCallByClientId.current.get(clientId);
          if (!existingEntry) {
            console.warn(
              `On ignoring port call update the port call ${clientId} was not found. It may be archived already.`,
            );
            return [];
          }
          return [
            [
              clientId,
              {
                ...existingEntry,
                plannedArrivalOverrideSourcedAt: now,
                plannedBerthingOverrideSourcedAt: now,
                plannedDepartureOverrideSourcedAt: now,
              },
            ],
          ];
        }),
      );

      const changeMaps: ChangedEntries = initChangedEntries({
        changedPortCalls,
      });

      createAndUpdateAssignments(changeMaps);
    },
  );

  const handleDeleteTCEventByIndex = useEvent(
    (vesselId: number, indexToDelete: number) => {
      const eventsByVessel = eventsRef.current;
      const events = eventsByVessel?.get(vesselId);
      const event = events?.[indexToDelete];
      if (!event) return;
      if (event.category == EventCategory.PortCall) {
        throw new Error(`PortCall events cannot be deleted`);
      } else if (event.category == EventCategory.TC) {
        const { order } = event;
        handleDeleteAssignment([order.id]);
      } else {
        const result = deleteEntry(
          scheduleContext,
          eventsByVessel,
          indexToDelete,
          vesselId,
          calculationConfig,
          selectedScenarioData.scenarioPortCallByClientId,
        );
        if (event.category === EventCategory.Single) {
          deleteEvent(event.entry.clientId, event.category);
        } else if (event.category === EventCategory.OffHire) {
          deleteEvent(event.entry.clientId, event.category);
        }
        createAndUpdateAssignments(result);
      }
    },
  );

  const handleCreateEvent = useEventWithUndo(
    { title: `Create single event` },
    (
      kind: SingleEventType,
      vesselId: number,
      locationId: number,
      locationName: string,
      locationTimeZone: string | null,
      duration: number,
      index: number,
      start: number,
    ): void => {
      const vessel = vessels.find((ship) => ship.id === vesselId);
      if (vessel) {
        const result = createSingleEventEntry(
          eventsRef.current,
          vessel,
          kind,
          locationId,
          locationName,
          locationTimeZone,
          duration,
          scheduleContext,
          index,
          start,
          calculationConfig,
          selectedScenarioData.scenarioPortCallByClientId,
        );
        createAndUpdateAssignments(result);
      } else {
        toast.error(`Strange vessel id`);
      }
    },
  );

  const handleSaveOffHire = useEventWithUndo(
    { title: `Create off-hire` },
    (offHire: OffHireEntry): void => {
      const vessel = vessels.find((ship) => ship.id === offHire.vesselId);
      if (vessel) {
        const result = createOffHireEntry(
          eventsRef.current,
          vessel,
          offHire,
          scheduleContext,
          calculationConfig,
          selectedScenarioData.scenarioPortCallByClientId,
        );
        createAndUpdateAssignments(result);
      } else {
        toast.error(`Strange vessel id`);
      }
    },
  );

  const handleDeleteOffHire = useEventWithUndo(
    { title: `Delete off-hire` },
    (offHire: OffHireEntry): void => {
      const eventsByVessel = eventsRef.current;
      if (offHire.vesselId == null) {
        throw new Error(`No vessel id`);
      }
      const events = eventsByVessel?.get(offHire.vesselId);
      const eventIdx = events?.findIndex(
        (e) =>
          e.type === CoreEventType.OffHire &&
          e.entry.clientId === offHire.clientId,
      );
      if (eventIdx === undefined) {
        throw new Error(`Event not found on vessel`);
      }
      handleDeleteTCEventByIndex(offHire.vesselId, eventIdx);
    },
  );

  const handleDeleteSingleEvent = useEventWithUndo(
    { title: `Delete single event` },
    (singleEvent: SingleEvent): void => {
      const eventsByVessel = eventsRef.current;
      if (singleEvent.vesselId == null) {
        throw new Error(`No vessel id`);
      }
      const events = eventsByVessel?.get(singleEvent.vesselId);
      const eventIdx = events?.findIndex(
        (e) =>
          e.type === CoreEventType.Single &&
          e.entry.clientId === singleEvent.entry.clientId,
      );
      if (eventIdx === undefined) {
        throw new Error(`Event not found on vessel`);
      }
      handleDeleteTCEventByIndex(singleEvent.vesselId, eventIdx);
    },
  );

  const handleViewAssignment = useEvent(
    (timelineFocus: TimelineFocus): void => {
      const hasCompletedOrders = timelineFocus.orderIds?.some((id) =>
        completedOrderIds.has(id),
      );
      const timeline = (
        hasCompletedOrders && completedData
          ? completedData.timelines
          : timelines
      ).find((t) => t.id === timelineFocus.timelineId);
      assertDefined(timeline, 'timeline not found');

      setCurrenttimelineFocus(timelineFocus);

      openModal({
        type: 'VIEW_SHIPMENT',
        props: {
          vessels,
          spotAssignments: spotEntries,
          coaAssignments: coaEntries,
          onUnassignOrder: (orderId) => handleDeleteAssignment([orderId]),
          scenario: selectedScenario,
          navTarget: timelineFocus.navTarget,
          completedData,
          completedOrderIds,
        },
      });
    },
  );

  const handleViewOffHire = useEvent((offHire: OffHireEntry) => {
    if (offHire.vesselId == null) {
      throw new Error(`OffHire without vesselId`);
    }

    openModal({
      type: 'EDIT_OFFHIRE',
      props: {
        vesselId: offHire.vesselId,
        initialStartTime: undefined,
        onSave: handleSaveOffHire,
        offHire,
      },
    });
  });

  const handleViewOpenPosition = useEvent(
    (openPosition: OpenPositionsFragment, lastItemEnd: number) => {
      if (openPosition.vesselId == null) {
        throw new Error(`OpenPosition without vesselId`);
      }

      openModal({
        type: 'EDIT_OPEN_POSITION',
        props: {
          vesselId: openPosition.vesselId,
          openPosition,
          lastItemEnd,
        },
      });
    },
  );

  const handleViewVesselReservation = useEvent(
    (vesselReservation: VesselReservationFragment, lastItemEnd: number) => {
      if (vesselReservation.vesselId == null) {
        throw new Error(`vessel reservation without vesselId`);
      }

      openModal({
        type: 'VESSEL_RESERVATION',
        props: {
          vesselId: vesselReservation.vesselId,
          vesselReservation,
          lastItemEnd,
        },
      });
    },
  );

  const handleSetVoyageAttribute = useEvent(
    (
      clientId: ClientId,
      shipment: ScenarioShipment | null,
      hasChange: boolean,
    ) => {
      openModal({
        type: 'VOYAGE_ATTRIBUTE',
        props: {
          clientId,
          shipment,
          hasChange,
        },
      });
    },
  );

  const openSpeedOptimizationModal = useDialog(
    (
      params: {
        vessel: Vessel;
        numLegsToOptimize: number;
      },
      onFinish: (result: LegOptimizationModalInputs | null) => void,
    ) => {
      const availableSpeeds = getAllAvailableSpeeds(params.vessel);

      return (
        <SpeedOptimizationModal
          legCount={params.numLegsToOptimize}
          onCancel={() => onFinish(null)}
          onComplete={onFinish}
          availableSpeeds={availableSpeeds}
        />
      );
    },
  );

  const handleOptimizeSpeed = useEventWithUndo(
    {
      title: 'Optimize leg speeds',
    },
    async (params: {
      timelineId: EventTimeline['id'];
      portCallsToOptimize: Set<ClientId>;
    }) => {
      const timeline = timelines.find((t) => t.id === params.timelineId);
      const vessel = timeline?.vessel;
      if (!timeline || !vessel) {
        console.error('Timeline not found for voyage optimization');
        return;
      }

      // Get optimization inputs from the user

      const timelineTds = timeline.voyageGroups.flatMap((vg) => vg.events);

      const optimizationParams = await openSpeedOptimizationModal({
        vessel,
        numLegsToOptimize: params.portCallsToOptimize.size,
      });

      if (!optimizationParams) {
        return;
      }

      // Prepare parameters for TCE computation

      const incomingFreightByOrderId = getFreightsByOrderId(
        pnlData.incomingVoyageChartersById,
        selectedScenarioData.scenarioOrderById,
      );

      const getIncomeForLoadedAmount: GetIncomeForLoadedAmount =
        incomeForLoadedAmountGetter(
          selectedScenarioData.scenarioOrderById,
          incomingFreightByOrderId,
          pnlData.incomingVoyageChartersById,
          currencyRates,
          defaultCurrency,
          bunkerPriceByGradeName,
          pnlData.worldscaleFreightRates,
        );

      const tceOptions: LegOptimizationContext['tceOptions'] = {
        euEtsCostEnabled: isEuEtsCostEnabledForUser(currentUser),
        fuelEuCostEnabled:
          !!scheduleContext.currentUser?.rootOrganization
            ?.configIncludeFuelEuInCosts,
        pnlData,
        portCallCostSharingDetails:
          getCostSharingDetailsForPortCalls(timelineTds),
      };

      // Prepare other parameters

      const timelineEvents = timelineTds.map((td) => td.event);

      const context: LegOptimizationContext = {
        vessel,
        vesselEvents: timelineEvents,
        calculationConfig,
        getIncomeForLoadedAmount,
        tceOptions,
        orderById: selectedScenarioData.scenarioOrderById,
        scheduleDataContext: scheduleContext,
        calculateTimeDataPnl,
      };

      const inputs = {
        ...optimizationParams,
        scope: {
          portCallIds: params.portCallsToOptimize,
        },
      };

      // Optimize

      const optimizationResult = optimizeVoyageLegSpeeds({
        ...context,
        ...inputs,
      });

      if (optimizationResult.type === 'error') {
        throw optimizationResult.error;
      }

      // Apply changes to the scenario

      const optimizedTimeData = optimizationResult.timeData;

      const changeMaps = deriveEventModifications(
        optimizedTimeData.map((td) => td.event),
        timelineEvents,
        vessel,
        scheduleContext,
        calculationConfig,
        selectedScenarioData.scenarioPortCallByClientId,
      );

      createAndUpdateAssignments(changeMaps);

      // Notify

      const toastContent = (
        <SpeedOptimizationToast
          legChanges={optimizationResult.changeSummary.legDetails}
          tce={optimizationResult.changeSummary.tce}
        />
      );

      notify(toastContent, {
        type: 'success',
        toastId: 'leg-speed-optimization',
        autoClose: false,
      });
    },
  );

  const handleViewSingleEvent = useEvent(
    (singleEvent: SingleEvent, timeline: EventTimeline) => {
      if (singleEvent.vesselId == null) {
        throw new Error(`single entry without vesselId`);
      }

      openModal({
        type: 'EDIT_SINGLE_EVENT',
        props: {
          vesselId: singleEvent.vesselId,
          initialStartTime: undefined,
          singleEvent: singleEvent,
          handleUpdateEntry,
          timeline: timeline,
        },
      });
    },
  );

  const [_focusedOrders, setFocusedOrders] = useFocusedOrders();
  const handleZoomToVoyages = useEvent((orderIds: number[] = []) => {
    setFocusedOrders(orderIds);
  });

  const handleOrderShipmentView = useEvent((orderId: number): void => {
    setTimelineSelection((prevState) => ({
      ...prevState,
      selectedOrderIds: new Set([orderId]),
    }));
    handleZoomToVoyages([orderId]);
  });

  const handleOrderSelect = useEvent((orderIds: number[]): void => {
    const allOrderIds = getAllIncomingVoyageCharterOrders(orderIds);

    setTimelineSelection((prevState) => ({
      ...prevState,
      selectedOrderIds: new Set(allOrderIds),
    }));

    if (
      selectedStatusFilter !== StatusFilter.OPEN &&
      selectedStatusFilter !== StatusFilter.TIMELINE_SELECTION
    ) {
      const assignedOrderIds = orderIds.filter(isOrderAssigned);
      if (timelineRef.current && assignedOrderIds.length > 0) {
        setTimelineSelection((prevState) => ({
          ...prevState,
          selectedOrderIds: new Set(assignedOrderIds),
        }));
        handleZoomToVoyages(assignedOrderIds);
      }
    }
  });

  const [tableColumnProps, setTableColumnProps] =
    useState<TableColumnsSelectorProps<OrderTableRowData> | null>(null);

  const [showOrderTableTimes, saveShowOrderTableTimes] = useLocalSetting(
    'planningOrdersTableShowTimes',
    false,
  );

  const [groupCargoesByWeek, setGroupCargoesByWeek] = useLocalSetting(
    'planningOrdersTableGroupByWeek',
    true,
  );

  const handleVesselChange = useEventWithUndo(
    { title: `Change vessel` },
    (
      contract:
        | { type: 'spot'; shipownerOrgId: number | null }
        | { type: 'coa'; coaId: number },
      orderIds: number[],
      vesselId: number | null,
      startTime: number,
    ) => {
      try {
        // Implementation note: We assume that when using this method for changing vessel we're moving complete shipments from vessel to another, so we keep the shipment and voyage charter relations intact
        handleDeleteAssignment(orderIds, true);
      } catch (err) {
        toast.error(`Error changing vessel: ${err.message}`);
        return;
      }

      if (contract.type === 'spot') {
        handleCreateSpotAssignment(
          orderIds.map((orderId, i) => ({
            orderId,
            startDate: new Date(startTime + i),
            ownerOrganizationId: contract.shipownerOrgId,
            vesselId,
            shared: sharedOrderIds.has(orderId),
          })),
          {
            shipWithOrderId: null,
            deleteIfExisting: false,
            startIsOverride: false,
          },
        );
      } else if (contract.type === 'coa') {
        handleCreateCoaAssignment(
          orderIds.map((orderId, i) => ({
            orderId,
            startDate: new Date(startTime + i),
            coaId: contract.coaId,
            vesselId,
            shared: sharedOrderIds.has(orderId),
          })),
          {
            shipWithOrderId: null,
            startIsOverride: false,
          },
        );
      } else {
        exhaustiveCheck(contract);
      }

      handleSetVoyageCharter(orderIds);
    },
  );

  const handleSpotContractVesselChange = useEventWithUndo(
    { title: `Change spot vessel` },
    (
      voyageCharterId: number,
      orderIds: number[],
      shipownerOrgId: number,
      vesselId: number | null,
      startTime: number,
    ) => {
      orderIds.forEach((orderId) =>
        setOrderVoyageCharter(orderId, voyageCharterId),
      );

      handleVesselChange(
        { type: 'spot', shipownerOrgId },
        orderIds,
        vesselId,
        startTime,
      );
    },
  );

  const handleStatusFilterChange = useEvent((newStatus: StatusFilter) => {
    setSelectedStatusFilter(newStatus);
    setSelectedOrderIds(
      selectedOrderIds.filter((id) =>
        filteredOrderIdsByStatusFilter[newStatus].has(id),
      ),
    );
  });

  const isCurrentScenarioMaster = isMasterScenario(selectedScenario);

  const {
    open: getConfirmDeleteScenario,
    modalProps: deleteScenarioModalProps,
  } = useModalWithResult<boolean, { scenarioName: string }>();

  const backupScenarios = useMemo(
    () => scenarios.filter(isBackupScenario),
    [scenarios],
  );

  const onRestoreBackupScenario = useEvent((s: BackupSchedule) => {
    createScenario(s, { keepName: true, selectCreated: true });
  });

  const onRestoreBackendBackupScenario = useEvent(
    (commitLogScenarioClientId: string, newScenarioName: string) => {
      notify('Duplicating backup scenario', {
        type: 'info',
        toastId: 'backend-scenario-restore',
      });
      client
        .query<
          CommitLogScenarioByClientIdQueryResult['data'],
          CommitLogScenarioByClientIdQueryVariables
        >({
          query: CommitLogScenarioByClientIdDocument,
          variables: {
            commitLogScenarioClientId,
          },
          fetchPolicy: 'no-cache',
        })
        .then((data) => {
          const entry = data.data?.allCommitLogScenarios?.nodes[0];
          if (!entry) {
            throw new Error();
          }

          if (!entry.dataSchemaVersion || !entry.scenarioData) {
            throw new Error();
          }
          const parsed = parseScenario({
            __typename: 'Scenario',
            id: 0,
            clientId: generateClientId(),
            organizationId: currentUser?.organizationId || 0,
            name: newScenarioName,
            createdBy: currentUser?.id || 0,
            updatedBy: currentUser?.id || 0,
            dataSchemaVersion: entry.dataSchemaVersion,
            data: entry.scenarioData,
            shared: false,
          });
          if (!parsed) {
            throw new Error();
          }
          parsed.autoUpdateFromMaster = 'never';
          parsed.masterCommitAt = masterScenario?.masterCommitAt;
          createScenario(parsed, { keepName: true, selectCreated: true });
          notify('Backup scenario duplicated', {
            type: 'success',
            toastId: 'backend-scenario-restore',
          });
        })
        .catch(() => {
          notify('Failed to fetch backend scenario', {
            type: 'warning',
            toastId: 'backend-scenario-restore',
          });
        });
    },
  );

  const removeSelectedScenario = useEventWithUndo(
    { title: 'Remove scenario' },
    () => {
      clearTimelineSelection();
      deleteScenarioToTrash(selectedScenarioCId);
    },
  );

  const clearTimelineSelection = useEvent(() => {
    setTimelineSelection({
      selectedEventIds: new Set(),
      selectedOrderIds: new Set(),
      selectedSuggestionIds: new Set(),
    });
  });

  const removeSelectedScenarioWithConfirm = useEvent(async () => {
    if (
      selectedScenario.shared &&
      selectedScenario.createdBy === currentUser?.id
    ) {
      const confirmed = await getConfirmDeleteScenario({
        scenarioName: selectedScenario.name ?? '',
      });

      if (confirmed) {
        removeSelectedScenario();
      }
    } else {
      removeSelectedScenario();
    }
  });

  const syncWithCommitted = useEventWithUndo(
    { title: 'Sync with committed' },
    () => {
      if (masterScenario) {
        mergeScenario(masterScenario.clientId, selectedScenarioCId);
      }
    },
  );

  const toggleAutoSyncSelectedScenario = useEventWithUndo(
    { title: 'Toggle auto sync scenario' },
    () => {
      const newValue = isAutoSyncEnabledForScenario(
        configAutoSyncUserScenarios,
        selectedScenario,
      )
        ? 'never'
        : 'always';
      setAutoSync(newValue);
      if (newValue === 'always') {
        if (masterScenario) {
          mergeScenario(masterScenario.clientId, selectedScenarioCId);
        }
      }
    },
  );

  const duplicateSelectedScenario = useEventWithUndo(
    { title: 'Duplicate scenario' },
    () => {
      createScenario(selectedScenario);
    },
  );

  const applyRenameScenario = useEventWithUndo(
    { title: 'Rename scenario' },
    () => {
      if (editingScenarioName) {
        renameScenario(selectedScenarioCId, editingScenarioName);
      }
      setEditingScenarioName(null);
    },
  );

  const handleCreateScenario = useEventWithUndo(
    { title: 'New scenario' },
    () => {
      createScenario(undefined);
    },
  );

  const handleCreateEmptyScenario = useEventWithUndo(
    { title: 'New empty scenario' },
    () => {
      createEmptyScenario();
    },
  );

  const lcq = useLastCommitsQuery({
    variables: {
      first: useDisplayedNumberOfBackups(),
    },
  });
  const refetchLastCommits = useMemo(
    () => debounce(lcq.refetch, 10_000),
    [lcq.refetch],
  );
  useCommitLogScenarioUpdatedSubscription({
    onData: () => {
      void refetchLastCommits();
    },
    variables: {
      org: `${currentUserOrg}`,
    },
    skip: !currentUserOrg,
  });

  const lastCommits = useDefined(lcq.data?.lastCommits?.nodes);

  const scenarioMenuItems: ScenarioMenuItemType[] = useMemo(() => {
    const backendBackupScenarios = lastCommits
      .map(
        ({
          committedAt,
          masterClientId,
          userByCommittedBy,
          commitLogScenarioByMasterClientId,
        }) => {
          if (!masterClientId || !commitLogScenarioByMasterClientId?.clientId) {
            // There is no strict foreign key between those two tables
            // So it is possible (but unlikely) that commit_log entry points
            // into non-existent commit_log_scenarios
            // Here we check that it actually exists so restore action will not fail
            return null;
          }

          return {
            previousMasterDataClientId: masterClientId,
            userInfo: userByCommittedBy?.name ?? 'n/a',
            committedAt: committedAt,
          };
        },
      )
      .filter(isDefined);

    return [
      {
        label: 'Save',
        accessibilityLabel: 'Save scenario',
        hidden: isMasterScenario(selectedScenario),
        handler: () => saveScenario(selectedScenario.clientId, {}),
      },
      {
        label: 'Commit',
        accessibilityLabel: 'Commit scenario',
        hidden: isMasterScenario(selectedScenario),
        handler: () => openCommitModal(selectedScenario.clientId),
      },
      {
        label: 'Share',
        accessibilityLabel: 'Share scenario',
        disabled: selectedScenario.shared,
        hidden:
          isMasterScenario(selectedScenario) ||
          selectedScenario.createdBy !== currentUser?.id,
        handler: () => saveScenario(selectedScenario.clientId, { share: true }),
      },
      {
        label: 'Unshare',
        accessibilityLabel: 'Unshare scenario',
        disabled:
          !selectedScenario.shared || !!selectedScenario.isIntegrationMaster,
        hidden:
          isMasterScenario(selectedScenario) ||
          selectedScenario.createdBy !== currentUser?.id,
        handler: () =>
          saveScenario(selectedScenario.clientId, { share: false }),
      },
      {
        label: 'Auto-sync with committed',
        accessibilityLabel: 'Sync with committed scenario',
        checked: isAutoSyncEnabledForScenario(
          configAutoSyncUserScenarios,
          selectedScenario,
        ),
        hidden: isMasterScenario(selectedScenario),
        handler: toggleAutoSyncSelectedScenario,
      },
      {
        label: 'Sync with committed',
        accessibilityLabel: 'Sync with committed scenario',
        hidden:
          isMasterScenario(selectedScenario) ||
          isAutoSyncEnabledForScenario(
            configAutoSyncUserScenarios,
            selectedScenario,
          ),
        handler: syncWithCommitted,
      },
      {
        label: 'Duplicate',
        accessibilityLabel: 'duplicate scenario',
        handler: duplicateSelectedScenario,
      },
      {
        label: 'Compare',
        accessibilityLabel: 'Compare scenario',
        handler: () => setComparisonVisible(true),
      },
      {
        label: 'Rename',
        accessibilityLabel: 'Rename scenario',
        hidden: isMasterScenario(selectedScenario),
        handler: () => setEditingScenarioName(selectedScenario.name ?? ''),
      },
      {
        label: 'Remove',
        accessibilityLabel: 'Remove scenario',
        hidden: isMasterScenario(selectedScenario),
        handler: removeSelectedScenarioWithConfirm,
      },
    ]
      .concat(
        backendBackupScenarios && backendBackupScenarios.length > 0
          ? []
          : sortBy(backupScenarios, (s) => -s.backedUpAt).map((s) => ({
              label: `Show version from ${moment(s.backedUpAt).format(
                'yyyy-MM-DD HH:mm',
              )}`,
              accessibilityLabel: `Restore ${s.name}`,
              hidden: !isMasterScenario(selectedScenario),
              handler: () => onRestoreBackupScenario(s),
            })),
      )
      .concat(
        backendBackupScenarios?.map((lastCommit) => {
          const commitDateStr = moment(lastCommit.committedAt).format(
            'yyyy-MM-DD HH:mm',
          );

          return {
            label:
              `Show version from ${commitDateStr} ` +
              `(${lastCommit.userInfo})`,

            accessibilityLabel: `Restore ${lastCommit.committedAt}`,
            hidden: !isMasterScenario(selectedScenario),
            handler: () =>
              onRestoreBackendBackupScenario(
                lastCommit.previousMasterDataClientId,
                `COMMITTED ${commitDateStr}`,
              ),
          };
        }) || [],
      );
  }, [
    lastCommits,
    selectedScenario,
    currentUser?.id,
    configAutoSyncUserScenarios,
    syncWithCommitted,
    toggleAutoSyncSelectedScenario,
    duplicateSelectedScenario,
    removeSelectedScenarioWithConfirm,
    backupScenarios,
    saveScenario,
    openCommitModal,
    onRestoreBackupScenario,
    onRestoreBackendBackupScenario,
  ]);

  const ScenarioDropDownMenu = ({
    hideDropdown,
  }: {
    hideDropdown: () => void;
  }) => {
    return (
      <Menu hasShadow>
        {scenarioMenuItems
          .filter((x: ScenarioMenuItemType) => !x.hidden)
          .map((x: ScenarioMenuItemType) => (
            <MenuItem
              key={x.label}
              data-testid={x.accessibilityLabel}
              accessibilityLabel={x.accessibilityLabel}
              disabled={!!x.disabled}
              checked={x.checked}
              readOnly
              label={x.label}
              onPress={() => {
                if (x.checked === undefined) {
                  hideDropdown();
                }
                x.handler();
              }}
            />
          ))}
      </Menu>
    );
  };

  const {
    onContextMenu: onRowContextMenu,
    styles: itemMenuStyles,
    hideMenu: hideContextMenu,
    isVisible: isItemMenuVisible,
    setMenuRef: setItemMenuRef,
  } = useContextMenu();

  const onContextMenu = useAsRef(
    useCallback(
      (event: React.MouseEvent<HTMLDivElement, MouseEvent>) =>
        onRowContextMenu(event),
      [onRowContextMenu],
    ),
  );

  // auto save scenario per 5 mins
  const AUTO_SAVE_INTERVAL = 5 * 60 * 1000;

  useInterval(async () => {
    if (!currentUserOrganization?.configEnableScenarioAutoSave) return;

    const updatedScenarioIdxes = scenarios
      .filter(isUserScenario)
      .filter((x) => x.changedAt)
      .map((x) => scenarios.findIndex((i) => i.clientId === x.clientId));
    if (!updatedScenarioIdxes.length) return;

    const toastId = toast.info(
      `Auto saving ${updatedScenarioIdxes.length} scenarios...`,
    );

    await withSpinner(async () => {
      const updatedScenarios = [...scenarios];

      for (let i = 0; i < updatedScenarioIdxes.length; ++i) {
        const updatedScenarioIndex = updatedScenarioIdxes[i];
        const originalScenario = scenarios[updatedScenarioIdxes[i]];
        const s: InternalScenarioSchedule | undefined = await saveScenario(
          originalScenario.clientId,
          {},
        );
        if (s) updatedScenarios[updatedScenarioIndex] = s;
        toast.update(toastId, {
          render: `${i + 1} scenario${i + 1 > 1 ? 's' : ''} saved`,
        });
      }

      setScenarios(updatedScenarios);
    });
  }, AUTO_SAVE_INTERVAL);

  const scenarioTextStyle = (s: ScenarioSchedule) => {
    if (isMasterScenario(s) || isBackupScenario(s) || isInitialScenario(s)) {
      return { opacity: 0.7 };
    } else if (s.id === null) {
      return { color: 'rgba(255, 255, 255, 0.6)' };
    }
    return undefined;
  };

  const [currentTimelineMode, setCurrentTimelineMode] = useState<
    'plan' | 'commit'
  >('plan');
  const [currentCommitMode, setCurrentCommitMode] = useLocalSetting(
    'commitPolicy',
    'merge',
  );

  // scenario bar wrapping
  const scenariosRef = useRef<HTMLDivElement>(null);
  const [scenariosWrap, setScenariosWrap] = useState(false);

  const handleResize = useMemo(
    () =>
      debounce(() => {
        setScenariosWrap(false);
        // out of syntheticEvent with setTimeout
        setTimeout(() => {
          if (!scenariosRef?.current) return;
          const { height } = scenariosRef.current.getBoundingClientRect();

          if (height > 48 || isMobileView) setScenariosWrap(true);
        });
      }, 500),
    [isMobileView],
  );

  useLayoutEffect(() => {
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [handleResize]);

  const [planningLayout, setLayout] = usePlanningLayoutState();

  useEffect(() => {
    if (isMobileView) {
      setLayout('horizontal');
    }
  }, [isMobileView, setLayout]);

  const [filterBarRef, { width: filterBarWidth }] = useMeasure();
  const [verticalLayoutRef, { width: verticalLayoutWidth }] = useMeasure();

  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getFormattedTimeStamp = (
    date: number | undefined,
    timeZone = userTimeZone,
  ) => {
    if (!date) return '';
    return moment(date).tz(timeZone).format('M/D/YYYY, h:mm:ss A');
  };

  const { data: relatedOrgsData } = useRelatedOrgsQuery();

  const orderTableRef = useRef<OrderTableRef>(null);

  const selectAndScrollToOrderIds = useEvent(
    (orderIds: number[], scrollToSelected?: boolean) => {
      handleOrderSelect(orderIds);
      if (scrollToSelected) {
        orderTableRef.current?.scrollToOrderId(orderIds[0]);
      }
    },
  );

  const onSaveAndCloseOrder = useEvent(
    ({
      orderIds,
      isPreliminary = false,
      contractType = 'spot',
    }: {
      orderIds: number[];
      isPreliminary: boolean;
      contractType: ContractDetailsSchema['type'];
    }) => {
      if (orderIds.length > 0) {
        selectAndScrollToOrderIds(orderIds, true);
      }
      const statusFilter = getStatusFilterFromContractDetailsType(
        contractType,
        isPreliminary,
      );
      setSelectedStatusFilter(statusFilter);
    },
  );
  const vesselById = useMemo(() => mapKeyBy(vessels, (v) => v.id), [vessels]);

  /** Open the voyage suggestion report modal */
  const handleOpenVoyageSuggestionReport = useEvent(async () => {
    if (!suggestions?.logs) return;
    await showVoyageSuggestionReport({
      suggestionResult: {
        ...suggestions,
        logs: suggestions.logs,
      },
      orderById: scenarioOrderById,
      vesselById,
    });
  });

  const onBatchEditCargoes = useOpenBatchEditCargoesModal({
    selectedOrderIds,
    scenarioOrderById,
    openModal,
  });

  return (
    <BodyWrap>
      <CalculationContext.Provider value={{ calculationConfig }}>
        <PortCallUpdateHandlerProvider
          onApplyUpdate={handleApplyPortCallUpdate}
          onIgnoreUpdates={handleIgnorePortCallUpdates}
        >
          <ModalProvider
            {...{
              relatedOrgsData,
              tcTimeDataByVessel,
              onSaveAndCloseOrder,
              setSelectedOrderIds,
              activeModals,
              openModal,
              closeModal,
              closeAllModals,
              visibleModal,
              eventTimelines: timelines,
              timelineFocus: currenttimelineFocus ?? undefined,
              EDIT_OFFHIRE: {
                scheduleContext,
              },
              EDIT_OPEN_POSITION: {
                scheduleContext,
              },
              VESSEL_RESERVATION: {
                scheduleContext,
              },
              VOYAGE_ATTRIBUTE: {
                voyageAttributeById: calculationConfig.voyageAttributeById,
                patchShipment,
              },
              EDIT_SINGLE_EVENT: {
                scheduleContext,
              },
              VIEW_SHIPMENT: {
                handleUpdateEntry,
                handleModalSwap: handleSwap,
                onSwapPortCall: handlePortCallSwap,
                handleCreateEvent,
                handleDelete: handleDeleteTCEventByIndex,
                onOptimizeVoyageSpeed: handleOptimizeSpeed,
                onVesselChange: handleVesselChange,
                onSpotContractVesselChange: handleSpotContractVesselChange,
                scenario: selectedScenario,
                saveAllocations,
                scheduleContext,
                orders,
                createAndUpdateAssignments,
                pnlData,
              },
              VIEW_ORDER: {
                isOrderAssigned,
                isOrderPublished,
              },
              COMMIT_SCENARIO: {
                committedData,
                getScenarioData: props.scenarioTimings.getScenarioData,
                masterScenario,
                scenarioToCommit: isUserScenario(selectedScenario)
                  ? selectedScenario
                  : undefined,
              },
              EDIT_VOYAGE_CHARTER: {
                handleAddToAssignment,
                handleGetSuggestionsForOrders,
                reApplyCargoAllocations: allocationOptions.allocateCargo
                  ? reApplyCargoAllocations
                  : undefined,
              },
            }}
          >
            <FilterBar
              height="auto"
              bg="filterbar.background"
              borderBottomColor="neutral.11"
              borderBottomWidth={1}
            >
              <Box
                ref={scenariosRef}
                display="flex"
                flexDirection="row"
                flexWrap="wrap"
                alignItems="center"
                width="100%"
              >
                {!isMobileView && (
                  <Text
                    fontWeight="bold"
                    color="neutral.white_50"
                    isUppercase
                    mr={4}
                  >
                    Scenarios
                  </Text>
                )}
                {!scenariosWrap ? (
                  <>
                    {scenarios
                      .filter((s) => !isBackupScenario(s))
                      .filter((s) => !s.deletedAt)
                      .map((scenario, index) => (
                        <FilterBarTab
                          key={index}
                          isActive={scenario.clientId === selectedScenarioCId}
                          onPress={() => {
                            setCurrentTimelineMode('plan');
                            undo.withTransaction(
                              { title: 'Select scenario' },
                              () => selectScenario(scenario.clientId),
                            );
                          }}
                          mr={3}
                          data-testid={`planning-page-scenario-button-${scenario.name}`}
                        >
                          <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            onContextMenu={(e) => {
                              e.preventDefault();
                              if (scenario.clientId !== selectedScenarioCId) {
                                return;
                              }
                              onContextMenu.current(e);
                            }}
                          >
                            {scenario.shared && <ShareIcon />}
                            {scenario.isIntegrationMaster ? (
                              <Text
                                ml={2}
                                mr={3}
                                style={scenarioTextStyle(scenario)}
                              >
                                {`${scenario.name ?? String.fromCharCode(65 + index)} ${getFormattedTimeStamp(
                                  scenario.importedAt,
                                )}`}
                                {scenario.changedAt ? '*' : ''}
                              </Text>
                            ) : (
                              <Text
                                ml={2}
                                mr={3}
                                style={scenarioTextStyle(scenario)}
                              >
                                {scenario.name ??
                                  String.fromCharCode(65 + index)}
                                {scenario.changedAt ? '*' : ''}
                              </Text>
                            )}
                            {!isMasterScenario(scenario) &&
                              !isAutoSyncEnabledForScenario(
                                configAutoSyncUserScenarios,
                                scenario,
                              ) && (
                                <Box
                                  ml={-2}
                                  mr={3}
                                  fontSize="0.8em"
                                  title="Scenario does not automatically synchronize changes from committed version"
                                >
                                  📵
                                </Box>
                              )}
                            {scenario.clientId === selectedScenarioCId &&
                              !isMasterScenario(scenario) && (
                                <DeleteButton
                                  onPress={removeSelectedScenarioWithConfirm}
                                />
                              )}
                          </Box>
                        </FilterBarTab>
                      ))}
                  </>
                ) : (
                  <>
                    {selectedScenario && (
                      <FilterBarTab
                        isActive
                        onPress={() => selectScenario(selectedScenarioCId)}
                        mr={3}
                      >
                        <Box
                          display="flex"
                          flexDirection="row"
                          alignItems="center"
                          onContextMenu={(e) => {
                            e.preventDefault();
                            onContextMenu.current(e);
                          }}
                        >
                          {selectedScenario.shared && <ShareIcon />}
                          {selectedScenario.isIntegrationMaster ? (
                            <Text
                              ml={2}
                              mr={3}
                              style={scenarioTextStyle(selectedScenario)}
                            >
                              {`${
                                selectedScenario.name ?? 'SCENARIO'
                              } ${getFormattedTimeStamp(
                                selectedScenario.importedAt,
                              )}`}
                              {selectedScenario.changedAt ? '*' : ''}
                            </Text>
                          ) : (
                            <Text
                              ml={2}
                              mr={3}
                              style={scenarioTextStyle(selectedScenario)}
                            >
                              {selectedScenario.name ?? 'SCENARIO'}
                              {selectedScenario.changedAt ? '*' : ''}
                            </Text>
                          )}

                          {!isMasterScenario(selectedScenario) && (
                            <DeleteButton
                              onPress={removeSelectedScenarioWithConfirm}
                            />
                          )}
                        </Box>
                      </FilterBarTab>
                    )}
                    <DropdownButton variant="dark" label="All scenarios">
                      <Menu hasShadow>
                        {scenarios
                          .filter((s) => !isBackupScenario(s))
                          .filter((s) => !s.deletedAt)
                          .map((scenario) => (
                            <MenuItem
                              key={scenario.name}
                              label={scenario.name ?? ''}
                              data-testid={`planning-page-scenario-button-${scenario.name}`}
                              onPress={() => selectScenario(scenario.clientId)}
                            />
                          ))}
                      </Menu>
                    </DropdownButton>
                  </>
                )}
                <Button
                  aria-label="New scenario"
                  onPress={() => {
                    handleCreateScenario();
                    setCurrentTimelineMode('plan');
                  }}
                  variant="dark"
                  mx={3}
                >
                  + New scenario
                </Button>
                {window.location.hash.includes('empty') && (
                  <Button
                    aria-label="New empty scenario"
                    onPress={() => {
                      handleCreateEmptyScenario();
                      setCurrentTimelineMode('plan');
                    }}
                    variant="dark"
                    mx={3}
                  >
                    + New empty scenario
                  </Button>
                )}

                <View flexGrow={1} />
                {!isMobileView && (
                  <>
                    <Text
                      fontWeight="bold"
                      color="neutral.white_50"
                      isUppercase
                      mr={4}
                    >
                      Scenario
                    </Text>

                    <Q88Updates
                      restoreIntegrationScenario={restoreIntegrationScenario}
                    />
                    <VesonUpdates
                      restoreIntegrationScenario={restoreIntegrationScenario}
                    />
                    <ShipnetUpdates
                      restoreIntegrationScenario={restoreIntegrationScenario}
                    />
                    <PreemUpdates />
                    <Button
                      mr={3}
                      aria-label="Compare scenario"
                      onPress={() => setComparisonVisible(true)}
                      variant="dark"
                    >
                      Compare
                    </Button>
                    <Box display="flex" flexDirection="row" alignItems="center">
                      <FilterBarTab
                        isActive={currentTimelineMode === 'commit'}
                        onPress={() => {
                          setCurrentTimelineMode((prev) => {
                            return prev === 'commit' ? 'plan' : 'commit';
                          });
                          handleZoomToVoyages();
                        }}
                        aria-label={'Commit preview'}
                      >
                        <Text mr={3}>Commit Preview</Text>
                        <Badge
                          data-testid={'commit-preview-count'}
                          variant={undefined}
                          isRounded
                        >
                          {changedTimelines.length}
                        </Badge>
                      </FilterBarTab>
                    </Box>

                    <Button
                      my={3}
                      aria-label="Commit scenario"
                      onPress={() => openCommitModal(selectedScenarioCId)}
                      variant="dark"
                      disabled={!isUserScenario(selectedScenario)}
                    >
                      Commit
                    </Button>
                  </>
                )}
              </Box>
            </FilterBar>

            <Box display="flex" flexDirection="column" flex={1} minHeight="0">
              {(!isMobileView ||
                (isMobileView && currentView === 'cargoes')) && (
                <FilterBar
                  height="auto"
                  flexWrap="wrap"
                  position="sticky"
                  top={0}
                  left={0}
                  zIndex={1}
                  {...(isCurrentScenarioMaster && {
                    backgroundColor: COLOR_BG_MASTER_SCENARIO,
                  })}
                >
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    flexWrap="wrap"
                  >
                    {matches.large && (
                      <Text
                        fontWeight="bold"
                        color="neutral.white_50"
                        isUppercase
                        mr={4}
                      >
                        Orders
                      </Text>
                    )}

                    <OrderFilterTabList
                      currentValue={selectedStatusFilter}
                      setActive={handleStatusFilterChange}
                      orderCountByStatus={filteredOrderIdsByStatusFilter}
                    />
                  </Box>
                  {planningMode === 'order' && (
                    <Button
                      aria-label="new order"
                      onPress={() =>
                        openModal({
                          type: 'CREATE_ORDER',
                          props: {
                            currentUserOrg: assertedDefined(
                              currentUserOrg,
                              'currentUserOrg',
                            ),
                          },
                        })
                      }
                      ml={4}
                      variant="dark"
                    >
                      + New order
                    </Button>
                  )}{' '}
                  {planningMode === 'voyage_charter' && (
                    <Button
                      aria-label="new cargo"
                      onPress={() => openEditVoyageCharterModal()}
                      ml={4}
                      variant="dark"
                    >
                      + New cargo
                    </Button>
                  )}
                  <Box flexGrow={1} />
                  <Box
                    display="flex"
                    flexDirection="row"
                    alignItems="center"
                    marginLeft="auto"
                    height={48}
                  >
                    <ItemFilters
                      onChange={onOrderFiltersChange}
                      completions={orderFilterCompletions}
                      initialValue={orderSearch}
                      usesResetHotkey={!suggestions}
                      usesFindHotkey={true}
                      width="18em"
                      queryParamNames={['externalOrderId']}
                    />
                    {tableColumnProps && (
                      <>
                        <FilterBarSeparator />
                        <TableColumnsSelector
                          allColumns={tableColumnProps.allColumns}
                          getToggleHideAllColumnsProps={
                            tableColumnProps.getToggleHideAllColumnsProps
                          }
                        >
                          <MenuSeparator />
                          <Box
                            as="label"
                            display="flex"
                            alignItems="center"
                            mb={1}
                          >
                            <input
                              type="checkbox"
                              checked={showOrderTableTimes}
                              onChange={(e) =>
                                saveShowOrderTableTimes(e.target.checked)
                              }
                            />
                            Show times
                          </Box>
                          <MenuSeparator />
                          <Box
                            as="label"
                            display="flex"
                            alignItems="center"
                            mb={1}
                          >
                            <input
                              type="checkbox"
                              checked={groupCargoesByWeek}
                              onChange={() =>
                                setGroupCargoesByWeek(!groupCargoesByWeek)
                              }
                            />
                            Group cargoes by week
                          </Box>
                        </TableColumnsSelector>
                      </>
                    )}
                  </Box>
                </FilterBar>
              )}
              <PlanningLayout>
                {!(isMobileView && currentView === 'timeline') && (
                  <ResizableContainer
                    isMobileView={isMobileView}
                    currentView={currentView}
                  >
                    <Box
                      display="flex"
                      flexDirection="column"
                      minHeight="0"
                      height="100%"
                      borderRight="1px solid #fff6"
                    >
                      <Box overflowY="auto" height="100%">
                        {orders.length ? (
                          <OrderTable
                            ref={orderTableRef}
                            orders={orders}
                            completedOrderIds={completedOrderIds}
                            handleArchiveOrders={handleArchiveSelected}
                            filteredOrderIds={filteredOrderIds}
                            onUpdateOrderSharing={handlePublishOrder}
                            onMarkAsLost={handleMarkAsLostContract}
                            onViewOrder={handleOrderView}
                            onViewVoyageCharter={handleVoyageCharterView}
                            onDuplicateOrder={handleOrderDuplicate}
                            onDuplicateAndOffsetSelectedOrders={
                              onDuplicateAndOffsetOrders
                            }
                            onDuplicateVoyageCharter={
                              handleVoyageCharterDuplicate
                            }
                            isSuggestAvailable={isSuggestAvailable}
                            onSuggestVoyage={handleGetSuggestionsForOrders}
                            onSelectOrder={handleOrderSelect}
                            selectedOrders={selectedOrderIds}
                            scenario={selectedScenario}
                            masterScenario={masterScenario}
                            sharedOrderIds={sharedOrderIds}
                            onViewOrderShipment={handleOrderShipmentView}
                            onColumnsChange={setTableColumnProps}
                            showTimes={showOrderTableTimes}
                            selectedStatusFilter={selectedStatusFilter}
                            isSearching={orderSearch !== ''}
                            getOrderContractName={getOrderContractName}
                            pnlData={pnlData}
                            onBatchEditSelectedCargoes={onBatchEditCargoes}
                            groupCargoesByWeek={groupCargoesByWeek}
                            reApplyCargoAllocations={reApplyCargoAllocations}
                          />
                        ) : null}
                      </Box>
                    </Box>
                  </ResizableContainer>
                )}
                <Box
                  display="flex"
                  flexDirection="column"
                  flex="1"
                  bg="neutral.gray_150"
                  zIndex="1"
                  {...(isMobileView && currentView === 'cargoes'
                    ? {
                        position: 'fixed',
                        bottom: 0,
                        width: '100%',
                      }
                    : {})}
                >
                  <FilterBar
                    height="auto"
                    flexWrap="wrap"
                    justifyContent="space-between"
                    {...(isCurrentScenarioMaster && {
                      backgroundColor: COLOR_BG_MASTER_SCENARIO,
                    })}
                  >
                    <Box display="flex" flexDirection="row" alignItems="center">
                      {!isMobileView && planningLayout === 'horizontal' && (
                        <Text
                          fontWeight="bold"
                          color="neutral.white_50"
                          isUppercase
                          mr={3}
                        >
                          SCHEDULE
                        </Text>
                      )}
                      {!isMobileView && (
                        <CategoryFilterTabList
                          selectedFilterIds={selectedFilterIds}
                          selectedCategory={selectedCategory}
                          setSelectedCategory={setSelectedCategory}
                          updatedTimelines={updatedTimelines}
                          timelinesMatchingSearchFilter={
                            timelinesMatchingSearchFilter
                          }
                          timelinesByFilter={timelinesByFilter}
                          isShipOwner={isShipOwner}
                        />
                      )}
                    </Box>

                    {!isMobileView && <FilterBarSeparator />}

                    {currentTimelineMode === 'commit' && (
                      <DropdownButton
                        ml={3}
                        mr={3}
                        label={
                          currentCommitMode === 'merge'
                            ? 'Merge to committed'
                            : 'Replace committed'
                        }
                      >
                        <Menu hasShadow>
                          <MenuItem
                            label="Merge to committed"
                            onPress={() => setCurrentCommitMode('merge')}
                          />
                          <MenuItem
                            label="Replace committed"
                            onPress={() => setCurrentCommitMode('replace')}
                          />
                        </Menu>
                      </DropdownButton>
                    )}

                    <Box
                      display="flex"
                      flexDirection="row"
                      alignItems="center"
                      marginLeft="auto"
                      height={48}
                    >
                      <HorizontalLayoutSelectionControls
                        currentTimelineMode={currentTimelineMode}
                        selectedOrderIds={selectedOrderIds}
                        selectedOrdersTotalVolume={selectedOrdersTotalVolume}
                        selectedOrdersTotalMass={selectedOrdersTotalMass}
                        isAutoAcceptEnabled={isAutoAcceptEnabled}
                        isSuggestAvailable={isSuggestAvailable}
                        isArchiveSelectedAvailable={isArchiveSelectedAvailable}
                        handleSelectCompleted={handleSelectCompleted}
                        handleAssignToSpot={handleAssignToSpot}
                        handleUnassignSelectedOrders={
                          handleUnassignSelectedOrders
                        }
                        handleGetSuggestions={handleGetSuggestionsForOrders}
                        handleSuggestVoyageForVessels={
                          handleSuggestVoyageForVessels
                        }
                        handleSuggestScenarioImprovements={() =>
                          openImprovementSuggestions({ mode: 'improvements' })
                        }
                        handleArchiveSelected={handleArchiveSelected}
                        isSelectedScenarioReadOnly={selectedScenario.isReadOnly}
                        isCurrentScenarioMaster={isCurrentScenarioMaster}
                        showFleetCleanup={() => setIsFleetCleanupVisible(true)}
                        currentView={currentView}
                        setCurrentView={setCurrentView}
                        isMobileView={isMobileView}
                      />
                      <GetPortCallUpdates
                        isAutoAcceptEnabled={isAutoAcceptEnabled}
                        acceptAllOnlyConfirmed={acceptAllOnlyConfirmed}
                        timelines={timelines}
                        showAcceptAll={
                          currentUserOrganization?.configEnableAcceptAllUpdates ??
                          false
                        }
                        scenario={selectedScenario}
                        saveScenario={saveScenario}
                        selectedScenario={selectedScenario}
                        isCharterer={isCharterer}
                        isMobileView={isMobileView}
                      />
                      {!isMobileView && (
                        <>
                          <FilterBarSeparator />

                          <ItemFilters
                            completions={timelineFilterCompletions}
                            onChange={(newValue) => {
                              setTimelinesSearchFilter(
                                newValue.freeFilter ?? '',
                              );
                            }}
                            initialValue={timelinesSearchFilter}
                            usesResetHotkey={true}
                            usesFindHotkey={false}
                          />
                        </>
                      )}
                    </Box>
                  </FilterBar>
                  {currentTimelineMode === 'plan' &&
                    !(isMobileView && currentView === 'cargoes') && (
                      <ScenarioTimeline
                        ref={timelineRef}
                        vessels={vessels}
                        scheduleContext={scheduleContext}
                        organizations={organizationsById}
                        orders={orders}
                        scenario={selectedScenario}
                        integrationMasterScenario={integrationMasterScenario}
                        scenarioMenuItems={scenarioMenuItems}
                        timelineDiff={timelineDiff}
                        timelines={timelines}
                        completedData={completedData}
                        filterGroups={filterShownTimelines}
                        filterGroupsByRegionBalance={
                          filterShownTimelinesByRegionBalance
                        }
                        coas={coas}
                        onCreateAssignment={handleCreateAssignment}
                        onCreateCoaAssignment={handleCreateCoaAssignment}
                        onCreateSpotAssignment={handleCreateSpotAssignment}
                        onChangeOrdersVoyage={handleChangeOrdersVoyage}
                        onMoveSingleEvent={handleMoveSingleEvent}
                        onViewAssignment={handleViewAssignment}
                        onDeleteAssignment={handleDeleteAssignment}
                        onRearrangeShipmentEvents={handleRearrangeShipment}
                        onResetAssignmentsToMaster={
                          handleResetAssignmentsToMaster
                        }
                        onViewOffHire={handleViewOffHire}
                        onSaveOffHire={handleSaveOffHire}
                        onDeleteOffHire={handleDeleteOffHire}
                        onViewOpenPosition={handleViewOpenPosition}
                        onViewVesselReservation={handleViewVesselReservation}
                        onSetVoyageAttribute={handleSetVoyageAttribute}
                        onOptimizeVoyageSpeed={handleOptimizeSpeed}
                        onCreateSingleEvent={handleCreateEvent}
                        onViewSingleEvent={handleViewSingleEvent}
                        onDeleteSingleEvent={handleDeleteSingleEvent}
                        onAddOrderToAssignment={handleAddToAssignment}
                        onMergeToShipment={handleMergeToShipment}
                        onRemoveOrderFromShipment={
                          handleRemoveOrderFromShipment
                        }
                        onSwapScheduleEntry={handleSwap}
                        onSwapPortCall={
                          isCurrentScenarioMaster
                            ? undefined
                            : handlePortCallSwap
                        }
                        onPublishOrder={handlePublishOrder}
                        onOrderSelectionChange={onOrderSelectionChange}
                        onEventSelectionChange={onEventSelectionChange}
                        onSuggestionSelectionChange={
                          onSuggestionSelectionChange
                        }
                        onToggleGroupCollapse={setTimelineGroupCollapsed}
                        selectedOrderIds={timelineSelection.selectedOrderIds}
                        selectedEventIds={timelineSelection.selectedEventIds}
                        selectedSuggestionIds={
                          timelineSelection.selectedSuggestionIds
                        }
                        suggestions={suggestions?.suggestionsByVessel}
                        onSuggestionSelect={handleSuggestionSelect}
                        onDismissSuggestion={handleDismissSuggestion}
                        dismissedSuggestionIds={dismissedSuggestionIds}
                        onOpenVoyageSuggestionReport={
                          handleOpenVoyageSuggestionReport
                        }
                        onClearSuggestions={clearSuggestions}
                        isSuggestAvailable={isSuggestAvailable}
                        isCompressedView={isCompressedView}
                        onSuggestVoyage={handleSuggestVoyage}
                        sharedOrderIds={sharedOrderIds}
                        regionPolygons={regionPolygons}
                        regionsByLocation={regions}
                        regionBalanceFilter={regionBalanceFilter}
                        setRegionBalanceFilter={setRegionBalanceFilter}
                        charteringOpenPositions={charteringOpenPositions}
                        vesselReservations={vesselReservations}
                        pnlData={pnlData}
                        isMobileView={isMobileView}
                      />
                    )}
                  {currentTimelineMode === 'commit' && committedData && (
                    <ScenarioMergeTimeline
                      ref={timelineRef}
                      organizations={organizationsById}
                      scheduleContext={scheduleContext}
                      masterData={masterData}
                      selectedScenarioData={selectedScenarioData}
                      committedData={committedData}
                      filterTimelines={filterShownTimelines}
                      onResetAssignmentsToMaster={
                        handleResetAssignmentsToMaster
                      }
                      onConfirmEventOverwrite={handleConfirmEventOverwrite}
                      sharedOrderIds={sharedOrderIds}
                      getScenarioData={props.scenarioTimings.getScenarioData}
                      mergeDirectives={
                        mergeDirectivesByScenario.get(selectedScenarioCId) ??
                        null
                      }
                      moveShipmentAfterAnother={(
                        shipmentToMove,
                        targetShipment,
                      ) => {
                        setMergeDirectivesByScenario((prev) => {
                          const directives =
                            prev.get(selectedScenarioCId) ?? {};
                          const shipmentDirectives = new Map(
                            directives.placeShipmentAfter,
                          );
                          if (targetShipment === null) {
                            shipmentDirectives.delete(shipmentToMove);
                          } else {
                            shipmentDirectives.set(
                              shipmentToMove,
                              targetShipment,
                            );
                          }
                          return new Map(prev).set(selectedScenarioCId, {
                            ...directives,
                            placeShipmentAfter: shipmentDirectives,
                          });
                        });
                      }}
                      commitPolicy={
                        currentCommitMode === 'merge'
                          ? ScenarioCommitPolicy.MERGE_COMPATIBLE
                          : ScenarioCommitPolicy.OVERRIDE
                      }
                    />
                  )}
                </Box>
              </PlanningLayout>

              {(!isMobileView ||
                (isMobileView && currentView === 'timeline')) && (
                <div
                  ref={filterBarRef}
                  style={{
                    ...(isMobileView && currentView === 'timeline'
                      ? {
                          position: 'fixed',
                          left: 0,
                          bottom: 0,
                          width: '100%',
                          zIndex: 10,
                        }
                      : {}),
                  }}
                >
                  <FilterBar>
                    <VesselAttributeFilterBar
                      timelines={timelines}
                      selectedFilterIds={selectedFilterIds}
                      selectedCategory={selectedCategory}
                      setSelectedCategory={setSelectedCategory}
                      setSelectedFilterIds={setSelectedFilterIds}
                      parentWidth={filterBarWidth}
                      controlsWidth={verticalLayoutWidth}
                    />
                    <IntegrationRaptorDraftStatus />
                    <VerticalLayoutSelectionControls
                      currentTimelineMode={currentTimelineMode}
                      selectedOrderIds={selectedOrderIds}
                      selectedOrdersTotalVolume={selectedOrdersTotalVolume}
                      selectedOrdersTotalMass={selectedOrdersTotalMass}
                      isAutoAcceptEnabled={isAutoAcceptEnabled}
                      isSuggestAvailable={isSuggestAvailable}
                      isArchiveSelectedAvailable={isArchiveSelectedAvailable}
                      handleSelectCompleted={handleSelectCompleted}
                      handleGetSuggestions={handleGetSuggestionsForOrders}
                      handleSuggestVoyageForVessels={
                        handleSuggestVoyageForVessels
                      }
                      handleSuggestScenarioImprovements={() =>
                        openImprovementSuggestions({ mode: 'improvements' })
                      }
                      timelineSelection={timelineSelection.selectedOrderIds}
                      collapseAllTimelines={collapseAllTimelines}
                      handleZoomToVoyages={handleZoomToVoyages}
                      timelineRef={timelineRef}
                      isCompressedView={isCompressedView}
                      isEmptyTimelinesHidden={isEmptyTimelinesHidden}
                      isIncompatibleTimelinesHidden={
                        isIncompatibleTimelinesHidden
                      }
                      setIsIncompatibleTimelinesHidden={
                        setIsIncompatibleTimelinesHidden
                      }
                      setIsCompressedView={setIsCompressedView}
                      setIsEmptyTimelinesHidden={setIsEmptyTimelinesHidden}
                      handleAssignToSpot={handleAssignToSpot}
                      handleUnassignSelectedOrders={
                        handleUnassignSelectedOrders
                      }
                      handleArchiveSelected={handleArchiveSelected}
                      configUseCalculatedLimit={configUseCalculatedLimit}
                      setCompletedLimit={setCompletedLimit}
                      completedLimit={completedLimit}
                      undo={undo}
                      containerRef={verticalLayoutRef}
                      isSelectedScenarioReadOnly={selectedScenario.isReadOnly}
                      isCurrentScenarioMaster={isCurrentScenarioMaster}
                      showFleetCleanup={() => setIsFleetCleanupVisible(true)}
                      currentView={currentView}
                      setCurrentView={setCurrentView}
                      isMobileView={isMobileView}
                    />
                  </FilterBar>
                </div>
              )}
            </Box>
            {comparisonVisible && committedData && (
              <ScenarioComparison
                scheduleContext={scheduleContext}
                scenarios={scenarios}
                isOpen={true}
                onClose={() => setComparisonVisible(false)}
                orderById={committedOrderById}
                vessels={vessels}
                calculationConfig={calculationConfig}
                organizationsById={organizationsById}
                currentUserOrgId={currentUserOrganization?.id ?? -1}
                pnlData={pnlData}
                selectedScenarioId={selectedScenarioCId}
                masterData={masterData}
                selectedScenarioData={selectedScenarioData}
                committedData={committedData}
                onResetAssignmentsToMaster={handleResetAssignmentsToMaster}
                onConfirmEventOverwrite={handleConfirmEventOverwrite}
                sharedOrderIds={sharedOrderIds}
                getScenarioData={props.scenarioTimings.getScenarioData}
              />
            )}

            {improvementSuggestionsModalProps && committedData && (
              <ScenarioImprovements
                scheduleContext={scheduleContext}
                scenarios={scenarios}
                setImprovementScenarios={setImprovementScenarios}
                onAcceptImprovement={handleAcceptImprovementScenario}
                isOpen={true}
                onClose={() => improvementSuggestionsModalProps.onComplete()}
                orderById={committedOrderById}
                vessels={vessels}
                calculationConfig={calculationConfig}
                organizationsById={organizationsById}
                currentUserOrgId={currentUserOrganization?.id ?? -1}
                pnlData={pnlData}
                selectedScenarioId={selectedScenario.clientId}
                masterData={masterData}
                selectedScenarioData={selectedScenarioData}
                committedData={committedData}
                onResetAssignmentsToMaster={handleResetAssignmentsToMaster}
                onConfirmEventOverwrite={handleConfirmEventOverwrite}
                sharedOrderIds={sharedOrderIds}
                getScenarioData={props.scenarioTimings.getScenarioData}
                cargoAllocationOptions={allocationOptions}
              />
            )}

            {deleteScenarioModalProps && (
              <Modal
                isOpen={true}
                onClose={() => deleteScenarioModalProps.onCancel()}
                width="568px"
                height="260px"
              >
                <ModalBody>
                  <Text fontWeight="bold">
                    Are you sure you want to delete scenario{' '}
                    {deleteScenarioModalProps.scenarioName}?
                    <br />
                    This shared scenario will be removed for all users
                  </Text>
                </ModalBody>
                <ModalFooter>
                  <Button
                    variant="secondary"
                    mr="2"
                    onPress={() => deleteScenarioModalProps.onCancel()}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="negative"
                    onPress={() => deleteScenarioModalProps.onComplete(true)}
                  >
                    Delete
                  </Button>
                </ModalFooter>
              </Modal>
            )}

            {editingScenarioName !== null && (
              <Modal
                isOpen={true}
                onClose={() => setEditingScenarioName(null)}
                width="400px"
                height="160px"
              >
                <ModalBody>
                  <Text fontWeight="bold" mb={4}>
                    Scenario Rename
                  </Text>

                  <Input
                    name="scenarioName"
                    type="text"
                    value={editingScenarioName}
                    onChange={(e) => setEditingScenarioName(e.target.value)}
                    required
                  />
                </ModalBody>
                <ModalFooter>
                  <Button
                    variant="secondary"
                    mr="auto"
                    onPress={() => setEditingScenarioName(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="positive"
                    disabled={
                      !editingScenarioName ||
                      editingScenarioName === selectedScenario.name
                    }
                    onPress={applyRenameScenario}
                  >
                    Confirm
                  </Button>
                </ModalFooter>
              </Modal>
            )}

            {editLostContractModalProps && (
              <LostContractModal {...editLostContractModalProps} />
            )}

            {duplicateOrdersModalProps && (
              <DuplicateOrdersModal {...duplicateOrdersModalProps} />
            )}

            <div ref={setItemMenuRef} style={itemMenuStyles}>
              {isItemMenuVisible && (
                <ScenarioDropDownMenu hideDropdown={hideContextMenu} />
              )}
            </div>
            {isFleetCleanupVisible && (
              <FleetCleanupModal
                onClose={() => setIsFleetCleanupVisible(false)}
                timelines={timelines}
              />
            )}

            {integrationMasterScenario && masterScenario && (
              <IntegrationScenarioUpdateModal
                openCommitModal={openCommitModal}
                integrationScenario={integrationMasterScenario}
                selectScenario={selectScenario}
                changedTimelines={changedTimelines}
              />
            )}
          </ModalProvider>
        </PortCallUpdateHandlerProvider>
      </CalculationContext.Provider>
      <DragDropDescription />
      <PlanningHotkeysListener
        activeModals={activeModals}
        filteredOrderIds={filteredOrderIds}
        handleUnassignSelectedOrders={handleUnassignSelectedOrders}
        handleZoomToVoyages={handleZoomToVoyages}
        setCurrentTimelineMode={setCurrentTimelineMode}
        setRegionBalanceFilter={setRegionBalanceFilter}
        setSelectedOrderIds={setSelectedOrderIds}
        setSuggestions={setSuggestions}
        suggestions={suggestions}
        timelineSelection={timelineSelection}
        timelines={timelines}
        undo={undo}
      />

      <IntegrationRaptorDraft
        locationsById={scheduleContext.locations}
        timelines={timelines}
      />
    </BodyWrap>
  );
};

const MemoizedPlanning = React.memo(
  PlanningImpl,
  (_prevProps, nextProps) => nextProps.isCommitRunning,
);

function PlanningWithAppState({ scenarioTimings }: GlobalPageProps) {
  const [isCommitRunning, setIsCommitRunning] = useState<boolean>(false);
  return (
    <AppStateContext.Provider
      value={{
        isCommitRunning: isCommitRunning,
        setIsCommitRunning: (newVal: boolean) => {
          setIsCommitRunning(newVal);
        },
      }}
    >
      <PnlProvider scenarioTimings={scenarioTimings}>
        <TimelinePreferencesProvider>
          <MemoizedPlanning
            scenarioTimings={scenarioTimings}
            isCommitRunning={isCommitRunning}
          />
        </TimelinePreferencesProvider>
      </PnlProvider>
    </AppStateContext.Provider>
  );
}

export default function Planning(props: GlobalPageProps) {
  const router = useRouter();

  const currentUser = useCurrentUser();
  const orgId = currentUser?.organizationId ?? null;
  if (!orgId) {
    return <LoadingOverlay />;
  }

  const isPlanner = hasUserRight(orgId, 'createAndEditVoyages');
  if (!isPlanner) {
    void router.replace('/operations');
  }

  return (
    <PortCallUpdateProvider>
      <VoyageCharterUpdateProvider>
        <PlanningWithAppState scenarioTimings={props.scenarioTimings} />
      </VoyageCharterUpdateProvider>
    </PortCallUpdateProvider>
  );
}
